import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import isEqual from 'lodash/isEqual';
import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { LENS_LAYOUT, lensTokens } from '~/configs/unifiedAnalyticsTheme';

import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleSendOutlinedIcon from '@mui/icons-material/ScheduleSendOutlined';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { type Theme, alpha, useTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';

import { AskRichardFavicon, AskRichardWordmark, LensMark } from '~/components/brand';

import { useLensPinToggle } from '~/pages/AskRichardUnified/useLensPinToggle';

import {
  useGetReportingLeLens,
  useGetReportingLeLenses,
  useUpdateReportingLeLens,
} from '~/hooks/apis/reporting/useGetReportingLeLens';
import {
  useGetReportingLeLensData,
  useRefreshReportingLeLensCard,
} from '~/hooks/apis/reporting/useGetReportingLeLensData';
import { useDryRunReportingLeLensDataset } from '~/hooks/apis/reporting/useLeLensDatasetChecks';
import { lensChangeFailure } from '~/hooks/apis/reporting/useLensChange';
import { useLensLiveState } from '~/hooks/apis/reporting/useLensCollaboration';
import { useSetLensDatasetDeleted } from '~/hooks/apis/reporting/useLensDeletion';
import { useGetBrowsableLensSpaces } from '~/hooks/apis/reporting/useLensSpaces';
import { lensWriteFailureMessage } from '~/hooks/apis/reporting/useUpdateReportingLeLensErrors';
import useCurrentUser from '~/hooks/useCurrentUser';

import type { LeDataContextBlock } from '~/services/ReportingAiAskService';

import type { LeDataImageAttachment } from '~/types/leData';
import { LENS_PERIOD_FILTER_ID, LENS_PERIOD_KEY } from '~/types/leLens';
import type {
  Lens,
  LensCard,
  LensCardAlignment,
  LensCardConfig,
  LensCardData,
  LensCardLayout,
  LensCardSize,
  LensCardTitleSize,
  LensCardType,
  LensDataset,
  LensFilter,
  LensFilterValues,
  LensInsightScope,
  LensPeriod,
  LensReadingCache,
  LensSection,
  LensTextScale,
} from '~/types/leLens';

import { legacyLensUrl } from '../AskRichardUnified/legacyLensUrl';

import AddLensFilterDialog from './AddLensFilterDialog';
import CrossFiltersDialog from './CrossFiltersDialog';
import DatasetFilterBar from './DatasetFilterBar';
import DatasetsDialog from './DatasetsDialog';
import EditLensDetailsDialog from './EditLensDetailsDialog';
import FiltersDialog from './FiltersDialog';
import LayoutWorkspace from './LayoutWorkspace';
import { LensCalendarContext } from './LensCalendarContext';
import LensCertificationMenuItems, { lensCertificationActions } from './LensCertificationMenuItems';
import LensCertificationTick from './LensCertificationTick';
import LensCollaborationDialog from './LensCollaborationDialog';
import LensConflictReceipt, { type LensConflict } from './LensConflictReceipt';
import LensEditBar from './LensEditBar';
import { type LensFiltered, mergeChatLensFilters } from './LensFilterReceipt';
import LensLiveRegion from './LensLiveRegion';
import LensViewStrip from './LensViewStrip';
import NativeLensFilter, {
  clearDeclaredFilters,
  effectiveDeclaredFilters,
  isActiveDeclaredValue,
  reconcileDeclaredFilters,
} from './NativeLensFilter';
import RowsDrawer from './RowsDrawer';
import SaveLensAsDialog from './SaveLensAsDialog';
import ShareLensDialog from './ShareLensDialog';
import { type ActiveFilterChip, activeFilterChips } from './activeFilters';
import { makeUniqueId } from './buildPlan';
import { type BulkCardPresentation, applyBulkCardPresentation } from './bulkCardPresentation';
import {
  applyCanvasFrames,
  evenlyArrangeSelectedCards,
  frameForCard,
  framesPlacedByHand,
  resolveCanvasLayouts,
  smartArrangeCards,
} from './canvasLayout';
import { moveLensCardToSection, placementOntoSavedCards } from './cardArrangement';
import { cardComparison } from './cardComparison';
import { duplicateCardTitles } from './cardDuplicates';
import { cardFilterReach } from './cardFilterReach';
import {
  type LensDateRange,
  type LensRange,
  type LensSelection,
  applyView,
  hasSelection,
  toggleMarkSelection,
} from './cardView';
import LensCanvas from './cards/LensCanvas';
import LensCardView from './cards/LensCardView';
import { useLensWandAsk } from './cards/LensWandAsk';
import { preserveChartWidthModes } from './cards/cardReadingLayout';
import { LENS_HERO_FONT_FAMILY, cardDataForBoardFailure, formatCardFreshness } from './cards/cardState';
import { compactLensBoardEvidence } from './cards/insightBoardEvidence';
import { measureInsightHeights } from './cards/insightSizing';
import { TABLE_COLLAPSED_ROW_SPAN, preserveTableHeightModes, tableUsesAutoHeight } from './cards/tableSizing';
import { cardsShapedLikeTheirRows, loadedRowsServe, rowsDefinitionKey } from './cardsForRows';
import { combineQueries } from './combineQueries';
import { LENS_CROSS_FILTER_PREFIX, withoutLensCrossFilters } from './crossFilterQuery';
import CustomLensView, { type CustomLensViewHandle } from './customView/CustomLensView';
import CustomLensViewDialog from './customView/CustomLensViewDialog';
import LensDisplaySettings from './LensDisplaySettings';
import { lensDisplay } from './lensDisplay';
import { applyRowFields, prepareFields } from './datasetFields';
import { bindFilterToCards, unbindFilterFromCards } from './filterBindings';
import { declaredDatasetLabel, humaniseFilterLabel } from './filterLabels';
import { GovernedDisplayMetadataProvider, governedDisplayDataset } from './governedDisplayMetadata';
import { GovernedMeasureMetadataProvider, effectiveGovernedMeasureColumns } from './governedMeasureMetadata';
import { governedDatasets, lensAnchor } from './lensAnchor';
import {
  announceLensFilter,
  describeCrossFilterAnnouncement,
  describeCrossFilterClearedAnnouncement,
} from './lensAnnouncer';
import { createLensAskPool } from './lensAskSession';
import { plottedValueExtent } from './lensAxisBounds';
import { LENS_DEFAULT_CALENDAR } from './lensCalendar';
import { lensCardDomId } from './lensCardDom';
import { buildChartModel, cardChartConfig } from './lensChart';
import { grainOptions } from './lensGrain';
import { describeLensLoadError } from './lensLoadError';
import { LensPdfExportError, exportLensBoardPdf } from './lensPdfExport';
import { boardPeriodOf, mergePeriods } from './lensPeriodPresets';
import { lensReaderFilterSummary } from './lensReaderContext';
import { type LensReaderScope, buildLensReaderScope } from './lensReaderScope';
import { type RowsDrawerChoices, readRowsDrawerChoices, writeRowsDrawerChoices } from './lensRowsDrawerStorage';
import { savedInsightScopeChanged } from './lensSavedInsightScope';
import { applyLensView, readLensView } from './lensUrlState';
import {
  type LensVisualIssue,
  auditLensCardElements,
  auditLensCardResults,
  retryableVisualIssueCardIds,
  sameVisualIssues,
  uniqueVisualIssues,
} from './lensVisualAudit';
import { useDeclareNow } from './panel/DeclareNow';
import LensEditPanel from './panel/LensEditPanel';
import LensSidebar from './panel/LensSidebar';
import UpgradeBanner from './panel/UpgradeBanner';
import DatasetActions from './panel/actions/DatasetActions';
import MergeBanner from './panel/actions/MergeBanner';
import NewDataset from './panel/actions/NewDataset';
import { overrideColumnKind, relabelDatasetColumn, removeCalculatedField } from './panel/actions/datasetColumnEdits';
import { findMergeCandidateFor } from './panel/actions/mergeCandidates';
import { configuredTablePresentationChanged, editedTablePresentation } from './panel/configuredTableEditing';
import { titleAfterMeasureChange } from './panel/measureTitle';
import {
  LENS_PANEL_WIDTH,
  type LensEditorPanelState,
  type LensPanelAskRenderArgs,
  type LensPanelMode,
  type LensPanelSection,
  PANEL_SECTION_FOR_TAB,
  PANEL_TAB_FOR_LEGACY_TAB,
  type PanelFieldHover,
} from './panel/panelTypes';
import ChartTab from './panel/tabs/ChartTab';
import DetailsTab, { type DetailsTabDraft } from './panel/tabs/DetailsTab';
import StyleTab from './panel/tabs/StyleTab';
import VisualBasics from './panel/tabs/VisualBasics';
import { useStyleTabState } from './panel/tabs/useStyleTabState';
import { useCardAutoSave } from './panel/useCardAutoSave';
import { type CardEditorDraft, draftSectionsDifferingFrom, useCardEditorDrafts } from './panel/useCardEditorDrafts';
import { useLensSidebarOpen } from './panel/useLensSidebarOpen';
import type { LensPrintTarget } from './print/lensPrintTypes';
import SectionReading from './readings/SectionReading';
import { sectionCards, viewSignature } from './readings/viewSignature';
import { formatResolvedPeriod, resolvedPeriodConvention } from './resolvedPeriod';
import { expandedRowsForResults } from './rowsDrawerDefaults';
import LensSchedulesDialog from './schedules/LensSchedulesDialog';
import { formatScopeLine } from './scopeLine';
import { useDebouncedValue } from './useDebouncedValue';
import { useLensUndoShortcut } from './useLensUndoShortcut';

/** Compact height for the on-board draft composer. */

function LensSettingsItem({
  description,
  disabled = false,
  icon,
  label,
  onClick,
}: {
  description: string;
  disabled?: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
}) {
  return (
    <ListItemButton disabled={disabled} onClick={onClick} sx={{ borderRadius: 1.5, px: 1, py: 1.25 }}>
      <ListItemIcon sx={{ color: 'primary.main', minWidth: 38 }}>{icon}</ListItemIcon>
      <ListItemText
        primary={label}
        secondary={description}
        primaryTypographyProps={{ fontSize: 15, fontWeight: 600 }}
        secondaryTypographyProps={{ fontSize: 13, lineHeight: 1.45 }}
      />
    </ListItemButton>
  );
}

/** Desktop authors can make room for field names without covering the board. */
export const LENS_EDITOR_PANEL_MIN_WIDTH = 360;
export const LENS_EDITOR_PANEL_MAX_WIDTH = 960;
/** Keep enough horizontal canvas for a readable card while the pane grows. */
export const LENS_EDITOR_PANEL_MIN_CANVAS_WIDTH = 480;

/**
 * The one source of truth for the panel width. A sheet covers the narrow
 * workspace; desktop gets a real lane beside the board.
 */
export function lensEditorPanelWidth(panelIsSheet: boolean, panelIsXl: boolean) {
  return panelIsSheet ? 0 : panelIsXl ? LENS_PANEL_WIDTH.xl : LENS_PANEL_WIDTH.lg;
}

/** Share of the window the pane takes by default, before the user drags it. */
export const LENS_EDITOR_PANEL_DEFAULT_SHARE = 0.33;

/**
 * The pane's starting width follows the window instead of a breakpoint: a third
 * of it, never under the readable minimum, never over the xl lane. It depends on
 * the window alone, not on the rail, so the rail's own collapse rule stays stable.
 */
export function smartLensEditorPanelWidth(viewportWidth: number): number {
  if (!(viewportWidth > 0)) {
    return LENS_PANEL_WIDTH.lg;
  }
  const share = Math.round(viewportWidth * LENS_EDITOR_PANEL_DEFAULT_SHARE);
  return Math.min(LENS_PANEL_WIDTH.xl, Math.max(LENS_EDITOR_PANEL_MIN_WIDTH, share));
}

/**
 * The panel never grows into the last readable canvas lane. Below `lg` it is
 * a sheet, but retaining a defensive viewport cap here also makes a resize
 * that coincides with a browser resize settle safely rather than clipping the
 * board behind the permanent Drawer.
 */
export function lensEditorPanelWidthBounds(viewportWidth: number, reservedWidth = 0): { min: number; max: number } {
  const safeViewport = viewportWidth > 0 ? viewportWidth : LENS_EDITOR_PANEL_MIN_CANVAS_WIDTH + LENS_PANEL_WIDTH.lg;
  // The host shell's navigation rail sits beside the board, so the board floor is measured after it.
  const canvasViewport = Math.max(0, safeViewport - Math.max(0, reservedWidth));
  const max = Math.max(
    LENS_EDITOR_PANEL_MIN_WIDTH,
    Math.min(LENS_EDITOR_PANEL_MAX_WIDTH, canvasViewport - LENS_EDITOR_PANEL_MIN_CANVAS_WIDTH),
  );
  return { min: LENS_EDITOR_PANEL_MIN_WIDTH, max };
}

export function clampLensEditorPanelWidth(width: number, viewportWidth: number, reservedWidth = 0): number {
  const { min, max } = lensEditorPanelWidthBounds(viewportWidth, reservedWidth);
  return Math.min(max, Math.max(min, width));
}

/**
 * A desktop editor needs a real lane beside the canvas. Keeping this decision
 * in one small helper prevents a later Drawer-only change from covering the
 * board again.
 */
export function lensWorkspaceWithEditor(editorOpen: boolean, panelWidth: number) {
  const reservesEditorLane = editorOpen && panelWidth > 0;
  return {
    marginLeft: reservesEditorLane ? 0 : 'auto',
    marginRight: reservesEditorLane ? `${panelWidth}px` : 'auto',
    maxWidth: (reservesEditorLane ? false : 'xl') as false | 'xl',
    width: reservesEditorLane ? `calc(100% - ${panelWidth}px)` : '100%',
  };
}

/**
 * A Details-panel size or height is an explicit request to resize the canvas
 * frame. Keep the untouched axis and the card's position exactly as they were:
 * selecting Tall must not unexpectedly narrow a manually sized card, and
 * selecting Small must not move it down the board.
 */
export function applyPanelPresentationFrame(
  card: LensCard,
  presentation: Pick<DetailsTabDraft, 'size' | 'height' | 'resizeWidth' | 'resizeHeight'>,
  data?: LensCardData,
): LensCard {
  const sizeChanged = presentation.resizeWidth || presentation.size !== (card.size ?? 'large');
  const currentHeight = tableUsesAutoHeight(card) ? '' : (card.height ?? (card.type === 'table' ? 'tall' : ''));
  const heightChanged = presentation.resizeHeight || presentation.height !== currentHeight;
  const nextCard: LensCard = {
    ...card,
    ...(sizeChanged ? { size: presentation.size } : {}),
    ...(heightChanged ? { height: presentation.height || undefined } : {}),
  };

  if (!sizeChanged && !heightChanged) {
    return nextCard;
  }

  const currentFrame = card.layout ?? frameForCard(card, data);
  const requestedFrame = frameForCard(nextCard, data);
  return {
    ...nextCard,
    layout: {
      ...currentFrame,
      ...(sizeChanged ? { w: requestedFrame.w } : {}),
      ...(heightChanged ? { h: requestedFrame.h } : {}),
      ...(heightChanged && card.type === 'table' ? { by: presentation.height ? 'user' : 'auto' } : {}),
    },
  };
}

/**
 * Height of a table card's collapsed "Rows" drawer strip. Independent of the
 * card's own saved height: only the frame the canvas shows while collapsed,
 * never persisted, so expanding it restores exactly the frame the card had.
 */
export const ROWS_DRAWER_ROW_SPAN = TABLE_COLLAPSED_ROW_SPAN;

/**
 * A collapsed table card's display frame: the same origin and width as its
 * saved layout, shrunk to the drawer strip's own height.
 */
export function rowsDrawerCollapsedFrame(layout: LensCardLayout): LensCardLayout {
  return { ...layout, h: ROWS_DRAWER_ROW_SPAN };
}

/**
 * Cards as the canvas should lay out: a collapsed table card's frame shrunk
 * to its drawer strip, every other card exactly as saved. Only ever used for
 * what the canvas renders; the real saved frame lives untouched on `cards`.
 */
export function withRowsDrawerFrames(
  cards: ReadonlyArray<LensCard>,
  expandedTableCardIds: ReadonlySet<string>,
): Array<LensCard> {
  return cards.map((card) =>
    card.type === 'table' && card.layout && !expandedTableCardIds.has(card.id)
      ? { ...card, layout: rowsDrawerCollapsedFrame(card.layout) }
      : card,
  );
}

/**
 * Frames the canvas reports back, with a collapsed table card's temporary
 * drawer-strip height removed.
 *
 * Dragging or resizing any other card on the board reports every card's
 * current frame, collapsed table cards included. Without stripping those
 * entries here, the next unrelated drag would save the drawer strip's height
 * as that table's real, persisted frame.
 */
export function withoutCollapsedRowsDrawerFrames(
  frames: Record<string, LensCardLayout>,
  collapsedTableCardIds: ReadonlySet<string>,
): Record<string, LensCardLayout> {
  if (collapsedTableCardIds.size === 0) {
    return frames;
  }
  return Object.fromEntries(Object.entries(frames).filter(([id]) => !collapsedTableCardIds.has(id)));
}

export interface LeLensPageProps {
  openId?: string;
  /** Set only when embedding this page outside its own route, e.g. the unified shell's main
   *  pane. Falls back to the `:lensId` route param for the standalone `/le-lens/:lensId` route. */
  lensId?: string;
  /** Set only when embedding this page outside its own route. When supplied, the "All
   *  dashboards" control calls back into the shell's own navigation instead of routing to the
   *  standalone `/le-lens` list, so the embed never leaves the shell's rail behind. Absent means
   *  the standalone route behaves exactly as it does today. */
  onNavigateToList?: () => void;
  /** Opens a newly-created copy while preserving the embedding shell when supplied. */
  onOpenLens?: (lensId: string) => void;
  /** Embedded-only: opens a fresh, editable askRichard draft with the selected card's context. */
  onAskRichard?: (
    question: string,
    includeLensContext?: boolean,
    autoSend?: boolean,
    images?: Array<LeDataImageAttachment>,
    context?: ReadonlyArray<LeDataContextBlock>,
  ) => void;
  /** Embedded-only: opens the Lens-scoped askRichard panel from the board header. */
  onOpenAskRichardPanel?: () => void;
  askRichardRequest?: number;
  /** Embedded-only: makes a canvas card the subject of the docked askRichard composer. */
  onSelectCard?: (card: Pick<LensCard, 'id' | 'title'>) => void;
  selectedCardId?: string | null;
  /** Embedded-only: a lightweight way to restore the reader's last locally saved filters. */
  restoreFiltersAction?: React.ReactNode;
  /** Embedded-only: renders Ask mode inside the right pane (the askRichard thread scoped to the card). */
  renderAskPane?: (args: LensPanelAskRenderArgs) => React.ReactNode;
  /** Embedded-only: opens the Data catalogue's detail screen for a governed dataset. Threaded to
   *  the board header's own anchor chip; absent leaves that chip as plain, non-interactive text
   *  (the standalone route has no catalogue view to open). */
  onOpenCatalogueDataset?: (catalogueId: string) => void;
  /** Embedded-only: "Browse the full catalogue" from the New dataset picker, leaving it for the
   *  Data catalogue's own list screen. Absent hides that action, the same as every other
   *  optional action in the picker. */
  onBrowseCatalogue?: () => void;
  /** Embedded-only: reports the right pane's open state and width so the shell keeps the board composer clear of it. */
  onEditorPanelChange?: (state: LensEditorPanelState) => void;
  /** Width the host shell reserves beside the board (its navigation rail). Default 0. */
  reservedCanvasWidth?: number;
  /**
   * Embedded-only: reports the period and filters the reader has in view, so a
   * question asked about this Lens is answered against them rather than against
   * a window the model has to guess at.
   */
  onLensContextChange?: (context: LensReaderScope) => void;
  /**
   * The Lens print page only (scheduled PDF emails, see ./print). Once the board has loaded, the
   * page makes the PDF itself with the same code as "Export as PDF" (or, for a custom view, the
   * same copy "Print / save as PDF" prints) and hands it to this target instead of saving it.
   */
  printTarget?: LensPrintTarget;
}

const CARD_EDIT_COMMIT_DELAY_MS = 800;

/** The dashboard owns destructive confirmation so the reader never has to rely on a browser prompt. */
type PendingConfirmation = {
  confirmLabel: string;
  description: string;
  title: string;
  /** Most confirmations here remove persisted data. Discarding an unsaved
   * card draft is recoverable, so it must not borrow destructive red chrome. */
  destructive?: boolean;
  onConfirm: () => Promise<void>;
};

/**
 * The always-reachable way back to collapsed, for an expanded table card.
 *
 * `LensCardView` is the real card, unmodified: this only lays a small
 * floating control over the top of it, so a reader who opened the rows can
 * always send them back behind the drawer strip without the card itself
 * needing to know a drawer exists.
 */

export default function LeLensPage({
  openId: openIdProp,
  lensId: lensIdProp,
  onNavigateToList,
  onOpenLens,
  onAskRichard,
  onOpenAskRichardPanel,
  askRichardRequest,
  onSelectCard: reportCardSelection,
  selectedCardId,
  restoreFiltersAction,
  onLensContextChange,
  renderAskPane,
  onOpenCatalogueDataset,
  onBrowseCatalogue,
  onEditorPanelChange,
  printTarget,
  reservedCanvasWidth = 0,
}: LeLensPageProps = {}) {
  const { lensId: routeLensId } = useParams<{ lensId: string }>();
  const navigate = useNavigate();
  const lensId = lensIdProp ?? routeLensId;
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const panelIsSheet = useMediaQuery(theme.breakpoints.down('lg'));
  // The pane starts at a share of the window and follows window resizes until
  // the user drags or keys it; from then on the chosen width is kept for this
  // visit. A narrow viewport turns the pane into a sheet, then returns to the
  // desktop width when there is a canvas lane again.
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window === 'undefined' ? 0 : window.innerWidth));
  const [requestedEditorPanelWidth, setRequestedEditorPanelWidth] = useState(() =>
    smartLensEditorPanelWidth(viewportWidth),
  );
  const userSizedPanelRef = useRef(false);
  const panelResizeRef = useRef<{ pointerId: number; startX: number; startWidth: number } | null>(null);
  const [isResizingEditorPanel, setIsResizingEditorPanel] = useState(false);
  const editorPanelWidth = panelIsSheet
    ? 0
    : clampLensEditorPanelWidth(requestedEditorPanelWidth, viewportWidth, reservedCanvasWidth);

  useEffect(() => {
    const updateViewportWidth = () => setViewportWidth(window.innerWidth);
    updateViewportWidth();
    window.addEventListener('resize', updateViewportWidth);
    return () => window.removeEventListener('resize', updateViewportWidth);
  }, []);

  // A window can shrink independently of the pointer gesture. Preserve the
  // editor's chosen width where possible, but keep the state itself valid so
  // returning to a larger screen does not briefly reserve a stale, oversized
  // lane.
  useEffect(() => {
    if (panelIsSheet) {
      return;
    }
    setRequestedEditorPanelWidth((current) =>
      clampLensEditorPanelWidth(
        userSizedPanelRef.current ? current : smartLensEditorPanelWidth(viewportWidth),
        viewportWidth,
        reservedCanvasWidth,
      ),
    );
  }, [panelIsSheet, reservedCanvasWidth, viewportWidth]);

  useEffect(() => {
    if (!isResizingEditorPanel) {
      return undefined;
    }
    const previousUserSelect = document.body.style.userSelect;
    const previousCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
    return () => {
      document.body.style.userSelect = previousUserSelect;
      document.body.style.cursor = previousCursor;
    };
  }, [isResizingEditorPanel]);

  const finishEditorPanelResize = useCallback((pointerId?: number) => {
    if (pointerId !== undefined && panelResizeRef.current?.pointerId !== pointerId) {
      return;
    }
    panelResizeRef.current = null;
    setIsResizingEditorPanel(false);
  }, []);

  const startEditorPanelResize = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (event.button !== 0) {
        return;
      }
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      panelResizeRef.current = { pointerId: event.pointerId, startX: event.clientX, startWidth: editorPanelWidth };
      userSizedPanelRef.current = true;
      setIsResizingEditorPanel(true);
    },
    [editorPanelWidth],
  );

  const resizeEditorPanel = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const resize = panelResizeRef.current;
      if (!resize || resize.pointerId !== event.pointerId) {
        return;
      }
      // The handle owns the pane's left edge: moving left widens it.
      setRequestedEditorPanelWidth(
        clampLensEditorPanelWidth(
          resize.startWidth + resize.startX - event.clientX,
          viewportWidth,
          reservedCanvasWidth,
        ),
      );
    },
    [reservedCanvasWidth, viewportWidth],
  );

  const resizeEditorPanelWithKey = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      const bounds = lensEditorPanelWidthBounds(viewportWidth, reservedCanvasWidth);
      const change =
        event.key === 'ArrowLeft'
          ? 24
          : event.key === 'ArrowRight'
            ? -24
            : event.key === 'Home'
              ? bounds.min - editorPanelWidth
              : event.key === 'End'
                ? bounds.max - editorPanelWidth
                : 0;
      if (!change) {
        return;
      }
      event.preventDefault();
      userSizedPanelRef.current = true;
      setRequestedEditorPanelWidth((current) =>
        clampLensEditorPanelWidth(current + change, viewportWidth, reservedCanvasWidth),
      );
    },
    [editorPanelWidth, reservedCanvasWidth, viewportWidth],
  );
  // The app chrome can be either one or two rows depending on the active
  // navigation and viewport. A fixed drawer must begin below its actual
  // rendered edge, not a guessed toolbar height, otherwise it overlaps the
  // black navigation whenever those links wrap.
  const [appChromeBottom, setAppChromeBottom] = useState(0);
  useEffect(() => {
    const appBar = document.querySelector<HTMLElement>('header.MuiAppBar-root');
    if (!appBar) {
      return undefined;
    }
    const updateOffset = () => setAppChromeBottom(Math.ceil(appBar.getBoundingClientRect().bottom));
    updateOffset();
    const observer = new ResizeObserver(updateOffset);
    observer.observe(appBar);
    window.addEventListener('resize', updateOffset);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateOffset);
    };
  }, []);
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const previewGroupedToolbar = searchParams.get('toolbarLayout') !== 'legacy';
  const previewFiltersBelow = previewGroupedToolbar || searchParams.get('filterPlacement') === 'below';
  const openId = useMemo(() => openIdProp ?? (lensId ? crypto.randomUUID() : undefined), [lensId, openIdProp]);
  const { data, isLoading, error, refetch, isFetching } = useGetReportingLeLens(lensId, openId);
  const [storedFilterValues, setFilterValues] = useState<LensFilterValues>(
    () => readLensView(window.location.search).filters,
  );
  const filterValues = useMemo(
    () => reconcileDeclaredFilters(data?.lens?.spec.filters ?? [], storedFilterValues),
    [data?.lens?.spec.filters, storedFilterValues],
  );
  useEffect(() => {
    if (data?.lens && !isEqual(storedFilterValues, filterValues)) setFilterValues(filterValues);
  }, [data?.lens, storedFilterValues, filterValues]);
  const updateLens = useUpdateReportingLeLens();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isFinishingEditing, setIsFinishingEditing] = useState(false);
  const canAuthor = Boolean(data?.access.canEdit && isEditMode);
  const [sharingOpen, setSharingOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [schedulesOpen, setSchedulesOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const { user } = useCurrentUser();
  const lensList = useGetReportingLeLenses();
  const pins = useLensPinToggle(lensList.isSuccess ? lensList.data.map((item) => item.id) : undefined);
  const spacesQuery = useGetBrowsableLensSpaces(sharingOpen);
  const refreshCard = useRefreshReportingLeLensCard();
  const dryRunDataset = useDryRunReportingLeLensDataset();
  const datasetDeletion = useSetLensDatasetDeleted();
  // A direct card starts life only in the editor. This avoids saving a blank
  // object just because somebody pressed Add card, while still letting the
  // right-hand Data pane be the first step rather than an AI request.
  const [newCardDraft, setNewCardDraft] = useState<LensCard | null>(null);
  const [editorConflicts, setEditorConflicts] = useState<
    Record<string, LensConflict & { mine: CardEditorDraft; base: LensCard }>
  >({});
  // The persistent right panel (02_RIGHT_PANEL_SPEC.md). panelDraft is the
  // live-preview hand-off: the shelf/dataset state the panel has not saved
  // yet, applied to the canvas's copy of the card so F1's "no warehouse call,
  // redraws from rows already loaded" holds without a second save.
  const [panelCardId, setPanelCardId] = useState<string | null>(null);
  // Which job the pane is doing, and which section opens when it lands in
  // Edit for a card. Mode is a property of the pane, not the card: it does
  // not reset when the reader moves from one card to another.
  const [panelMode, setPanelMode] = useState<LensPanelMode>(renderAskPane ? 'ask' : 'edit');
  useEffect(() => {
    setIsEditMode(false);
    setIsFinishingEditing(false);
    setPanelMode('ask');
  }, [lensId, data?.access.canEdit]);
  const [sidebarOpen, setSidebarOpen] = useLensSidebarOpen(panelIsSheet);
  const onSelectCard = (card: Pick<LensCard, 'id' | 'title'>) => {
    if (renderAskPane) {
      setSidebarOpen(true);
    }
    reportCardSelection?.(card);
  };
  const [panelSection, setPanelSection] = useState<LensPanelSection | undefined>(undefined);
  const [panelFormatFocus, setPanelFormatFocus] = useState<'commentary' | 'sort' | undefined>(undefined);
  // Sections a just-applied Ask proposal changed, forced open and ringed by
  // the pane; cleared here a few seconds after Apply.
  const [ringedSections, setRingedSections] = useState<Array<LensPanelSection>>([]);
  const savedPanelCard = data?.lens.spec.cards.find((card) => card.id === panelCardId) ?? newCardDraft;
  const cardEditorDrafts = useCardEditorDrafts(lensId, panelCardId, savedPanelCard);
  const latestDraftsByLens = useRef<Record<string, Record<string, CardEditorDraft>>>({});
  latestDraftsByLens.current[lensId ?? ''] = cardEditorDrafts.drafts;
  const latestCardDrafts = useRef(cardEditorDrafts.drafts);
  latestCardDrafts.current = cardEditorDrafts.drafts;
  const panelDraft = cardEditorDrafts.draft?.data ?? null;
  const panelType = cardEditorDrafts.draft?.type ?? null;
  const panelDetailsDraft = cardEditorDrafts.draft?.details ?? null;
  const panelStyleConfig = cardEditorDrafts.draft?.config ?? null;
  const panelSort = cardEditorDrafts.draft ? cardEditorDrafts.draft.sort : savedPanelCard?.config?.sort;
  const setPanelDraft = (value: React.SetStateAction<typeof panelDraft>) => cardEditorDrafts.setField('data', value);
  const setPanelType = (value: React.SetStateAction<typeof panelType>) => cardEditorDrafts.setField('type', value);
  const setPanelDetailsDraft = (value: React.SetStateAction<typeof panelDetailsDraft>) =>
    cardEditorDrafts.setField('details', value);
  const setPanelStyleConfig = (value: React.SetStateAction<typeof panelStyleConfig>) =>
    cardEditorDrafts.setField('config', value);
  const setPanelSort = (value: React.SetStateAction<typeof panelSort>) => cardEditorDrafts.setField('sort', value);
  // Which field is hovered in the panel's field list, and which cards on the
  // canvas use it (FieldList.tsx already computes that list). Read per card
  // when rendering LensCardView below, so only the cards that use the field
  // show its outline and "Uses <field>" caption.
  const [hoveredField, setHoveredField] = useState<PanelFieldHover>(null);
  // Region 5 (dataset actions) and region 2's "+ New"/merge banner: C2's brief authorises this
  // wiring once C1's mount lands and this file is clean in git status (03_LANE_BRIEFS.md).
  const [newDatasetRequest, setNewDatasetRequest] = useState<{ prefillSql?: string; bindCardId?: string } | null>(null);
  // NewDataset persists immediately, whereas the card remains a local panel
  // draft until the author saves it. Bumping this key deliberately re-seeds
  // the panel's independently-owned draft after that hand-off.
  const [datasetBindingEpoch, setDatasetBindingEpoch] = useState(0);
  const [dismissedMergePairs, setDismissedMergePairs] = useState<ReadonlySet<string>>(new Set());
  // The five lower-rank action bar controls (Layout, Datasets, Filters, Add filter,
  // Cross-filters) collapse into this one overflow menu. Editing remains a secondary
  // toolbar action; completing the editing session owns the primary right-hand position.
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState<HTMLElement | null>(null);
  const lensActionsButtonRef = useRef<HTMLButtonElement | null>(null);
  const [bulkMoveAnchor, setBulkMoveAnchor] = useState<HTMLElement | null>(null);
  // Anchored to the Configure button the picker was opened from, so it appears
  // where the reader clicked rather than beside the filters.
  const [filterColumnsAnchor, setFilterColumnsAnchor] = useState<HTMLElement | null>(null);
  const [filtersAnchor, setFiltersAnchor] = useState<HTMLElement | null>(null);
  const filtersAskRequested = useRef(false);
  const askPaneContainerRef = useRef<HTMLDivElement | null>(null);
  const [focusAskComposer, setFocusAskComposer] = useState(false);
  const [isLayoutWorkspaceOpen, setIsLayoutWorkspaceOpen] = useState(false);
  const [isDatasetsOpen, setIsDatasetsOpen] = useState(false);
  // One selection reaches compatible governed queries before aggregation; custom
  // query cards can still narrow fields they already return in the browser.
  const [selections, setSelections] = useState<LensSelection>(() => readLensView(window.location.search).selections);
  // The chart that raised the latest mark selection keeps its unfiltered
  // comparison context and highlights the mark. Every other compatible card
  // receives the ordinary narrowed result.
  const [crossFilterSourceCardId, setCrossFilterSourceCardId] = useState<string | null>(null);
  const [ranges, setRanges] = useState<Record<string, LensRange>>(() => readLensView(window.location.search).ranges);
  const [isAddFilterOpen, setIsAddFilterOpen] = useState(false);
  const [editingLensDetails, setEditingLensDetails] = useState<Lens | null>(null);
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [pdfProgress, setPdfProgress] = useState<{ done: number; total: number } | null>(null);
  const [isCustomViewOpen, setIsCustomViewOpen] = useState(false);
  const [customViewDataEditingLensId, setCustomViewDataEditingLensId] = useState<string | null>(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [isCrossFiltersOpen, setIsCrossFiltersOpen] = useState(false);
  const [editingFilter, setEditingFilter] = useState<LensFilter | null>(null);
  const [insightRefreshes, setInsightRefreshes] = useState<Record<string, number>>({});
  // View controls should not claim canvas space until the reader asks to change them.
  // Collapse keeps their in-progress input mounted, so closing the shelf never discards a draft.
  // Figma-style guides describe the object currently being manipulated, not
  // every possible row in the dashboard. That keeps the canvas legible.
  const [isCanvasSelectionMode, setIsCanvasSelectionMode] = useState(false);
  const [selectedCanvasCardIds, setSelectedCanvasCardIds] = useState<Array<string>>([]);
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingConfirmation | null>(null);
  const [confirmationError, setConfirmationError] = useState<string | null>(null);

  const requestConfirmation = useCallback((confirmation: PendingConfirmation) => {
    setConfirmationError(null);
    setPendingConfirmation(confirmation);
  }, []);

  const dismissConfirmation = useCallback(() => {
    if (!updateLens.isPending) {
      setConfirmationError(null);
      setPendingConfirmation(null);
    }
  }, [updateLens.isPending]);

  const confirmPendingAction = useCallback(async () => {
    if (!pendingConfirmation) {
      return;
    }
    setConfirmationError(null);
    try {
      await pendingConfirmation.onConfirm();
      setPendingConfirmation(null);
    } catch {
      // Mutation feedback still carries the technical detail. This keeps the decision in place
      // so the reader can retry rather than having to retrace their steps through the canvas.
      setConfirmationError('Could not make this change. Nothing has been removed — try again.');
    }
  }, [pendingConfirmation]);

  const lens = data?.lens;
  const visibleCustomView = lens?.spec.customView;
  const lensAnchorValue = useMemo(() => (lens ? lensAnchor(lens) : undefined), [lens]);
  const lensGovernedDatasets = useMemo(() => (lens ? governedDatasets(lens) : []), [lens]);
  const lensAnchorName =
    lensGovernedDatasets.find((dataset) => dataset.id === lensAnchorValue?.datasetId)?.name ?? 'Governed dataset';
  // F10's per-dataset fallback (DatasetFacts.tsx's "Declare now"): one dry run on the dataset's
  // own SQL, then one lens save. `lens` may still be undefined on first render; DeclareNow.tsx
  // guards `onDeclareNow` against that itself.
  const declareNow = useDeclareNow(lens);
  // Dragging changes the visible canvas immediately. The server response replaces this
  // draft on success; a failed save restores the last confirmed layout.
  const [cardLayoutDraft, setCardLayoutDraft] = useState<Array<LensCard> | null>(null);
  const layoutCards = cardLayoutDraft ?? lens?.spec.cards ?? [];
  useEffect(() => setCardLayoutDraft(null), [lens?.id, lens?.version]);
  useEffect(() => {
    setIsCanvasSelectionMode(false);
    setSelectedCanvasCardIds([]);
  }, [lens?.id]);
  // Narrowing belongs to the dashboard it was set on. A vendor filter on one Lens is not a
  // vendor filter on another, so moving between them has to drop it: the unified shell swaps
  // `lensId` on one mounted page, and without this the new Lens silently opens narrowed by the
  // previous one's filters and cross-filter selections with nothing on screen saying so.
  //
  // Guarded on a real previous id rather than reset on mount, because the URL's `?view=` seeds
  // the first Lens this page shows and resetting would discard a shared link's own filters.
  const shownLensIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const previous = shownLensIdRef.current;
    shownLensIdRef.current = lens?.id;
    if (!previous || previous === lens?.id) {
      return;
    }
    setFilterValues({});
    setSelections({});
    setCrossFilterSourceCardId(null);
    setRanges({});
  }, [lens?.id]);
  // The address follows what the viewer has chosen, so a link is a link to what
  // they are actually looking at. Replace rather than push: a filter change is
  // not a navigation, and pushing would fill the back button with them.
  useEffect(() => {
    // Read from the live address rather than from the router's copy of it. The unified shell
    // writes its own parameters with history.pushState, which the router does not observe, so
    // building this update from `searchParams` dropped the shell's lens and thread ids.
    const live = new URLSearchParams(window.location.search);
    const next = applyLensView(live, { filters: filterValues, selections, ranges });
    if (next.toString() === live.toString()) {
      return;
    }
    setSearchParams(next, { replace: true });
  }, [filterValues, selections, ranges, searchParams, setSearchParams]);

  useEffect(() => {
    const apply = (event: Event) => {
      const receipt = (event as CustomEvent<LensFiltered>).detail;
      if (!receipt || receipt.lensId !== lens?.id) return;
      if (receipt.clear) {
        setFilterValues(clearDeclaredFilters({}, lens.spec.filters ?? []));
        setSelections({});
        setRanges({});
      } else {
        setFilterValues((current) => mergeChatLensFilters(current, receipt.filters, boardPeriodOf(lens.spec)));
        setSelections((current) => ({ ...current, ...receipt.selections }));
      }
      setCrossFilterSourceCardId(null);
    };
    window.addEventListener('lens-view-filter', apply);
    return () => window.removeEventListener('lens-view-filter', apply);
  }, [lens?.id, lens?.spec.filters, lens?.spec.period, lens?.spec.periodDefault]);

  const calendar = lens?.spec.calendar ?? LENS_DEFAULT_CALENDAR;
  const baseFilterValues = useMemo(
    () => effectiveDeclaredFilters(lens?.spec.filters ?? [], filterValues),
    [lens?.spec.filters, filterValues],
  );
  // The scope line's own geography segment reads differently once the viewer has actually chosen
  // a value in the filter the create-journey gate minted for it (scopeGeographyFilter): "choose
  // yours in the filter" while nothing is selected, the chosen value once one is. Found by
  // column, not by a fixed id, since the filter's id is a slug of its label and not otherwise
  // knowable here.
  const scopeGeographyFilterValue = useMemo(() => {
    const field = lens?.spec.scope?.geography?.field;
    if (!field) {
      return undefined;
    }
    const filterId = lens?.spec.filters?.find((filter) => filter.column === field)?.id;
    const raw = filterId ? baseFilterValues[filterId] : undefined;
    return typeof raw === 'string' || Array.isArray(raw) ? raw : undefined;
  }, [baseFilterValues, lens?.spec.filters, lens?.spec.scope]);
  // Same reasoning as the create-journey gate's own datasetNameFallback: `scope.dataset` can be
  // null on a saved Lens that nonetheless reads from exactly one dataset (a routed plan's
  // detector never named one, but every card still landed on the same answer). Named only when
  // there is exactly one, so a Lens genuinely spanning several keeps reading "Not set".
  const scopeDatasetNameFallback = lens?.spec.datasets?.length === 1 ? lens.spec.datasets[0].name : undefined;
  const viewerPeriod = useMemo(
    () =>
      mergePeriods(
        lens ? boardPeriodOf(lens.spec) : undefined,
        filterValues[LENS_PERIOD_KEY] as Partial<LensPeriod> | undefined,
      ),
    [lens, filterValues],
  );
  const hasChangedSavedInsightScope = useMemo(
    () =>
      Boolean(
        lens &&
        savedInsightScopeChanged({
          filters: lens.spec.filters ?? [],
          values: baseFilterValues,
          boardPeriod: boardPeriodOf(lens.spec),
          viewerPeriod,
          selections,
          ranges,
        }),
      ),
    [lens, baseFilterValues, viewerPeriod, selections, ranges],
  );
  const settledSelections = useDebouncedValue(selections, 350);
  const selectionPending = JSON.stringify(selections) !== JSON.stringify(settledSelections);
  const effectiveFilterValues = useMemo(
    () => ({
      ...baseFilterValues,
      ...Object.fromEntries(
        Object.entries(settledSelections)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([column, values]) => [`${LENS_CROSS_FILTER_PREFIX}${column}`, [...values].sort()]),
      ),
    }),
    [baseFilterValues, settledSelections],
  );
  // The period is part of what is asked of BigQuery, so it travels with the
  // filter values and a change to it refetches rather than reshaping rows.

  // What is narrowing the board, so the strip can say it and offer to undo it.
  const filterChips = useMemo(
    () =>
      activeFilterChips(lens?.spec.filters ?? [], baseFilterValues, selections, ranges, (column) =>
        humaniseFilterLabel(column, declaredDatasetLabel(column, lens?.spec.datasets ?? [])),
      ),
    [lens?.spec.filters, lens?.spec.datasets, baseFilterValues, selections, ranges],
  );

  const clearFilterChip = useCallback((chip: ActiveFilterChip) => {
    if (chip.kind === 'filter') {
      setFilterValues((current) => ({ ...current, [chip.id]: '' }));
      return;
    }
    if (chip.kind === 'selection') {
      setSelections((current) => {
        const next = { ...current };
        delete next[chip.id];
        if (!hasSelection(next)) {
          setCrossFilterSourceCardId(null);
        }
        return next;
      });
      return;
    }
    setRanges((current) => {
      const next = { ...current };
      delete next[chip.id];
      return next;
    });
  }, []);
  const boardFilters = useMemo(
    () =>
      filterChips
        .filter((chip) => chip.kind !== 'range')
        .map((chip) => ({
          column:
            chip.kind === 'selection' ? chip.id : lens?.spec.filters?.find((filter) => filter.id === chip.id)?.column,
          filterId: chip.kind === 'filter' ? chip.id : undefined,
          label: `${chip.label}: ${chip.value}`,
          onRemove: () => clearFilterChip(chip),
        })),
    [filterChips, lens?.spec.filters, clearFilterChip],
  );
  // Only a query that binds the range answers a period change.  Naming them
  // means the control can say what it will re-run instead of implying it
  // reaches everything on the page.
  // What the reader has in view, told to the shell so a question asked about
  // this Lens is answered against it. The period is reported with its resolved
  // summary as well as its bounds, since an unbounded Lens has no bounds to
  // send and "every available date" is itself the answer.
  const activeFilterSummary = useMemo(() => lensReaderFilterSummary(filterChips), [filterChips]);

  const datedDatasets = useMemo(
    () => (lens?.spec.datasets ?? []).filter((dataset) => dataset.dateFilter).map((dataset) => dataset.name),
    [lens?.spec.datasets],
  );
  const {
    data: lensData,
    isFetching: isLoadingData,
    error: dataError,
    isPlaceholderData: hasPreviousData,
    isShowingPreviousData: isShowingPreviousKeyData,
    refetch: refetchData,
    refreshData,
  } = useGetReportingLeLensData(
    lens?.id === lensId ? lensId : undefined,
    effectiveFilterValues,
    true,
    lens?.version,
    data?.view?.revision,
    openId,
  );
  const drafting = Boolean(data?.drafting);
  const responsesByDefinition = useRef(new Map<string, NonNullable<typeof data>>());
  const shownDefinition = data ? rowsDefinitionKey(data.lens.id, data.lens.version, data.view?.revision) : undefined;
  if (data && shownDefinition) {
    responsesByDefinition.current.set(shownDefinition, data);
  }
  const rowsVersion = lensData?.lensVersion;
  const rowsDefinition =
    rowsVersion === undefined || !lensData?.lensId
      ? undefined
      : rowsDefinitionKey(lensData.lensId, rowsVersion, lensData.viewRevision);
  const rowsResponse = useMemo(
    () =>
      data && rowsDefinition !== undefined && rowsDefinition !== shownDefinition
        ? ((lensData?.lensId === data.lens.id ? responsesByDefinition.current.get(rowsDefinition) : undefined) ?? {
            ...data,
            lens: { ...data.lens, spec: { ...data.lens.spec, cards: [], customView: undefined } },
          })
        : undefined,
    [data, lensData?.lensId, rowsDefinition, shownDefinition],
  );
  const rowsLens = rowsResponse?.lens;
  const rowsLensCards = rowsLens?.spec.cards;
  const isShowingPreviousData = isShowingPreviousKeyData || rowsLensCards !== undefined;
  const rowsAheadOfLens = Boolean(lens && rowsVersion !== undefined && rowsVersion > lens.version);
  useEffect(() => {
    if (rowsAheadOfLens) {
      void refetch();
    }
  }, [rowsAheadOfLens, refetch]);
  const hasCrossFilterSelection = hasSelection(settledSelections);
  const sourceContextFilterValues = useMemo(
    () => withoutLensCrossFilters(effectiveFilterValues),
    [effectiveFilterValues],
  );
  // Usually a cache hit for the result that was on screen immediately before
  // the click. It is enabled only while a source chart needs that context, so
  // an idle dashboard never pays for a duplicate board request.
  const { data: sourceContextLensData, isFetching: isFetchingSourceContext } = useGetReportingLeLensData(
    crossFilterSourceCardId && hasCrossFilterSelection && lens?.id === lensId ? lensId : undefined,
    sourceContextFilterValues,
    true,
    lens?.version,
    data?.view?.revision,
    openId,
  );
  const sourceContextCardDataById = useMemo(
    () => new Map((sourceContextLensData?.cards ?? []).map((result) => [result.cardId, result])),
    [sourceContextLensData?.cards],
  );
  const boardRange = useMemo<LensDateRange>(
    () => (lensData?.period ? { from: lensData.period.current.from, to: lensData.period.current.to } : {}),
    [lensData?.period],
  );
  const insightScope = useMemo<LensInsightScope>(
    () => ({
      effectiveFilters: {
        ...effectiveFilterValues,
        [LENS_PERIOD_KEY]: lensData?.period
          ? {
              window: { from: lensData.period.current.from, to: lensData.period.current.to },
              comparison: lensData.period.comparison
                ? { from: lensData.period.comparison.from, to: lensData.period.comparison.to }
                : null,
            }
          : viewerPeriod,
      },
      ranges,
    }),
    [effectiveFilterValues, lensData?.period, viewerPeriod, ranges],
  );
  const boardResolved = Boolean(lensData);
  const readerScope = useMemo(
    () =>
      buildLensReaderScope({
        lensId,
        loadedLensId: lens?.id,
        range: boardRange,
        filters: activeFilterSummary,
        hasPreviousData,
        selectionPending,
        boardResolved,
        cards: lensData?.cards,
      }),
    [
      lensId,
      lens?.id,
      boardRange,
      activeFilterSummary,
      hasPreviousData,
      selectionPending,
      boardResolved,
      lensData?.cards,
    ],
  );
  useEffect(() => {
    onLensContextChange?.(readerScope);
  }, [onLensContextChange, readerScope]);
  const cardData = lensData?.cards ?? [];
  const cardDataById = useMemo(
    () =>
      new Map(
        cardData
          .filter((result) => !rowsLensCards || rowsLensCards.some((card) => card.id === result.cardId))
          .map((result) => [result.cardId, result]),
      ),
    [cardData, rowsLensCards],
  );
  const boardEvidence = useMemo(
    () =>
      compactLensBoardEvidence(
        cardsShapedLikeTheirRows(lens?.spec.cards ?? [], rowsLensCards, cardDataById, cardDataById),
        cardDataById,
      ),
    [lens?.spec.cards, rowsLensCards, cardDataById],
  );
  const editingCard = lens?.spec.cards.find((item) => item.id === panelCardId) ?? newCardDraft;
  const queryDraft = editingCard
    ? editedTablePresentation(editingCard, {
        ...editingCard,
        ...(panelType ? { type: panelType } : {}),
        ...(panelDraft ? { datasetId: panelDraft.datasetId, view: panelDraft.view, period: panelDraft.period } : {}),
      })
    : null;
  /**
   * A card's result, or a stated failure when the board's fetch died and this
   * card never got one. Without the second half a total outage handed every
   * card `undefined`, which the card reads as "ran and found nothing" and
   * prints as "No rows for this period": a zero a reader would have acted on.
   */
  const cardDataFor = useCallback(
    (cardId: string): LensCardData | undefined =>
      cardDataById.get(cardId) ?? (dataError && !isLoadingData ? cardDataForBoardFailure(cardId) : undefined),
    [cardDataById, dataError, isLoadingData],
  );
  const shownCardDataFor = useCallback(
    (cardId: string): LensCardData | undefined =>
      cardId === crossFilterSourceCardId
        ? (sourceContextCardDataById.get(cardId) ?? cardDataFor(cardId))
        : cardDataFor(cardId),
    [cardDataFor, crossFilterSourceCardId, sourceContextCardDataById],
  );
  const duplicateOfByCardId = useMemo(
    () => duplicateCardTitles(lens?.spec.cards ?? [], cardDataById),
    [cardDataById, lens?.spec.cards],
  );
  const [freshnessNow, setFreshnessNow] = useState(() => new Date());
  useEffect(() => {
    const interval = window.setInterval(() => setFreshnessNow(new Date()), 30_000);
    return () => window.clearInterval(interval);
  }, []);
  const canvasFreshness = useMemo(() => {
    const labels = [
      ...new Set(cardData.map((result) => formatCardFreshness(result.refreshedAt, freshnessNow)).filter(Boolean)),
    ];
    if (labels.length === 1) {
      return labels[0];
    }
    if (labels.length > 1) {
      return 'Updated at different times';
    }
    return isLoadingData ? 'Updating…' : undefined;
  }, [cardData, isLoadingData, freshnessNow]);
  const panelCard =
    newCardDraft?.id === panelCardId
      ? newCardDraft
      : (lens?.spec.cards.find((card) => card.id === panelCardId) ?? null);
  const sidebarVisible = Boolean(panelCard) || (Boolean(renderAskPane) && sidebarOpen);

  const tablePresentationChanged = Boolean(
    panelCard && queryDraft && configuredTablePresentationChanged(panelCard, queryDraft),
  );

  const panelHasUnsavedChanges = useMemo(
    () => draftSectionsDifferingFrom(cardEditorDrafts.draft, panelCard).length > 0,
    [cardEditorDrafts.draft, panelCard],
  );
  const pendingLensCardIds = Object.entries(cardEditorDrafts.drafts)
    .filter(
      ([id, draft]) =>
        draftSectionsDifferingFrom(
          draft,
          lens?.spec.cards.find((card) => card.id === id),
        ).length > 0,
    )
    .map(([id]) => id);
  const conflictingLensCardIds = lens
    ? Object.keys(editorConflicts)
        .filter((key) => key.startsWith(`${lens.id}:`))
        .map((key) => key.slice(lens.id.length + 1))
    : [];
  useLensUndoShortcut(lens?.id, canAuthor);
  const { hasRemoteChanges, accessError } = useLensLiveState(
    lens,
    isEditMode || panelHasUnsavedChanges,
    Boolean(data?.access.canEdit),
  );

  // Reports the pane's open state and width to the shell, which collapses the
  // rail when the board gets tight. Covers the Ask pane and the card editor alike.
  // A sheet is a full-width modal, so it carries no width to reserve.
  useEffect(() => {
    const width = sidebarVisible ? editorPanelWidth : 0;
    onEditorPanelChange?.({ open: sidebarVisible, width });
  }, [editorPanelWidth, onEditorPanelChange, sidebarVisible]);

  /**
   * The card as Ask mode's proposals see it: the saved or draft card with
   * every pending Edit-section change (type, dataset/view, details, style)
   * already folded in, the same fields the Save bar's own onSave folds in at
   * persist time. Kept apart from that path since a proposal can change any
   * of them without the reader ever having touched a shelf.
   */
  const currentPanelCard = useCallback((): LensCard | null => {
    if (!panelCard) {
      return null;
    }
    const nextCard: LensCard = {
      ...panelCard,
      ...(panelDraft ? { datasetId: panelDraft.datasetId, view: panelDraft.view, period: panelDraft.period } : {}),
      ...(panelType ? { type: panelType } : {}),
      ...(panelDetailsDraft
        ? {
            title: panelDetailsDraft.title.trim() || panelCard.title,
            description: panelDetailsDraft.description.trim() || undefined,
            section: panelDetailsDraft.section || undefined,
          }
        : {}),
      config: {
        ...(panelStyleConfig ?? panelCard.config ?? {}),
        sort: panelSort,
        ...(panelDetailsDraft
          ? {
              titleAlignment: panelDetailsDraft.alignment === 'default' ? undefined : panelDetailsDraft.alignment,
              titleSize: panelDetailsDraft.titleSize === 'default' ? undefined : panelDetailsDraft.titleSize,
            }
          : {}),
      },
    };
    const presented = editedTablePresentation(panelCard, nextCard);
    return panelDetailsDraft
      ? applyPanelPresentationFrame(presented, panelDetailsDraft, cardDataById.get(panelCard.id))
      : presented;
  }, [panelCard, panelDetailsDraft, panelDraft, panelSort, panelStyleConfig, panelType, cardDataById]);

  /**
   * Ask mode's Apply: writes the proposed card into the same draft state a
   * manual edit uses, so the canvas preview picks it up exactly as it would
   * a Data, Visual, Layout or Format change. Never saves anything itself.
   *
   * The Data section's own shelf editor (usePanelState, inside LensEditPanel)
   * only re-seeds from a different card id, so a proposal that changes the
   * dataset or view will not show in an already-open Data section until the
   * pane is reopened for this card; the collapsed summary and the canvas
   * preview both reflect it immediately regardless.
   */
  const onApplyProposal = useCallback(
    (proposed: LensCard) => {
      const before = currentPanelCard();
      if (!panelCardId || !before || !canAuthor) {
        return;
      }
      const changedSections = new Set<LensPanelSection>();
      if (proposed.type !== before.type) {
        changedSections.add('visual');
      }
      if (proposed.datasetId !== before.datasetId || !isEqual(proposed.view ?? {}, before.view ?? {})) {
        changedSections.add('data');
      }
      if (
        proposed.title !== before.title ||
        (proposed.description ?? '') !== (before.description ?? '') ||
        (proposed.size ?? 'large') !== (before.size ?? 'large') ||
        (proposed.height ?? '') !== (before.height ?? '') ||
        (proposed.section ?? '') !== (before.section ?? '') ||
        (proposed.config?.titleAlignment ?? 'default') !== (before.config?.titleAlignment ?? 'default') ||
        (proposed.config?.titleSize ?? 'default') !== (before.config?.titleSize ?? 'default')
      ) {
        changedSections.add('layout');
      }
      const withoutLayoutKeys = (config?: LensCardConfig) => {
        const { titleAlignment: _alignment, titleSize: _titleSize, ...rest } = config ?? {};
        return rest;
      };
      if (JSON.stringify(withoutLayoutKeys(proposed.config)) !== JSON.stringify(withoutLayoutKeys(before.config))) {
        changedSections.add('format');
      }

      setPanelType(proposed.type);
      setPanelDraft({ cardId: panelCardId, datasetId: proposed.datasetId, view: proposed.view ?? {} });
      setPanelDetailsDraft({
        title: proposed.title,
        description: proposed.description ?? '',
        alignment: proposed.config?.titleAlignment ?? 'default',
        titleSize: proposed.config?.titleSize ?? 'default',
        size: proposed.size ?? 'large',
        height: proposed.height ?? '',
        section: proposed.section ?? '',
      });
      setPanelStyleConfig(proposed.config ?? {});
      // Stays in Ask: the proposal card shows its own applied state and an
      // inline Save right there, and the reader chooses when to move to Edit
      // and see the change in place. The ring's timed clear only starts once
      // that happens (see the effect below), so it is not spent unseen.
      if (changedSections.size) {
        setRingedSections(Array.from(changedSections));
      }
    },
    [currentPanelCard, panelCardId, canAuthor],
  );

  // A ring means nothing while Ask is still showing: it starts its 4-second
  // clear only once the pane actually enters Edit with something still
  // ringed, whether that happens right after Apply or much later.
  useEffect(() => {
    if (panelMode !== 'edit' || ringedSections.length === 0) {
      return undefined;
    }
    const timeout = window.setTimeout(() => setRingedSections([]), 4000);
    return () => window.clearTimeout(timeout);
  }, [panelMode, ringedSections]);

  /**
   * Ask mode's inline Save: persists the current draft exactly as the Save
   * bar does, but leaves the pane open on this card in Ask mode so the
   * thread and the just-applied proposal stay on screen. Only the draft
   * states clear, since the card itself now matches what was just saved;
   * panelCardId is untouched (a new card keeps the same id once saved, so
   * panelCard now simply resolves from the saved spec instead of the local
   * draft, the moment newCardDraft clears).
   */
  const onSaveDraft = useCallback(async () => {
    const merged = currentPanelCard();
    // A viewer reaches this only if something upstream failed to disable the button that calls
    // it (AskPane's own Save is already disabled for them): refusing here as well means a viewer
    // can never actually persist a card, belt and braces.
    // Read vs build: nothing saves from view mode either. The chat offers "Review in edit" instead.
    if (!merged || !lens || !canAuthor) {
      return false;
    }
    const committedDraft = cardEditorDrafts.draft;
    setEditorConflicts(({ [`${lens.id}:${merged.id}`]: _resolved, ...others }) => others);
    try {
      await updateLens.mutateAsync({
        id: lens.id,
        title: lens.title,
        description: lens.description,
        spec: {
          ...lens.spec,
          cards:
            newCardDraft?.id === merged.id
              ? [...lens.spec.cards, merged]
              : lens.spec.cards.map((item) => (item.id === merged.id ? merged : item)),
        },
        version: lens.version,
        successMessage: 'Card updated',
        refetchData: cardDataNeedsRefresh(panelCard, merged, lens.spec.datasets ?? []),
        quietFailure: true,
      });
    } catch (error) {
      if ((error as { status?: number }).status !== 409) {
        enqueueSnackbar(lensWriteFailureMessage(error as Error, 'save'), { variant: 'error' });
        return false;
      }
      const mine = latestDraftsByLens.current[lens.id]?.[merged.id] ?? committedDraft;
      const base = committedDraft?.base ?? mine?.base;
      cardEditorDrafts.clear();
      if (mine && base) {
        setEditorConflicts((current) => ({
          ...current,
          [`${lens.id}:${merged.id}`]: {
            kind: 'lens_conflict',
            lensId: lens.id,
            error: lensChangeFailure(error),
            mine,
            base,
          },
        }));
      }
      return false;
    }
    if (latestCardDrafts.current[merged.id] === committedDraft) {
      cardEditorDrafts.clear();
    }
    setNewCardDraft((current) => (current?.id === merged.id ? null : current));
    return true;
  }, [
    currentPanelCard,
    canAuthor,
    lens,
    newCardDraft,
    updateLens,
    panelCard,
    cardEditorDrafts.clear,
    cardEditorDrafts.draft,
    enqueueSnackbar,
  ]);

  const editorAutoSave = useCardAutoSave({
    cardId: panelCard?.id,
    draft: cardEditorDrafts.draft,
    dirty: panelHasUnsavedChanges,
    enabled:
      canAuthor &&
      Boolean(panelCard) &&
      newCardDraft?.id !== panelCard?.id &&
      !updateLens.isPending &&
      !pendingConfirmation &&
      !isFinishingEditing,
    onSave: onSaveDraft,
    delayMs: CARD_EDIT_COMMIT_DELAY_MS,
  });

  const closePanel = useCallback(() => {
    setPanelCardId(null);
    setHoveredField(null);
  }, []);

  useEffect(() => {
    if (askRichardRequest) {
      setSidebarOpen(true);
      setPanelMode('ask');
    }
  }, [askRichardRequest, setSidebarOpen]);

  const hasChat = Boolean(renderAskPane);

  useEffect(() => {
    if (hasChat && sidebarOpen && selectedCardId) {
      setPanelCardId(selectedCardId);
    }
  }, [hasChat, selectedCardId, sidebarOpen]);

  useEffect(() => {
    if (hasChat && !selectedCardId) {
      setPanelCardId(null);
    }
  }, [hasChat, selectedCardId]);

  const discardPanelDraft = useCallback(() => {
    editorAutoSave.cancel();
    cardEditorDrafts.clear();
    if (lens && panelCardId) {
      setEditorConflicts(({ [`${lens.id}:${panelCardId}`]: _discarded, ...others }) => others);
    }
    setPanelCardId(null);
    setHoveredField(null);
    setNewCardDraft(null);
  }, [cardEditorDrafts.clear, editorAutoSave.cancel, lens, panelCardId]);

  const discardEditingSession = () => {
    editorAutoSave.cancel();
    cardEditorDrafts.clearLens();
    if (lens)
      setEditorConflicts((current) =>
        Object.fromEntries(Object.entries(current).filter(([key]) => !key.startsWith(`${lens.id}:`))),
      );
    setPanelCardId(null);
    setHoveredField(null);
    setNewCardDraft(null);
  };

  const requestSessionDiscard = (afterDiscard?: () => void) => {
    editorAutoSave.cancel();
    requestConfirmation({
      title: 'Discard unsaved changes?',
      description: 'Discard all unsaved card edits for this Lens? Previously saved edits stay saved.',
      confirmLabel: 'Discard changes',
      destructive: false,
      onConfirm: async () => {
        discardEditingSession();
        afterDiscard?.();
      },
    });
  };

  const requestPanelDiscard = useCallback(
    (afterDiscard?: () => void) => {
      editorAutoSave.cancel();
      requestConfirmation({
        confirmLabel: 'Discard changes',
        description: 'This card has changes that are not saved. Discard them and continue?',
        title: 'Discard unsaved changes?',
        destructive: false,
        onConfirm: async () => {
          discardPanelDraft();
          afterDiscard?.();
        },
      });
    },
    [discardPanelDraft, requestConfirmation, editorAutoSave.cancel],
  );

  const finishEditing = async () => {
    if (!canAuthor || updateLens.isPending || isFinishingEditing) return;
    editorAutoSave.cancel();
    const finish = () => {
      discardEditingSession();
      setIsCanvasSelectionMode(false);
      setSelectedCanvasCardIds([]);
      setIsLayoutWorkspaceOpen(false);
      setSettingsMenuAnchor(null);
      setPanelMode('ask');
      setSidebarOpen(false);
      setIsEditMode(false);
    };
    if (conflictingLensCardIds.length > 0) {
      setPanelCardId(conflictingLensCardIds[0]);
      setPanelMode('edit');
      setSidebarOpen(true);
      enqueueSnackbar('Review the conflicting changes before finishing editing.', { variant: 'warning' });
      return;
    }
    if (newCardDraft) {
      requestSessionDiscard(finish);
      return;
    }
    if (panelHasUnsavedChanges) {
      setIsFinishingEditing(true);
      const saved = await editorAutoSave.flush();
      setIsFinishingEditing(false);
      if (!saved) return;
    }
    const otherPendingId = pendingLensCardIds.find((id) => id !== panelCardId);
    if (otherPendingId) {
      setPanelCardId(otherPendingId);
      setPanelMode('edit');
      setSidebarOpen(true);
      enqueueSnackbar('This card still has unsaved edits. Save or discard them before finishing.', {
        variant: 'warning',
      });
      return;
    }
    finish();
  };

  // Region 5 (dataset actions) and region 2 (merge banner, "+ New"): the dataset the panel
  // currently targets follows the same not-yet-saved draft the live preview already reads, so
  // switching datasets in the panel (F2) updates these before Save is pressed.
  const panelDatasetId = panelDraft?.datasetId ?? panelCard?.datasetId;
  const panelDataset = lens?.spec.datasets?.find((entry) => entry.id === panelDatasetId);
  const panelDatasetCards = lens?.spec.cards.filter((card) => card.datasetId === panelDatasetId) ?? [];
  // Every card reading a dataset gets that dataset's own rows verbatim (loadCardData runs the
  // dataset's SQL, the view is applied client-side), so any card open in the panel is a source of
  // its dataset's loaded rows for Calculated field's preview.
  const panelCardData = panelCard ? cardDataFor(panelCard.id) : undefined;
  const panelDatasetRows = panelCardData?.rows
    ? { columns: panelCardData.columns ?? [], rows: panelCardData.rows }
    : undefined;

  const mergePairKey = (aId: string, bId: string) => [aId, bId].sort().join('::');
  const mergeCandidate =
    panelDataset && lens ? findMergeCandidateFor(panelDataset.id, lens.spec.datasets ?? []) : undefined;
  const activeMergeCandidate =
    mergeCandidate && !dismissedMergePairs.has(mergePairKey(mergeCandidate.from.id, mergeCandidate.into.id))
      ? mergeCandidate
      : undefined;

  /** Persists one changed dataset (F4's Add column, F5's Calculated field, F8's SQL edit) by
   *  replacing it in place, or adding it when it is new to the board (never called with an id
   *  already on the board that is not `nextDataset.id` itself, since every caller reads its
   *  starting point from `lens.spec.datasets`). */
  const saveDatasetChange = async (nextDataset: LensDataset) => {
    if (!lens) {
      return;
    }
    const existing = lens.spec.datasets ?? [];
    const isNew = !existing.some((entry) => entry.id === nextDataset.id);
    await updateLens.mutateAsync({
      id: lens.id,
      title: lens.title,
      description: lens.description,
      spec: {
        ...lens.spec,
        datasets: isNew
          ? [...existing, nextDataset]
          : existing.map((entry) => (entry.id === nextDataset.id ? nextDataset : entry)),
      },
      version: lens.version,
      successMessage: 'Dataset updated',
      // The SQL may have changed (Add column, Edit SQL), which every other dataset-action save
      // also risks even when it did not this time; re-running is the safe default here.
      refetchData: true,
    });
  };

  /** Metadata-only dataset edits (F8's relabel, kind override, calculated-field remove): change
   *  nothing the SQL returns, so unlike saveDatasetChange above these never re-run the query. */
  const saveDatasetMetadata = async (nextDataset: LensDataset, successMessage: string) => {
    if (!lens) {
      return;
    }
    await updateLens.mutateAsync({
      id: lens.id,
      title: lens.title,
      description: lens.description,
      spec: {
        ...lens.spec,
        datasets: (lens.spec.datasets ?? []).map((entry) => (entry.id === nextDataset.id ? nextDataset : entry)),
      },
      version: lens.version,
      successMessage,
      refetchData: false,
    });
  };

  /** F7: removes the absorbed dataset, repoints only the cards the merge preview found
   *  identical, and replaces the survivor's own entry with the passed-in object, which a union
   *  merge (`MergeBanner.tsx`) carries with a rewritten sql/columns/dryRun and a plain merge
   *  passes back unchanged. A blocked card keeps reading the absorbed dataset, which stays on
   *  the board for it, matching "kept on its own dataset because its number would change." */
  const mergeDatasets = async (
    survivor: LensDataset,
    absorbedDatasetId: string,
    movedCardIds: ReadonlyArray<string>,
  ) => {
    if (!lens) {
      return;
    }
    const movedIds = new Set(movedCardIds);
    const stillReadsAbsorbed = lens.spec.cards.some(
      (card) => card.datasetId === absorbedDatasetId && !movedIds.has(card.id),
    );
    // The absorbed dataset only actually leaves the board once nothing reads it any more; a
    // blocked card left behind keeps it alive, same as F7's own blocked-card wording implies.
    const datasetsWithoutAbsorbed = stillReadsAbsorbed
      ? lens.spec.datasets
      : (lens.spec.datasets ?? []).filter((entry) => entry.id !== absorbedDatasetId);
    await updateLens.mutateAsync({
      id: lens.id,
      title: lens.title,
      description: lens.description,
      spec: {
        ...lens.spec,
        cards: lens.spec.cards.map((card) => (movedIds.has(card.id) ? { ...card, datasetId: survivor.id } : card)),
        datasets: (datasetsWithoutAbsorbed ?? []).map((entry) => (entry.id === survivor.id ? survivor : entry)),
      },
      version: lens.version,
      successMessage: 'Merged. One query serves both.',
      // A union merge rewrites the survivor's own SQL, so the board needs the rows a fresh load
      // actually produces rather than whatever the pre-merge cache still holds.
      refetchData: true,
    });
    setDismissedMergePairs((current) => new Set(current).add(mergePairKey(absorbedDatasetId, survivor.id)));
  };

  /** F6: saves a brand-new dataset onto the board and returns its id, the same slugify-and-dedupe
   *  id generation DatasetsDialog's own onCreateDataset already uses just below. */
  const saveNewDataset = async (
    draft: Pick<LensDataset, 'name' | 'sql' | 'columns' | 'grain' | 'source' | 'dryRun'>,
  ) => {
    if (!lens) {
      throw new Error('No Lens is open to add this dataset to.');
    }
    const taken = new Set([
      ...(lens.spec.datasets ?? []).map((entry) => entry.id),
      ...(lens.spec.sections ?? []).map((entry) => entry.id),
    ]);
    let id =
      draft.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 58) || 'dataset';
    if (!/^[a-z]/.test(id)) {
      id = `dataset-${id}`;
    }
    let candidate = id;
    let counter = 2;
    while (taken.has(candidate)) {
      candidate = `${id}-${counter}`;
      counter += 1;
    }
    await updateLens.mutateAsync({
      id: lens.id,
      title: lens.title,
      description: lens.description,
      spec: { ...lens.spec, datasets: [...(lens.spec.datasets ?? []), { ...draft, id: candidate }] },
      version: lens.version,
      successMessage: `Added dataset "${draft.name}"`,
      refetchData: false,
    });
    return candidate;
  };

  // Names come from the rendered result, so colour and per-field controls are
  // labelled with what the card actually shows: series for a chart, columns for
  // a table. Feeds the panel's own Chart/Details/Style tab content below.
  const { editingSeriesNames, editingFieldNames, editingColumnNames, editingValueExtent } = useMemo(() => {
    const empty = {
      editingSeriesNames: [],
      editingFieldNames: [],
      editingColumnNames: [],
      editingValueExtent: undefined,
    };
    if (!panelCard) {
      return empty;
    }

    // Columns as the query returned them. A view groups and measures by
    // column, so it must never be offered the series names below: after a
    // pivot those are the values of a column, not columns.
    //
    // A card just pointed at a dataset has not fetched it yet, so borrow the
    // columns from a card already reading that dataset rather than offering
    // the ones its previous query happened to return.
    const peerColumns = panelCard.datasetId
      ? cardData.find((item) => {
          const owner = lens?.spec.cards.find((entry) => entry.id === item.cardId);
          return owner?.datasetId === panelCard.datasetId && Boolean(item.columns?.length);
        })?.columns
      : undefined;

    const cardResult = cardData.find((item) => item.cardId === panelCard.id);
    const dataset = panelCard.datasetId
      ? (lens?.spec.datasets ?? []).find((entry) => entry.id === panelCard.datasetId)
      : undefined;
    // A dataset's calculated fields are columns as far as a view is concerned:
    // one is exactly what a card should be measuring when the card wants a rate.
    const fieldNames = (dataset?.fields ?? []).map((field) => field.name);
    // The dataset's own declared schema, from a BigQuery dry run, is the
    // column list every view resolves against; never a guess read out of the
    // dataset's SQL text or a query result. A dataset saved before that
    // schema existed declares no columns yet, and for that legacy case only,
    // this borrows what a sibling card reading the same dataset already
    // returned, or this card's own last result, so the editor still has
    // something to offer while the dataset waits to be declared.
    const declaredColumnNames = dataset?.columns?.map((column) => column.name);
    const editingColumnNames = declaredColumnNames
      ? [...new Set([...declaredColumnNames, ...fieldNames])]
      : [...new Set([...(peerColumns ?? cardResult?.columns ?? []), ...fieldNames])];
    if (!cardResult?.rows || !cardResult.columns) {
      return { ...empty, editingColumnNames };
    }

    if (panelCard.type === 'table') {
      return {
        editingSeriesNames: [],
        editingFieldNames: cardResult.columns,
        editingColumnNames,
        editingValueExtent: undefined,
      };
    }
    // Series are what a chart plots, which is what colours and per-field
    // formats attach to.
    //
    // Shaped by the card's own view first, exactly as the card body does. A
    // card that splits its rows by a column plots the values of that column,
    // not the measure columns the query returned, so a model built on the raw
    // rows offers the editor a list of names the renderer never draws: the
    // second-scale picker, the mark-per-series picker, the value-label picker
    // and the colour swatches all read this list.
    const editingFields = prepareFields(
      (lens?.spec.datasets ?? []).find((entry) => entry.id === panelCard.datasetId)?.fields,
      cardResult.columns,
    );
    const enrichedRows = editingFields.row.length
      ? applyRowFields(cardResult.rows, editingFields.row)
      : cardResult.rows;
    const enrichedColumns = editingFields.row.length
      ? [...cardResult.columns, ...editingFields.row.map((entry) => entry.field.name)]
      : cardResult.columns;
    const editingShaped = applyView(
      enrichedRows,
      enrichedColumns,
      panelCard.view,
      panelCard.type,
      editingFields.aggregate,
      // A governed card's rows are already one-per-group server-side, so this editor preview
      // must not re-reduce them a second time.
      cardResult.aggregated,
      effectiveGovernedMeasureColumns(
        (lens?.spec.datasets ?? []).find((entry) => entry.id === panelCard.datasetId),
        { savedDatasets: lens?.spec.datasets, metadata: data?.governedMeasureMetadata },
      ),
    );
    const chartModel = buildChartModel(editingShaped.rows, editingShaped.columns, cardChartConfig(panelCard));
    const series = chartModel?.series ?? [];
    // The range of what is actually plotted, so the axis controls can name the
    // numbers rather than leaving someone to guess where a floor may sit. The
    // dialog has no rows and cannot draw the card, so this is the substitute
    // for seeing it.
    const editingValueExtent = chartModel ? plottedValueExtent(chartModel) : undefined;
    return { editingSeriesNames: series, editingFieldNames: series, editingColumnNames, editingValueExtent };
  }, [panelCard, cardData, lens?.spec.datasets, data?.governedMeasureMetadata]);

  // The card the panel is editing, with its unsaved edits substituted in:
  // this, not a second shaping call, is F1's "redraws from rows already
  // loaded" preview. LensCardView already shapes a card from its own
  // datasetId/view/type/config, so overriding those on the object it
  // receives is the same path the canvas always draws through, not a new
  // one. datasetId/view come from C1's panelDraft; type, title/description/
  // size/height/section and config come from lane C4's own panelType/
  // panelDetailsDraft/panelStyleConfig, each undefined until its tab reports
  // a first change.
  const previewLayoutCards = useMemo(() => {
    return layoutCards.map((card) => {
      const retained = cardEditorDrafts.drafts[card.id];
      if (!retained) return card;
      const { data: panelDraft, type: panelType, details: panelDetailsDraft, config: panelStyleConfig } = retained;
      const definition: LensCard = {
        ...card,
        ...(panelDraft ? { datasetId: panelDraft.datasetId, view: panelDraft.view, period: panelDraft.period } : {}),
        ...(panelType ? { type: panelType } : {}),
      };
      const nextCard: LensCard = {
        ...card,
        ...(loadedRowsServe(card, definition, cardDataById.get(card.id))
          ? { datasetId: definition.datasetId, view: definition.view, period: definition.period, type: definition.type }
          : {}),
        ...(panelDetailsDraft
          ? {
              title: panelDetailsDraft.title,
              description: panelDetailsDraft.description || undefined,
              section: panelDetailsDraft.section || undefined,
            }
          : {}),
        config: {
          ...(panelStyleConfig ?? card.config ?? {}),
          ...(panelDetailsDraft
            ? {
                titleAlignment: panelDetailsDraft.alignment === 'default' ? undefined : panelDetailsDraft.alignment,
                titleSize: panelDetailsDraft.titleSize === 'default' ? undefined : panelDetailsDraft.titleSize,
              }
            : {}),
        },
      };
      const presented = editedTablePresentation(card, nextCard);
      return panelDetailsDraft
        ? applyPanelPresentationFrame(presented, panelDetailsDraft, cardDataById.get(card.id))
        : presented;
    });
  }, [layoutCards, cardEditorDrafts.drafts, cardDataById]);

  // Existing frames are respected; older cards receive a safe, data-aware
  // position on first render rather than relying on CSS to guess a row.
  const canvasCards = useMemo(
    () =>
      resolveCanvasLayouts(
        cardsShapedLikeTheirRows(previewLayoutCards, rowsLensCards, cardDataById, cardDataById),
        cardDataById,
      ),
    [cardDataById, previewLayoutCards, rowsLensCards],
  );
  const visualAuditRootRef = useRef<HTMLDivElement | null>(null);
  const [visualAuditIssues, setVisualAuditIssues] = useState<Array<LensVisualIssue>>([]);
  const [visualAuditComplete, setVisualAuditComplete] = useState(false);
  const automaticVisualRetries = useRef(new Set<string>());
  const visualRetryScope = `${lens?.id ?? ''}:${JSON.stringify(effectiveFilterValues)}`;
  useEffect(() => {
    automaticVisualRetries.current.clear();
  }, [visualRetryScope]);
  useEffect(() => {
    const root = visualAuditRootRef.current;
    if (!root || isLoadingData || isShowingPreviousData) {
      setVisualAuditComplete(false);
      return;
    }
    let frame = 0;
    const run = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const next = uniqueVisualIssues([
          ...auditLensCardResults(canvasCards, cardDataById),
          ...auditLensCardElements(root, canvasCards),
        ]);
        // The observer sees this page's own re-render, so a fresh but equal array would loop forever.
        setVisualAuditIssues((current) => (sameVisualIssues(current, next) ? current : next));
        setVisualAuditComplete(true);
      });
    };
    run();
    const resizeObserver = new ResizeObserver(run);
    resizeObserver.observe(root);
    const mutationObserver = new MutationObserver(run);
    mutationObserver.observe(root, { attributes: true, childList: true, subtree: true });
    return () => {
      window.cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [canvasCards, isLoadingData, isShowingPreviousData, cardDataById]);
  useEffect(() => {
    if (!lens || dataError || !visualAuditComplete || refreshCard.isPending) return;
    const cardId = retryableVisualIssueCardIds(visualAuditIssues, cardDataById).find(
      (candidate) => !automaticVisualRetries.current.has(`${visualRetryScope}:${candidate}`),
    );
    if (!cardId) return;
    // Mark before starting the request so StrictMode and fast re-renders cannot spend a second
    // warehouse query. A genuine empty result is deliberately excluded by the helper above.
    automaticVisualRetries.current.add(`${visualRetryScope}:${cardId}`);
    refreshCard.mutate({ lensId: lens.id, cardId, filters: effectiveFilterValues });
  }, [
    effectiveFilterValues,
    lens,
    cardDataById,
    dataError,
    refreshCard,
    visualAuditComplete,
    visualAuditIssues,
    visualRetryScope,
  ]);

  // Everything a written reading depended on, so saved prose is shown only
  // while it still describes the board in front of the reader.
  const lensViewState = useMemo(
    () => ({ filters: filterValues, selections, ranges }),
    [filterValues, selections, ranges],
  );
  const signatureForSection = useCallback(
    (sectionId: string) =>
      viewSignature({ view: lensViewState, cards: sectionCards(lens?.spec.cards ?? [], sectionId) }),
    [lensViewState, lens?.spec.cards],
  );
  const periodLabel = useMemo(() => [boardRange.from, boardRange.to].filter(Boolean).join(' to '), [boardRange]);
  // The reading is written for the reader who asked, and saved so the next one
  // does not pay for it again. A failed save is not surfaced: the paragraph is
  // already on screen, and losing only its persistence is not worth an error.
  const persistSectionReading = useCallback(
    async (sectionId: string, cache: LensReadingCache) => {
      if (!lens) return;
      const sections = (lens.spec.sections ?? []).map((section) =>
        section.id === sectionId && section.reading?.mode === 'narrative'
          ? { ...section, reading: { ...section.reading, cached: cache } }
          : section,
      );
      await updateLens.mutateAsync({
        id: lens.id,
        title: lens.title,
        description: lens.description,
        spec: { ...lens.spec, sections },
        version: lens.version,
        quietSuccess: true,
        quietFailure: true,
      });
    },
    [lens, updateLens],
  );

  // Cards keep their spec order inside a section; anything unassigned renders
  // first, so adding sections later never hides an existing card.
  const cardGroups = useMemo(() => {
    if (!lens) {
      return [];
    }
    const sections = lens.spec.sections ?? [];
    const ungrouped = canvasCards.filter((card) => !card.section || !sections.some((s) => s.id === card.section));
    const withFrames = (section: LensSection | null, groupCards: Array<LensCard>) => ({
      id: section?.id ?? null,
      title: section?.title?.trim() ? section.title : null,
      question: section?.question?.trim() ? section.question : null,
      section,
      cards: groupCards,
    });

    const groups = sections.map((section) =>
      withFrames(
        section,
        canvasCards.filter((card) => card.section === section.id),
      ),
    );
    return [
      ...(ungrouped.length ? [withFrames(null, ungrouped)] : []),
      ...groups.filter((group) => group.cards.length > 0),
    ];
  }, [canvasCards, lens]);

  // One or two tables open by default. Larger boards open compact, complete
  // results. Explicit open/closed choices take precedence and stay local.
  const rowsDrawerLensId = lens?.id;
  const [rowsDrawerState, setRowsDrawerState] = useState<{ lensId?: string; choices: RowsDrawerChoices }>({
    choices: {},
  });
  const [exportExpandedRowsCardIds, setExportExpandedRowsCardIds] = useState<ReadonlySet<string> | null>(null);
  useEffect(() => {
    setRowsDrawerState({
      lensId: rowsDrawerLensId,
      choices: rowsDrawerLensId ? readRowsDrawerChoices(rowsDrawerLensId) : {},
    });
    setExportExpandedRowsCardIds(null);
  }, [rowsDrawerLensId]);
  const expandedRowsCardIds = useMemo(
    () =>
      exportExpandedRowsCardIds ??
      expandedRowsForResults(
        canvasCards,
        shownCardDataFor,
        rowsDrawerState.lensId === rowsDrawerLensId
          ? rowsDrawerState.choices
          : rowsDrawerLensId
            ? readRowsDrawerChoices(rowsDrawerLensId)
            : {},
        lens?.spec.datasets,
      ),
    [canvasCards, shownCardDataFor, rowsDrawerState, rowsDrawerLensId, exportExpandedRowsCardIds, lens?.spec.datasets],
  );
  const toggleRowsDrawer = useCallback(
    (cardId: string) => {
      if (!rowsDrawerLensId) {
        return;
      }
      const expanded = !expandedRowsCardIds.has(cardId);
      setRowsDrawerState((current) => {
        const next = { ...(current.lensId === rowsDrawerLensId ? current.choices : {}), [cardId]: expanded };
        writeRowsDrawerChoices(rowsDrawerLensId, next);
        return { lensId: rowsDrawerLensId, choices: next };
      });
    },
    [rowsDrawerLensId, expandedRowsCardIds],
  );
  // The true saved frame for every table card currently showing as a strip, so a drag or
  // resize elsewhere on the board never persists the strip's height as that card's own.
  const collapsedTableCardIds = useMemo(
    () =>
      new Set(
        canvasCards.filter((card) => card.type === 'table' && !expandedRowsCardIds.has(card.id)).map((card) => card.id),
      ),
    [canvasCards, expandedRowsCardIds],
  );

  // Coarser buckets for whichever columns the card's own result carries dates
  // in. Offered beside the columns rather than added to them, so a grain shows
  // up where a reader groups and nowhere they pick a measure.
  const editingGrainNames = useMemo(() => {
    const result = panelCard ? cardData.find((item) => item.cardId === panelCard.id) : undefined;
    return result?.rows && result.columns ? grainOptions(result.rows, result.columns) : [];
  }, [panelCard, cardData]);

  // The panel's Chart, Details and Style tab content. An insight card shows
  // nothing on the Data tab (LensEditPanel's own isInsight check), but Chart,
  // Details and Style render for one exactly like any other card, so none of
  // this needs to special-case one.
  //
  // useStyleTabState is a hook, called unconditionally here: when no card is
  // open, a fixed-id placeholder stands in so the call still happens on
  // every render, in the same order, per the rules of hooks. Its id never
  // changes while the panel is closed, so it never re-seeds for no reason.
  const stylePlaceholderCard: LensCard = panelCard ?? { id: '__panel-style-placeholder__', title: '', type: 'metric' };
  const panelStyleType = panelType ?? stylePlaceholderCard.type;
  const panelBusy = updateLens.isPending;
  const styleTabState = useStyleTabState({
    card: { ...stylePlaceholderCard, config: panelStyleConfig ?? stylePlaceholderCard.config },
    columnNames: editingColumnNames,
    onChange: setPanelStyleConfig,
    seriesNames: editingSeriesNames,
    sortBy: panelSort?.by ?? 'none',
    sortDirection: panelSort?.direction ?? 'desc',
    sortSeries: panelSort?.series ?? '',
    type: panelStyleType,
    valueExtent: editingValueExtent,
  });
  const chartTabContent = panelCard ? (
    <ChartTab
      busy={panelBusy}
      card={panelCard}
      columnNames={editingColumnNames}
      draft={{ type: panelType ?? panelCard.type }}
      grainNames={editingGrainNames}
      rows={panelCardData?.status === 'ready' ? panelCardData.rows : undefined}
      onChange={(next) => setPanelType(next.type)}
      onSparklineChange={(value) =>
        setPanelStyleConfig((current) => ({ ...(current ?? panelCard.config ?? {}), sparkline: value }))
      }
      onValueLabelsChange={styleTabState.setValueLabelsOn}
      onValueLabelModeChange={styleTabState.setValueLabelMode}
      onValueLabelSizeChange={styleTabState.setValueLabelSize}
      onStackedChange={styleTabState.setStacked}
      barOrientation={styleTabState.barOrientation}
      onBarOrientationChange={styleTabState.setBarOrientation}
      seriesNames={editingSeriesNames}
      sparkline={(panelStyleConfig ?? panelCard.config)?.sparkline ?? true}
      stacked={styleTabState.stacked}
      valueLabels={styleTabState.valueLabelsOn}
      valueLabelMode={styleTabState.valueLabelMode}
      valueLabelSize={styleTabState.valueLabelSize}
      viewSeries={panelDraft?.view?.series ?? ''}
      viewX={panelDraft?.view?.x ?? panelCard.view?.x ?? ''}
    />
  ) : undefined;

  const panelDetailsDraftValue: DetailsTabDraft | null = panelCard
    ? (panelDetailsDraft ?? defaultDetailsDraft(panelCard))
    : null;

  const detailsTabContent = panelDetailsDraftValue ? (
    <DetailsTab
      busy={panelBusy}
      draft={panelDetailsDraftValue}
      isTable={panelCard?.type === 'table'}
      layout={panelCard ? applyPanelPresentationFrame(panelCard, panelDetailsDraftValue).layout : undefined}
      onChange={setPanelDetailsDraft}
      sections={lens?.spec.sections ?? []}
    />
  ) : undefined;

  const chartDetailsContent = panelCard ? (
    <StyleTab
      busy={panelBusy}
      card={panelCard}
      columnNames={editingColumnNames}
      fieldNames={editingFieldNames}
      scope="chart"
      seriesNames={editingSeriesNames}
      state={styleTabState}
      type={panelStyleType}
      valueExtent={editingValueExtent}
    />
  ) : undefined;
  const appearanceDetailsContent = panelCard ? (
    <StyleTab
      busy={panelBusy}
      card={panelCard}
      columnNames={editingColumnNames}
      fieldNames={editingFieldNames}
      scope="appearance"
      seriesNames={editingSeriesNames}
      state={styleTabState}
      type={panelStyleType}
      valueExtent={editingValueExtent}
    />
  ) : undefined;
  const visualBasicsContent = panelCard ? (
    <VisualBasics busy={panelBusy} state={styleTabState} type={panelStyleType} />
  ) : undefined;

  const askPaneContent = renderAskPane
    ? renderAskPane({
        card: panelCard ?? undefined,
        onApplyProposal,
        onSaveDraft: async () => {
          await onSaveDraft();
        },
        dirty: panelHasUnsavedChanges,
        isSaving: updateLens.isPending,
        lensTitle: lens?.title,
        canEdit: canAuthor,
        onClearCard: () => {
          setPanelCardId(null);
          setHoveredField(null);
          setSidebarOpen(true);
        },
      })
    : undefined;

  /**
   * Start a card without saving a blank object first. The unified workbench
   * leads with askRichard; standalone Lens surfaces safely fall back to the
   * manual editor when no ask pane has been supplied.
   */
  const unusedCardId = (current: Lens) =>
    makeUniqueId(
      'new card',
      new Set([
        ...current.spec.cards.map((card) => card.id),
        ...(current.spec.datasets ?? []).map((dataset) => dataset.id),
        ...(current.spec.sections ?? []).map((section) => section.id),
      ]),
      'card',
    );

  const beginCardPlan = (mode: LensPanelMode = 'ask') => {
    if (!lens || !data?.access.canEdit || isFinishingEditing) {
      return;
    }
    setIsEditMode(true);
    const card: LensCard = {
      id: unusedCardId(lens),
      title: 'New card',
      type: 'metric',
      view: { aggregate: 'sum' },
    };
    setNewCardDraft(card);
    setPanelCardId(card.id);
    // Nothing is configured yet, so Add card opens with askRichard exposed.
    // The explicit Build manually path still opens the same pane in Edit.
    const resolvedMode = mode === 'ask' && !renderAskPane ? 'edit' : mode;
    setPanelMode(resolvedMode);
    setPanelSection(resolvedMode === 'edit' ? 'data' : undefined);
    setPanelDraft(null);
    setHoveredField(null);
  };

  const openCardPane = (card: LensCard, mode: LensPanelMode = renderAskPane ? 'ask' : 'edit') => {
    if (isFinishingEditing) return;
    if (mode !== 'ask' && data?.access.canEdit) setIsEditMode(true);
    onSelectCard?.({ id: card.id, title: card.title });
    setPanelCardId(card.id);
    setPanelMode(mode);
    setPanelSection(undefined);
    setPanelFormatFocus(undefined);
  };

  const addBlankCard = async () => {
    setSettingsMenuAnchor(null);
    if (!lens) {
      return;
    }
    const card: LensCard = {
      id: unusedCardId(lens),
      title: 'New card',
      type: 'metric',
      brief: { request: 'New card' },
    };
    await updateLens.mutateAsync({
      id: lens.id,
      title: lens.title,
      description: lens.description,
      spec: { ...lens.spec, cards: [...lens.spec.cards, card] },
      version: lens.version,
      successMessage: 'Card added',
    });
    setSidebarOpen(true);
    openCardPane(card, 'edit');
  };

  const closeSidebar = () => {
    const close = () => {
      closePanel();
      setSidebarOpen(false);
    };
    if (panelHasUnsavedChanges) {
      requestPanelDiscard(close);
    } else {
      close();
    }
  };

  const openDataCardEditor = () => {
    if (!lens || !data?.access.canEdit) {
      return;
    }
    setSettingsMenuAnchor(null);
    setIsEditMode(true);
    setCustomViewDataEditingLensId(lens.spec.customView ? lens.id : null);
    const target =
      (newCardDraft?.id === panelCardId ? newCardDraft : undefined) ??
      (panelCardId ? lens.spec.cards.find((card) => card.id === panelCardId) : undefined) ??
      (selectedCardId ? lens.spec.cards.find((card) => card.id === selectedCardId) : undefined) ??
      lens.spec.cards[0];
    if (!target) {
      beginCardPlan(renderAskPane ? 'ask' : 'edit');
      return;
    }
    openCardPane(target);
  };

  const openCustomViewEditor = () => {
    if (!data?.access.canEdit) return;
    setIsEditMode(true);
    setSettingsMenuAnchor(null);
    setIsCustomViewOpen(true);
  };

  const openLensDetailsEditor = () => {
    if (!lens || !data?.access.canEdit || panelHasUnsavedChanges || updateLens.isPending) return;
    setSettingsMenuAnchor(null);
    setDetailsOpen(false);
    setIsEditMode(true);
    setEditingLensDetails(lens);
  };

  const openEditPane = () => {
    if (lens?.spec.customView) {
      openCustomViewEditor();
      return;
    }
    openDataCardEditor();
  };

  const startEditing = () => {
    if (!lens || !data?.access.canEdit) return;
    setIsEditMode(true);
    setSidebarOpen(true);
    setPanelMode('lens');
  };

  // The toolbar always asks about the Lens. Card actions opt into a card explicitly.
  const openAskPaneFromToolbar = (focusComposer = false) => {
    if (onOpenAskRichardPanel) {
      const openLensConversation = () => {
        discardPanelDraft();
        onOpenAskRichardPanel();
        if (focusComposer) {
          setFocusAskComposer(true);
        }
      };
      if (panelHasUnsavedChanges) {
        requestPanelDiscard(openLensConversation);
      } else {
        openLensConversation();
      }
      return;
    }
    if (!lens || !renderAskPane) {
      return;
    }
    const target =
      (newCardDraft?.id === panelCardId ? newCardDraft : undefined) ??
      (panelCardId ? lens.spec.cards.find((card) => card.id === panelCardId) : undefined) ??
      (selectedCardId ? lens.spec.cards.find((card) => card.id === selectedCardId) : undefined) ??
      lens.spec.cards[0];
    if (!target) {
      if (focusComposer) {
        setSidebarOpen(true);
        setPanelMode('ask');
        setFocusAskComposer(true);
        return;
      }
      // Starting a brand-new card from nothing is an authoring action a viewer has no button for
      // elsewhere either; the drawer is at least somewhere to ask, on a Lens with nothing to open
      // the pane on yet.
      if (data.access.canEdit) {
        beginCardPlan('ask');
      } else {
        onOpenAskRichardPanel?.();
      }
      return;
    }
    onSelectCard?.({ id: target.id, title: target.title });
    setPanelCardId(target.id);
    setPanelMode('ask');
    setPanelSection(undefined);
    if (focusComposer) {
      setFocusAskComposer(true);
    }
  };

  const openAskPaneFromFilters = () => {
    filtersAskRequested.current = true;
    setFiltersAnchor(null);
  };

  const handleFiltersClosed = () => {
    if (!filtersAskRequested.current) {
      return;
    }
    filtersAskRequested.current = false;
    if (!onOpenAskRichardPanel && !renderAskPane) {
      navigate(legacyLensUrl(lens?.id, window.location.search, window.location.hash));
      return;
    }
    openAskPaneFromToolbar(true);
  };

  useEffect(() => {
    if (!focusAskComposer) {
      return;
    }
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout>;
    const focusExistingComposer = () => {
      // The legacy conversation lives in this pane; live Lens uses the shell's
      // existing composer. Focusing either must leave its draft and chat intact.
      const candidates = [
        ...(askPaneContainerRef.current?.querySelectorAll<HTMLTextAreaElement>('textarea') ?? []),
        ...document.querySelectorAll<HTMLTextAreaElement>('[data-testid="ui-stream-composer"] textarea'),
      ];
      const composer = candidates.find(
        (input) => !input.disabled && input.getAttribute('aria-hidden') !== 'true' && input.getClientRects().length,
      );
      if (composer) {
        composer.focus();
        setFocusAskComposer(false);
      } else if (++attempts < 20) {
        timer = setTimeout(focusExistingComposer, 50);
      } else {
        setFocusAskComposer(false);
      }
    };
    timer = setTimeout(focusExistingComposer, 0);
    return () => clearTimeout(timer);
  }, [focusAskComposer]);

  const saveCardLayout = useCallback(
    (cards: Array<LensCard>, successMessage: string) => {
      if (!lens) {
        return;
      }
      if (!canAuthor || updateLens.isPending) return;
      const placed = placementOntoSavedCards(cards, lens.spec.cards);
      setCardLayoutDraft(placed);
      void updateLens
        .mutateAsync({
          id: lens.id,
          title: lens.title,
          description: lens.description,
          spec: { ...lens.spec, cards: placed },
          version: lens.version,
          successMessage,
          refetchData: false,
        })
        .catch(() => setCardLayoutDraft(null));
    },
    [lens, updateLens, canAuthor],
  );

  // One handler per action, called from both the canvas card's own kebab
  // menu (LensCardShell.tsx) and the card editor pane's strip (CardStrip.tsx
  // via LensEditPanel's onDeleteCard/onDuplicateCard/onMoveCardToSection), so
  // the two entry points can never drift apart into separate implementations.
  const deleteLensCard = useCallback(
    (card: LensCard) => {
      if (!lens) {
        return;
      }
      requestConfirmation({
        title: `Remove “${card.title}”?`,
        description: 'This takes the card off this Lens. Its underlying data stays untouched.',
        confirmLabel: 'Remove card',
        onConfirm: async () => {
          await updateLens.mutateAsync({
            id: lens.id,
            title: lens.title,
            description: lens.description,
            spec: { ...lens.spec, cards: lens.spec.cards.filter((item) => item.id !== card.id) },
            version: lens.version,
            successMessage: 'Card removed',
          });
        },
      });
    },
    [lens, requestConfirmation, updateLens],
  );

  const duplicateLensCard = useCallback(
    async (card: LensCard) => {
      if (!lens) {
        return;
      }
      // A copy is placed by the layout pass rather than on top of its
      // original: it carries no layout, so resolveCanvasLayouts finds it the
      // first vacant frame.
      const taken = new Set(lens.spec.cards.map((item) => item.id));
      const copy: LensCard = {
        ...card,
        id: makeUniqueId(`${card.id}-copy`, taken, 'card'),
        title: `${card.title} (copy)`,
        layout: undefined,
      };
      const index = lens.spec.cards.findIndex((item) => item.id === card.id);
      const cards = [...lens.spec.cards];
      cards.splice(index + 1, 0, copy);
      await updateLens.mutateAsync({
        id: lens.id,
        title: lens.title,
        description: lens.description,
        spec: { ...lens.spec, cards },
        version: lens.version,
        successMessage: 'Card duplicated',
      });
    },
    [lens, updateLens],
  );

  const lensCardMoveOptions = useCallback(
    (card: LensCard): Array<{ label: string; onMove: () => void }> => {
      if (!lens) {
        return [];
      }
      return [
        { id: undefined, title: 'No section' },
        ...(lens.spec.sections ?? []).map((section) => ({ id: section.id, title: section.title })),
      ]
        .filter((section) => section.id !== card.section)
        .map((section) => ({
          label: section.id ? `Move to ${section.title}` : 'Move out of sections',
          onMove: () =>
            saveCardLayout(
              moveLensCardToSection(canvasCards, lens.spec.sections ?? [], card.id, section.id),
              section.id ? `Moved to ${section.title}` : 'Moved out of sections',
            ),
        }));
    },
    [canvasCards, lens, saveCardLayout],
  );

  /**
   * Insight text is part of the Lens, not a throwaway render-time request.
   * The card waits for this promise before it re-enables Refresh, so a reader
   * cannot stack a second AskRichard call while the first answer is saving.
   */
  const persistInsight = useCallback(
    async (cardId: string, insight: NonNullable<LensCard['insight']>) => {
      if (!lens || !data?.access.canEdit) {
        throw new Error('You no longer have permission to save this insight.');
      }
      await updateLens.mutateAsync({
        id: lens.id,
        title: lens.title,
        description: lens.description,
        spec: {
          ...lens.spec,
          cards: lens.spec.cards.map((item) => (item.id === cardId ? { ...item, insight } : item)),
        },
        version: lens.version,
        quietFailure: true,
        quietSuccess: true,
        refetchData: false,
      });
    },
    [data?.access.canEdit, lens, updateLens],
  );

  const handleSmartArrange = useCallback(() => {
    saveCardLayout(
      smartArrangeCards(canvasCards, cardDataById, measureInsightHeights(canvasCards)),
      'Smart layout applied',
    );
  }, [canvasCards, cardDataById, saveCardLayout]);

  const toggleCanvasCardSelection = useCallback((cardId: string) => {
    setSelectedCanvasCardIds((current) =>
      current.includes(cardId) ? current.filter((id) => id !== cardId) : [...current, cardId],
    );
  }, []);

  const handleArrangeSelectedEvenly = useCallback(() => {
    if (selectedCanvasCardIds.length < 2) {
      return;
    }
    saveCardLayout(
      evenlyArrangeSelectedCards(canvasCards, selectedCanvasCardIds, cardDataById),
      'Selected cards arranged evenly',
    );
  }, [canvasCards, cardDataById, saveCardLayout, selectedCanvasCardIds]);

  const handleMoveSelectedToSection = useCallback(
    (destinationSectionId?: string) => {
      if (!lens || selectedCanvasCardIds.length === 0) {
        return;
      }
      const cards = selectedCanvasCardIds.reduce(
        (current, cardId) => moveLensCardToSection(current, lens.spec.sections ?? [], cardId, destinationSectionId),
        canvasCards,
      );
      const destinationTitle = destinationSectionId
        ? (lens.spec.sections ?? []).find((section) => section.id === destinationSectionId)?.title || 'Untitled section'
        : 'no section';
      const count = selectedCanvasCardIds.length;
      saveCardLayout(cards, `Moved ${count} ${count === 1 ? 'card' : 'cards'} to ${destinationTitle}`);
      setSelectedCanvasCardIds([]);
      setBulkMoveAnchor(null);
    },
    [canvasCards, lens, saveCardLayout, selectedCanvasCardIds],
  );

  const handleApplyBulkPresentation = useCallback(
    (presentation: BulkCardPresentation) => {
      if (!lens || selectedCanvasCardIds.length === 0) {
        return;
      }
      const cards = applyBulkCardPresentation(lens.spec.cards, selectedCanvasCardIds, presentation);
      const count = selectedCanvasCardIds.length;
      void updateLens.mutateAsync({
        id: lens.id,
        title: lens.title,
        description: lens.description,
        spec: { ...lens.spec, cards },
        version: lens.version,
        successMessage: `Updated formatting for ${count} ${count === 1 ? 'card' : 'cards'}`,
        refetchData: false,
      });
    },
    [lens, selectedCanvasCardIds, updateLens],
  );

  // Loaded rows seed filter options; opening a governed field also loads its values.
  /**
   * Every card that returned rows can be filtered, whether or not it shares a
   * query. Filtering depends on cards carrying the same field, not on them
   * coming from one dataset.
   */
  const filterSources = useMemo(() => {
    const sources: Array<{
      cardId: string;
      title: string;
      rows: Array<Record<string, unknown>>;
      columns: Array<string>;
    }> = [];
    for (const card of lens?.spec.cards ?? []) {
      const loaded = cardData.find((item) => item.cardId === card.id);
      if (!loaded?.rows?.length || !loaded.columns?.length) {
        continue;
      }
      sources.push({ cardId: card.id, title: card.title, rows: loaded.rows, columns: loaded.columns });
    }
    return sources;
  }, [lens, cardData]);

  // Empty results still report filter reach. Keep these receipts separate from
  // the populated rows used to offer values, so filtering a card to zero rows
  // does not remove it from the coverage count.
  const filterCoverageSources = useMemo(() => {
    return (lens?.spec.cards ?? []).flatMap((card) => {
      const loaded = cardData.find((item) => item.cardId === card.id);
      if (!loaded || (loaded.status !== 'ready' && loaded.status !== 'empty')) {
        return [];
      }
      const dataset = lens?.spec.datasets?.find((dataset) => dataset.id === card.datasetId);
      const dimensions =
        dataset?.columns?.filter((field) => field.kind === 'dimension').map((field) => field.name) ?? [];
      return [
        { cardId: card.id, title: card.title, columns: [...new Set([...(loaded.columns ?? []), ...dimensions])] },
      ];
    });
  }, [lens, cardData]);

  // Which cards a column reaches, including valid empty results.
  const affectedCards = useMemo(() => {
    const byColumn: Record<string, Array<string>> = {};
    for (const source of filterCoverageSources) {
      const card = lens?.spec.cards.find((card) => card.id === source.cardId);
      const { narrowedBy } = cardFilterReach(
        selections,
        card?.view?.where,
        cardData.find((item) => item.cardId === source.cardId),
      );
      const reachable = source.columns.filter((column) => !(column in selections));
      for (const column of new Set([...reachable, ...narrowedBy])) {
        byColumn[column] = [...(byColumn[column] ?? []), source.title];
      }
    }
    return byColumn;
  }, [filterCoverageSources, lens, selections, cardData]);

  // Whether any filter narrows the dashboard right now, in any of the three
  // ways it can: a dashboard filter value, a cross-filter selection, or a
  // measure range. A card's empty state offers to clear all three together.
  const hasActiveFilters =
    Object.values(effectiveFilterValues).some(isActiveDeclaredValue) ||
    Object.keys(selections).length > 0 ||
    Object.keys(ranges).length > 0;

  const boardPeriodLabel = lensData?.period?.label
    ? `${lensData.period.label.current}${lensData.period.label.comparison ? ` vs ${lensData.period.label.comparison}` : ''}`
    : 'Date range';

  // Whole-board PDF. The board must be settled first: the same states that put the
  // "Updating cards…" veil over the canvas, plus a card refreshing on its own. While the
  // export runs, a backdrop blocks the board, and the export stops if the data or filters
  // change under it, so the header always describes the cards printed beneath it.
  const isBoardBusy =
    isLoadingData || isShowingPreviousData || selectionPending || isFetchingSourceContext || refreshCard.isPending;
  const pdfFilterKey = JSON.stringify({ effectiveFilterValues, selections, ranges });
  const pdfWatchRef = useRef({ busy: isBoardBusy, data: lensData, filterKey: pdfFilterKey, lens });
  const pdfInFlightRef = useRef(false);
  // A custom HTML view prints itself (see customView/customLensPrint.ts): the browser's
  // print dialog, where "Save as PDF" gives the file, laid out by the view's own print CSS.
  const customViewRef = useRef<CustomLensViewHandle>(null);
  const [isPrintingCustomView, setIsPrintingCustomView] = useState(false);
  // A ref, like pdfInFlightRef, so a second activation in the same turn cannot clear the
  // flag while the first print is still being prepared.
  const customPrintInFlightRef = useRef(false);
  const isCustomViewBusy = isBoardBusy;
  const printCustomView = async () => {
    setSettingsMenuAnchor(null);
    if (!customViewRef.current || customPrintInFlightRef.current) {
      return;
    }
    if (isCustomViewBusy) {
      enqueueSnackbar('The view is still updating. Print once it has loaded.', { variant: 'info' });
      return;
    }
    customPrintInFlightRef.current = true;
    setIsPrintingCustomView(true);
    try {
      await customViewRef.current.print();
    } catch (printError) {
      enqueueSnackbar(printError instanceof Error ? printError.message : 'The view could not be printed.', {
        variant: 'error',
      });
    } finally {
      customPrintInFlightRef.current = false;
      setIsPrintingCustomView(false);
    }
  };
  const pdfMountedRef = useRef(true);
  // Update synchronously with the rendered board rather than waiting for an effect.
  pdfWatchRef.current = { busy: isBoardBusy, data: lensData, filterKey: pdfFilterKey, lens };
  useEffect(() => {
    pdfMountedRef.current = true;
    return () => {
      pdfMountedRef.current = false;
    };
  }, []);
  // `target` is set only by the Lens print page (scheduled emails): the PDF goes to it instead of
  // being saved, and every outcome is reported to it rather than shown in a snackbar.
  const exportBoardPdf = async (target?: LensPrintTarget) => {
    setSettingsMenuAnchor(null);
    const root = visualAuditRootRef.current;
    if (!root || !lens || pdfInFlightRef.current || lens.spec.customView) {
      target?.reject(new Error('The board was not ready to export.'));
      return;
    }
    if (pdfWatchRef.current.busy) {
      if (target) {
        target.reject(new Error('The board was still updating.'));
      } else {
        enqueueSnackbar('The board is still updating. Export once the cards have loaded.', { variant: 'info' });
      }
      return;
    }
    const snapshot = pdfWatchRef.current;
    const tableCardIds = canvasCards.filter((card) => card.type === 'table').map((card) => card.id);
    const expandedBefore = expandedRowsCardIds;
    pdfInFlightRef.current = true;
    setIsExportingPdf(true);
    setPdfProgress(null);
    // Table cards are opened for the export so the PDF carries their rows rather than the
    // collapsed strip. Set directly, not through toggleRowsDrawer, so the reader's saved
    // drawer state is untouched, and put back afterwards.
    setExportExpandedRowsCardIds(new Set([...expandedBefore, ...tableCardIds]));
    try {
      // Two frames for React to commit and the grid to lay out, then time for charts to
      // finish resizing.
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      await new Promise((resolve) => setTimeout(resolve, 800));
      let delivered: ArrayBuffer | undefined;
      const result = await exportLensBoardPdf({
        deliver: target
          ? (pdf) => {
              delivered = pdf;
            }
          : undefined,
        generatedAt: target?.generatedAt,
        expectedCardCount: cardGroups.reduce((sum, group) => sum + group.cards.length, 0),
        filters: filterChips.map((chip) => `${chip.label}: ${chip.value}`),
        isStale: () =>
          !pdfMountedRef.current ||
          pdfWatchRef.current.busy ||
          pdfWatchRef.current.data !== snapshot.data ||
          pdfWatchRef.current.lens !== snapshot.lens ||
          pdfWatchRef.current.filterKey !== snapshot.filterKey,
        onProgress: (done, total) => setPdfProgress({ done, total }),
        periodComparison: lensData?.period?.label?.comparison ?? undefined,
        periodCurrent: lensData?.period?.label?.current,
        root,
        title: lens.title,
      });
      const warnings = [
        ...result.missingCharts.map((title) => `Chart not copied: ${title}`),
        ...(result.substitutedCharacters ? ['Some characters printed as "?"'] : []),
      ];
      if (target) {
        if (!delivered) {
          throw new Error('The PDF was not produced.');
        }
        target.resolve({ kind: 'pdf', pdf: delivered, warnings });
      } else if (result.missingCharts.length || result.substitutedCharacters) {
        // The PDF was saved, but say plainly what it could not carry; the PDF says so too.
        enqueueSnackbar(
          [
            result.missingCharts.length
              ? `${result.missingCharts.length === 1 ? '1 chart' : `${result.missingCharts.length} charts`} could not be copied into the PDF (${result.missingCharts.join('; ')}).`
              : '',
            result.substitutedCharacters ? 'Some names print as "?" because the PDF font cannot show them.' : '',
          ]
            .filter(Boolean)
            .join(' '),
          { variant: 'warning' },
        );
      }
    } catch (error) {
      if (target) {
        target.reject(error instanceof Error ? error : new Error('The PDF could not be created.'));
        return;
      }
      const code = error instanceof LensPdfExportError ? error.code : undefined;
      enqueueSnackbar(
        code === 'stale'
          ? 'The board changed while the PDF was being made, so nothing was saved. Try again.'
          : code === 'missing-cards'
            ? 'Some cards could not be placed in the PDF, so nothing was saved. Try again.'
            : code === 'empty'
              ? 'There is nothing on this board to export.'
              : 'The PDF could not be created. Try again, or export cards one at a time.',
        { variant: 'error' },
      );
    } finally {
      pdfInFlightRef.current = false;
      if (pdfMountedRef.current) {
        if (pdfWatchRef.current.lens?.id === snapshot.lens?.id) {
          setExportExpandedRowsCardIds(null);
        }
        setIsExportingPdf(false);
        setPdfProgress(null);
      }
    }
  };
  // Lens print page only (scheduled emails): once the board has its data, print it once and hand
  // the result to the print page. A custom view gives its cleaned HTML (svc-reporting prints it with
  // scripts and network off); a native board gives the same PDF the Export menu saves.
  const printStartedRef = useRef(false);
  const scheduledView = useMemo(
    () => (printTarget ? { includeInternal: printTarget.includeInternal === true } : null),
    [printTarget],
  );
  const latestExportRef = useRef(exportBoardPdf);
  latestExportRef.current = exportBoardPdf;
  useEffect(() => {
    if (!printTarget || printStartedRef.current || !lens || !lensData || isBoardBusy) {
      return;
    }
    printStartedRef.current = true;
    const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
    void (async () => {
      try {
        // Let charts and the custom view finish drawing, then wait out any late refresh.
        await pause(1500);
        for (let waited = 0; pdfWatchRef.current.busy; waited += 250) {
          if (waited > 60_000) {
            throw new Error('The board was still updating after a minute.');
          }
          await pause(250);
        }
        if (!lens.spec.customView) {
          await latestExportRef.current(printTarget);
          return;
        }
        // The view's frame can still be loading on the first try; it answers within a few seconds.
        let lastError: unknown;
        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            if (!customViewRef.current) {
              throw new Error('The custom view did not load.');
            }
            printTarget.resolve({ kind: 'html', html: await customViewRef.current.snapshot(), warnings: [] });
            return;
          } catch (error) {
            lastError = error;
            await pause(2000);
          }
        }
        throw lastError;
      } catch (error) {
        printTarget.reject(error instanceof Error ? error : new Error(String(error)));
      }
    })();
  }, [printTarget, lens, lensData, isBoardBusy]);
  // A Lens or data request that fails leaves nothing to print; say so rather than wait for a timeout.
  useEffect(() => {
    if (!printTarget || printStartedRef.current || !(error || dataError)) {
      return;
    }
    printStartedRef.current = true;
    const failure = (error ?? dataError) as { message?: string } | null;
    printTarget.reject(new Error(`The Lens could not load: ${failure?.message ?? 'unknown error'}`));
  }, [printTarget, error, dataError]);
  const hasConfiguredFilters =
    Object.entries(effectiveFilterValues).some(([filterId, value]) => {
      if (filterId === LENS_PERIOD_FILTER_ID || filterId === LENS_PERIOD_KEY) {
        return false;
      }
      return isActiveDeclaredValue(value);
    }) ||
    Object.keys(selections).length > 0 ||
    Object.keys(ranges).length > 0;

  const clearAllFilters = () => {
    setFilterValues((current) => clearDeclaredFilters(current, lens?.spec.filters ?? []));
    setSelections({});
    setCrossFilterSourceCardId(null);
    setRanges({});
  };

  const wandAsk = useLensWandAsk({
    canvasRef: visualAuditRootRef,
    cards: (lens?.spec.cards ?? []).map((card) => ({ id: card.id, title: card.title })),
    enabled: Boolean(onAskRichard) && !isEditMode && !lens?.spec.customView && (lens?.spec.cards.length ?? 0) > 0,
    onCommit: (turn) => {
      onAskRichard?.(turn.question, true, true, turn.image ? [turn.image] : undefined, [turn.context]);
    },
  });

  if (isLoading) {
    return <LensLoading />;
  }

  if (error || accessError || !lens) {
    // A 403, a 404 and a network failure are three different problems and used to share one
    // message, so a reader refused access went looking for a typo in their link. Retry is offered
    // only where retrying can change the outcome.
    const loadError = describeLensLoadError(error ?? accessError);
    return (
      <Container sx={{ py: 4 }}>
        <Alert
          action={
            <Stack direction="row" spacing={1}>
              {loadError.canRetry && (
                <Button color="inherit" disabled={isFetching} onClick={() => refetch()} size="small">
                  {isFetching ? 'Retrying…' : 'Retry'}
                </Button>
              )}
              <Button color="inherit" component={RouterLink} size="small" to="/le-lens">
                All Lenses
              </Button>
            </Stack>
          }
          severity="error"
        >
          {loadError.message}
        </Alert>
      </Container>
    );
  }

  // Editing has a dedicated workbench. Previously its board controls were appended to
  // the reporting-period line, so date context, selection and automatic layout competed
  // for one visual row. Keep the three jobs legible: Lens settings in the title row,
  // board operations here, and reporting context below.
  const showCardEditingTools = !lens.spec.customView || customViewDataEditingLensId === lens.id;
  const { minimalChrome, showTitle } = lensDisplay(lens.spec, isEditMode);
  const cardEditorOpen = Boolean(panelCard) && panelMode === 'edit' && showCardEditingTools && canAuthor;
  const editToolbar = canAuthor && showCardEditingTools && !isCanvasSelectionMode && (
    <Box
      aria-label="Lens editing tools"
      sx={{
        alignItems: 'center',
        borderBottom: 1,
        borderColor: 'divider',
        display: 'flex',
        mb: 1.5,
        minHeight: 44,
        px: { sm: 1.5, xs: 1.25 },
        py: 0.5,
      }}
    >
      <Stack
        alignItems={{ lg: 'center', xs: 'flex-start' }}
        direction={{ lg: 'row', xs: 'column' }}
        justifyContent="space-between"
        spacing={1}
        sx={{ width: '100%' }}
      >
        <Typography color="text.secondary" variant="body2">
          {formatCardDatasetSummary(lens.spec.cards.length, (lens.spec.datasets ?? []).length)}
        </Typography>

        <Stack direction="row" flexWrap="wrap" spacing={0.5} useFlexGap>
          <Button
            disabled={updateLens.isPending || isFinishingEditing}
            onClick={() => beginCardPlan('ask')}
            size="small"
            startIcon={<AutoAwesomeOutlinedIcon fontSize="small" />}
            variant="outlined"
          >
            Add card
          </Button>
          {layoutCards.length > 0 && !previewGroupedToolbar && (
            <Button
              disabled={updateLens.isPending}
              onClick={() => setIsLayoutWorkspaceOpen(true)}
              size="small"
              sx={{ whiteSpace: 'nowrap' }}
              variant="outlined"
            >
              Layout
            </Button>
          )}
          {layoutCards.length > 0 && (
            <Button
              disabled={updateLens.isPending}
              onClick={() => {
                setIsCanvasSelectionMode(true);
                setSelectedCanvasCardIds([]);
              }}
              size="small"
              startIcon={<SelectAllIcon fontSize="small" />}
              sx={{ whiteSpace: 'nowrap' }}
              variant="outlined"
            >
              Select cards
            </Button>
          )}
          {layoutCards.length > 0 && !previewGroupedToolbar && (
            <Button
              disabled={updateLens.isPending}
              onClick={handleSmartArrange}
              size="small"
              startIcon={<AutoFixHighIcon fontSize="small" />}
              sx={{ whiteSpace: 'nowrap' }}
              variant="outlined"
            >
              Smart arrange
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );

  const selectionToolbar = canAuthor && showCardEditingTools && isCanvasSelectionMode && (
    <Box
      aria-label="Selected cards actions"
      sx={{
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.14 : 0.045),
        border: 1,
        borderColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.38 : 0.18),
        borderRadius: `${LENS_LAYOUT.cardRadius}px`,
        mb: 1.5,
        px: { sm: 1.5, xs: 1.25 },
        py: 1.25,
      }}
    >
      <Stack
        alignItems={{ lg: 'center', xs: 'flex-start' }}
        direction={{ lg: 'row', xs: 'column' }}
        justifyContent="space-between"
        spacing={1.25}
      >
        <Stack alignItems="center" direction="row" spacing={1}>
          <Box
            aria-hidden
            sx={{
              alignItems: 'center',
              backgroundColor: theme.palette.primary.main,
              borderRadius: `${LENS_LAYOUT.cardRadius}px`,
              color: theme.palette.primary.contrastText,
              display: 'flex',
              height: 30,
              justifyContent: 'center',
              width: 30,
            }}
          >
            <SelectAllIcon fontSize="small" />
          </Box>
          <Box>
            <Typography sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }} variant="body2">
              {selectedCanvasCardIds.length} {selectedCanvasCardIds.length === 1 ? 'card' : 'cards'} selected
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" flexWrap="wrap" spacing={0.75} useFlexGap>
          <Button
            disabled={updateLens.isPending}
            onClick={() => {
              setIsCanvasSelectionMode(false);
              setSelectedCanvasCardIds([]);
            }}
            size="small"
            variant="outlined"
          >
            Done selecting
          </Button>
          <Button
            disabled={selectedCanvasCardIds.length < 2 || updateLens.isPending}
            onClick={handleArrangeSelectedEvenly}
            size="small"
          >
            Arrange evenly
          </Button>
          <Button
            aria-haspopup="menu"
            disabled={selectedCanvasCardIds.length === 0 || updateLens.isPending}
            endIcon={<ExpandMoreIcon />}
            onClick={(event) => setBulkMoveAnchor(event.currentTarget)}
            size="small"
            startIcon={<DriveFileMoveOutlinedIcon fontSize="small" />}
          >
            Move to section
          </Button>
          <Menu anchorEl={bulkMoveAnchor} onClose={() => setBulkMoveAnchor(null)} open={Boolean(bulkMoveAnchor)}>
            <ListSubheader sx={{ lineHeight: 2.2 }}>Move selected cards</ListSubheader>
            <MenuItem onClick={() => handleMoveSelectedToSection()}>Move out of sections</MenuItem>
            {(lens.spec.sections ?? []).map((section) => (
              <MenuItem key={section.id} onClick={() => handleMoveSelectedToSection(section.id)}>
                Move to {section.title || 'Untitled section'}
              </MenuItem>
            ))}
          </Menu>
          {selectedCanvasCardIds.length > 0 && (
            <Button disabled={updateLens.isPending} onClick={() => setSelectedCanvasCardIds([])} size="small">
              Clear selection
            </Button>
          )}
        </Stack>
      </Stack>
      {selectedCanvasCardIds.length > 0 && (
        <Stack
          spacing={1}
          sx={{ borderTop: 1, borderColor: alpha(theme.palette.primary.main, 0.2), mt: 1.25, pt: 1.25 }}
        >
          <Typography fontWeight={700} variant="caption">
            Apply to selected cards
          </Typography>
          <Stack direction={{ md: 'row', xs: 'column' }} flexWrap="wrap" spacing={1.25} useFlexGap>
            <Box>
              <Typography color="text.secondary" sx={{ display: 'block', mb: 0.5 }} variant="caption">
                Title alignment
              </Typography>
              <ToggleButtonGroup
                aria-label="Apply title alignment to selected cards"
                disabled={updateLens.isPending}
                exclusive
                onChange={(_event, value: LensCardAlignment | null) => {
                  if (value) {
                    handleApplyBulkPresentation({ titleAlignment: value });
                  }
                }}
                size="small"
                value={null}
              >
                <ToggleButton aria-label="Align selected titles left" value="left">
                  <FormatAlignLeftIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton aria-label="Centre selected titles" value="center">
                  <FormatAlignCenterIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton aria-label="Align selected titles right" value="right">
                  <FormatAlignRightIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box>
              <Typography color="text.secondary" sx={{ display: 'block', mb: 0.5 }} variant="caption">
                Title size
              </Typography>
              <ToggleButtonGroup
                aria-label="Apply title size to selected cards"
                disabled={updateLens.isPending}
                exclusive
                onChange={(_event, value: 'default' | LensCardTitleSize | null) => {
                  if (value) {
                    handleApplyBulkPresentation({ titleSize: value });
                  }
                }}
                size="small"
                value={null}
              >
                <ToggleButton value="default">Default</ToggleButton>
                <ToggleButton value="small">Small</ToggleButton>
                <ToggleButton value="medium">Medium</ToggleButton>
                <ToggleButton value="large">Large</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Box>
              <Typography color="text.secondary" sx={{ display: 'block', mb: 0.5 }} variant="caption">
                Chart text
              </Typography>
              <ToggleButtonGroup
                aria-label="Apply chart text size to selected cards"
                disabled={updateLens.isPending}
                exclusive
                onChange={(_event, value: 'default' | LensTextScale | null) => {
                  if (value) {
                    handleApplyBulkPresentation({ textScale: value });
                  }
                }}
                size="small"
                value={null}
              >
                <ToggleButton value="small">Small</ToggleButton>
                <ToggleButton value="default">Default</ToggleButton>
                <ToggleButton value="large">Large</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Stack>
        </Stack>
      )}
    </Box>
  );

  const lensSettingsContent = (
    <Box sx={{ maxWidth: 480, mx: 'auto', px: 3, py: 3, width: '100%' }}>
      {lens.spec.customView && !showCardEditingTools ? (
        <Typography color="text.secondary" sx={{ fontSize: 13, lineHeight: 1.5, mb: 2 }}>
          Manage the data and controls behind this HTML page. Use Edit custom HTML view below to change its
          presentation.
        </Typography>
      ) : null}

      <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }} variant="subtitle2">
        Identity
      </Typography>
      <List disablePadding sx={{ mx: -1 }}>
        <LensSettingsItem
          description="Change the name and description shown in the Lens library."
          disabled={panelHasUnsavedChanges || updateLens.isPending}
          icon={<DriveFileRenameOutlineIcon fontSize="small" />}
          label="Edit Lens details"
          onClick={openLensDetailsEditor}
        />
      </List>

      <Divider sx={{ my: 2 }} />
      <LensDisplaySettings
        key={lens.id}
        spec={lens.spec}
        disabled={!canAuthor || panelHasUnsavedChanges || updateLens.isPending || isFinishingEditing}
        onChange={async (preference, checked) => {
          if (!canAuthor || panelHasUnsavedChanges || updateLens.isPending) return;
          await updateLens.mutateAsync({
            id: lens.id,
            title: lens.title,
            description: lens.description,
            spec: { ...lens.spec, display: { ...lens.spec.display, [preference]: checked } },
            version: lens.version,
            refetchData: false,
            successMessage: 'Lens display updated',
          });
        }}
      />

      <Divider sx={{ my: 2 }} />
      <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }} variant="subtitle2">
        Data &amp; filters
      </Typography>
      <List disablePadding sx={{ mx: -1 }}>
        {(lens.spec.datasets ?? []).length > 0 && (
          <LensSettingsItem
            description="Manage the sources behind the cards."
            icon={<DatasetOutlinedIcon fontSize="small" />}
            label="Datasets"
            onClick={() => setIsDatasetsOpen(true)}
          />
        )}
        <LensSettingsItem
          description="Edit controls that change card queries."
          icon={<FilterAltOutlinedIcon fontSize="small" />}
          label="Lens filters"
          onClick={() => setIsFiltersOpen(true)}
        />
        <LensSettingsItem
          description="Add a control for readers to narrow the data."
          icon={<AddIcon fontSize="small" />}
          label="Add Lens filter"
          onClick={() => setIsAddFilterOpen(true)}
        />
        {showCardEditingTools && (
          <LensSettingsItem
            description="Use a chart selection to filter other cards."
            icon={<HubOutlinedIcon fontSize="small" />}
            label="Cross-filters"
            onClick={() => setIsCrossFiltersOpen(true)}
          />
        )}
        <LensSettingsItem
          description="Choose which fields readers can filter by."
          icon={<TuneIcon fontSize="small" />}
          label="Filter fields"
          onClick={(event) => {
            setFiltersAnchor(event.currentTarget);
            setFilterColumnsAnchor(event.currentTarget);
          }}
        />
      </List>

      {previewGroupedToolbar && showCardEditingTools && layoutCards.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }} variant="subtitle2">
            Appearance
          </Typography>
          <List disablePadding sx={{ mx: -1 }}>
            <LensSettingsItem
              description="Manage sections, card order and sizes, or preview an automatic arrangement."
              disabled={updateLens.isPending}
              icon={<TuneIcon fontSize="small" />}
              label="Organise cards"
              onClick={() => setIsLayoutWorkspaceOpen(true)}
            />
          </List>
        </>
      )}

      <Divider sx={{ my: 2 }} />
      <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }} variant="subtitle2">
        Advanced
      </Typography>
      <List disablePadding sx={{ mx: -1 }}>
        <LensSettingsItem
          description="Create a bespoke layout for this Lens."
          icon={<CodeOutlinedIcon fontSize="small" />}
          label={lens.spec.customView ? 'Edit custom HTML view' : 'Create custom HTML view'}
          onClick={openCustomViewEditor}
        />
      </List>
    </Box>
  );

  const workspaceLayout = lensWorkspaceWithEditor(sidebarVisible, editorPanelWidth);

  const editBar = canAuthor && (
    <Box sx={{ bgcolor: tokens.band, flexShrink: 0, position: 'sticky', top: 0, zIndex: theme.zIndex.appBar }}>
      <Box sx={{ width: workspaceLayout.width }}>
        <LensEditBar
          hasUnsavedChanges={pendingLensCardIds.length > 0 || Boolean(newCardDraft)}
          hasConflict={conflictingLensCardIds.length > 0}
          isSaving={updateLens.isPending || isFinishingEditing}
          onDiscard={() => requestSessionDiscard()}
          onDone={() => void finishEditing()}
        />
      </Box>
    </Box>
  );

  const board = (
    <Container
      sx={{
        ml: workspaceLayout.marginLeft,
        mr: workspaceLayout.marginRight,
        minWidth: 0,
        py: { sm: 2.5, xs: 2 },
        // The editor owns a desktop lane. The board therefore moves into the
        // remaining workspace instead of disappearing underneath the Drawer.
        transition: isResizingEditorPanel ? 'none' : 'width 160ms ease, margin 160ms ease',
        width: workspaceLayout.width,
      }}
    >
      <AskRichardFavicon />
      <LensLiveRegion />
      <Helmet>
        <title>{`${lens.title} · Lens`}</title>
      </Helmet>

      {/* The board header is only identity and everyday actions. Context and health
          have their own drawer so neither competes with the canvas. */}
      <Box
        data-lens-chrome={minimalChrome ? 'minimal' : 'full'}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: minimalChrome ? 1 : 1.25, pb: minimalChrome ? 0.5 : 1.25 }}
      >
        <Box
          sx={{
            alignItems: 'center',
            columnGap: 2,
            display: 'grid',
            gridTemplateAreas: minimalChrome
              ? '"identity actions"'
              : { md: '"identity actions"', xs: '"identity" "actions"' },
            gridTemplateColumns: minimalChrome
              ? 'minmax(0, 1fr) auto'
              : { md: 'minmax(0, 1fr) auto', xs: 'minmax(0, 1fr)' },
            rowGap: 1,
          }}
        >
          <Box sx={{ gridArea: 'identity', minWidth: 0 }}>
            <Stack alignItems="center" direction="row" gap={1} sx={{ minWidth: 0 }}>
              {onNavigateToList ? (
                <Tooltip title="Back">
                  <IconButton
                    aria-label="Back"
                    disabled={updateLens.isPending || isFinishingEditing}
                    onClick={() => {
                      if (pendingLensCardIds.length > 0 || newCardDraft || conflictingLensCardIds.length > 0) {
                        requestSessionDiscard(onNavigateToList);
                      } else {
                        onNavigateToList();
                      }
                    }}
                    size="small"
                  >
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Back">
                  <IconButton
                    aria-label="Back"
                    disabled={updateLens.isPending || isFinishingEditing}
                    onClick={() => {
                      const navigateToList = () => navigate('/le-lens');
                      if (pendingLensCardIds.length > 0 || newCardDraft || conflictingLensCardIds.length > 0) {
                        requestSessionDiscard(navigateToList);
                      } else {
                        navigateToList();
                      }
                    }}
                    size="small"
                  >
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {!minimalChrome && showTitle && <LensMark size={22} />}
              <Typography
                component="h1"
                sx={
                  !showTitle
                    ? visuallyHidden
                    : {
                        // A one-off board is not the section header for the whole product: 28px, not
                        // the 32-44px hero scale the Lenses list page's own title uses.
                        fontFamily: LENS_HERO_FONT_FAMILY,
                        fontSize: { lg: '1.75rem', xs: '1.5rem' },
                        fontWeight: 400,
                        lineHeight: 1.2,
                        minWidth: 0,
                        overflow: 'hidden',
                        overflowWrap: 'anywhere',
                        flex: '1 1 280px',
                      }
                }
              >
                {lens.title}
                {showTitle && !hasChangedSavedInsightScope && (
                  <LensCertificationTick certification={data.certification} />
                )}
              </Typography>
              {!showTitle && !hasChangedSavedInsightScope && (
                <LensCertificationTick certification={data.certification} size={16} />
              )}
              {minimalChrome && hasConfiguredFilters && (
                <Chip
                  aria-label="Filters are narrowing this view. Clear filters"
                  label="Filtered · Clear"
                  onClick={clearAllFilters}
                  size="small"
                  variant="outlined"
                />
              )}
              {minimalChrome && restoreFiltersAction}
            </Stack>
          </Box>
          <Stack
            alignItems="center"
            direction="row"
            flexWrap="wrap"
            justifyContent="flex-end"
            gap={previewGroupedToolbar ? 1 : 0.75}
            sx={{ gridArea: 'actions', maxWidth: '100%' }}
          >
            <Box
              sx={
                previewGroupedToolbar
                  ? {
                      display: 'contents',
                      '& [data-lens-wand-ask]': { border: 0, height: 36, width: 36 },
                      '& [data-lens-wand-ask] .MuiSvgIcon-root': { fontSize: 20 },
                    }
                  : { display: 'contents' }
              }
            >
              {wandAsk.button}
            </Box>
            <Stack
              aria-label={previewGroupedToolbar && !isEditMode ? 'askRichard tools' : undefined}
              direction="row"
              role={previewGroupedToolbar ? 'group' : undefined}
              sx={
                previewGroupedToolbar && !isEditMode
                  ? {
                      alignItems: 'center',
                      border: 1,
                      borderColor: alpha(theme.palette.primary.main, 0.4),
                      borderRadius: 1,
                      overflow: 'hidden',
                      '& .MuiButtonBase-root': { border: 0, borderRadius: 0, minHeight: 34 },
                      '& .MuiButton-root': { px: 1.5 },
                    }
                  : { display: 'contents' }
              }
            >
              {!minimalChrome && (!previewFiltersBelow || lens.spec.cards.length === 0) && (
                <Tooltip title={hasConfiguredFilters ? 'Filters (active)' : 'Filters'}>
                  <IconButton
                    aria-label="Filters"
                    aria-haspopup="dialog"
                    aria-expanded={Boolean(filtersAnchor)}
                    onClick={(event) => setFiltersAnchor(event.currentTarget)}
                    size="small"
                  >
                    <Badge color="primary" variant="dot" invisible={!hasConfiguredFilters}>
                      <FilterAltOutlinedIcon fontSize="small" />
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}
              {!isEditMode && (onOpenAskRichardPanel || renderAskPane) && (
                <Button
                  aria-label="Open askRichard panel"
                  onClick={() => openAskPaneFromToolbar()}
                  size="small"
                  startIcon={<AutoAwesomeOutlinedIcon fontSize="small" />}
                  sx={{ whiteSpace: 'nowrap' }}
                  variant={data.access.canEdit ? 'outlined' : 'contained'}
                >
                  <AskRichardWordmark inverted={theme.palette.mode === 'dark'} size={14} />
                </Button>
              )}
            </Stack>
            <Stack
              aria-label={previewGroupedToolbar ? 'Lens controls' : undefined}
              direction="row"
              role={previewGroupedToolbar ? 'group' : undefined}
              sx={
                previewGroupedToolbar
                  ? {
                      alignItems: 'center',
                      border: 1,
                      borderColor: alpha(theme.palette.primary.main, 0.4),
                      borderRadius: 1,
                      overflow: 'hidden',
                      '& .MuiIconButton-root': { borderRadius: 0, minHeight: 34, width: 36 },
                    }
                  : { display: 'contents' }
              }
            >
              {data.access.canEdit && (
                <Button
                  aria-label={isEditMode ? 'Lens settings' : 'Edit Lens'}
                  disabled={updateLens.isPending || isFinishingEditing}
                  onClick={startEditing}
                  size="small"
                  startIcon={<TuneIcon fontSize="small" />}
                  sx={{ borderRadius: 0, minHeight: 34, whiteSpace: 'nowrap', px: 1.5 }}
                  variant={isEditMode ? 'text' : 'contained'}
                >
                  {isEditMode ? 'Lens settings' : 'Edit Lens'}
                </Button>
              )}
              {lens && !previewGroupedToolbar && (
                <Tooltip title={pins.isPinned(lens.id) ? 'Pinned to rail. Click to unpin' : 'Pin to rail'}>
                  <IconButton
                    aria-label={pins.isPinned(lens.id) ? 'Pinned' : 'Pin'}
                    aria-pressed={pins.isPinned(lens.id)}
                    onClick={() => pins.toggle(lens.id)}
                    size="small"
                  >
                    {pins.isPinned(lens.id) ? (
                      <PushPinIcon fontSize="small" />
                    ) : (
                      <PushPinOutlinedIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="More Lens actions">
                <IconButton
                  aria-label="More Lens actions"
                  aria-haspopup="menu"
                  aria-expanded={Boolean(settingsMenuAnchor)}
                  onClick={(event) => setSettingsMenuAnchor(event.currentTarget)}
                  ref={lensActionsButtonRef}
                  sx={
                    previewGroupedToolbar && data.access.canEdit && renderAskPane
                      ? { borderLeft: '1px solid', borderColor: 'divider' }
                      : undefined
                  }
                  size="small"
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
            <Menu
              anchorEl={settingsMenuAnchor}
              onClose={() => setSettingsMenuAnchor(null)}
              open={Boolean(settingsMenuAnchor)}
              slotProps={{
                paper: {
                  sx: {
                    maxWidth: 'calc(100vw - 24px)',
                    p: 0.5,
                    width: 272,
                    '& .MuiListItemIcon-root': { color: 'text.secondary', minWidth: 34 },
                    '& .MuiMenuItem-root': { borderRadius: 1, minHeight: 42 },
                  },
                },
              }}
            >
              {previewGroupedToolbar && (
                <MenuItem
                  onClick={() => {
                    pins.toggle(lens.id);
                    setSettingsMenuAnchor(null);
                  }}
                >
                  <ListItemIcon>
                    {pins.isPinned(lens.id) ? (
                      <PushPinIcon fontSize="small" />
                    ) : (
                      <PushPinOutlinedIcon fontSize="small" />
                    )}
                  </ListItemIcon>
                  {pins.isPinned(lens.id) ? 'Unpin from rail' : 'Pin to rail'}
                </MenuItem>
              )}
              {minimalChrome && (
                <MenuItem
                  onClick={() => {
                    setSettingsMenuAnchor(null);
                    setFiltersAnchor(lensActionsButtonRef.current);
                  }}
                >
                  <ListItemIcon>
                    <FilterAltOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  Filters…
                </MenuItem>
              )}
              {data.access.canEdit && lens.spec.customView && (
                <MenuItem onClick={openEditPane}>
                  <ListItemIcon>
                    <TuneIcon fontSize="small" />
                  </ListItemIcon>
                  Edit HTML
                </MenuItem>
              )}
              {data.access.canEdit && lens.spec.customView && !showCardEditingTools && (
                <MenuItem onClick={openDataCardEditor}>
                  <ListItemIcon>
                    <TuneIcon fontSize="small" />
                  </ListItemIcon>
                  Edit data cards
                </MenuItem>
              )}
              {canAuthor && showCardEditingTools && (
                <MenuItem onClick={() => void addBlankCard()}>
                  <ListItemIcon>
                    <AddIcon fontSize="small" />
                  </ListItemIcon>
                  Add blank card
                </MenuItem>
              )}
              {data.access.canRead && (
                <MenuItem
                  disabled={panelHasUnsavedChanges}
                  onClick={() => {
                    setSettingsMenuAnchor(null);
                    setSharingOpen(true);
                  }}
                >
                  <ListItemIcon>
                    <ShareOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  Share
                </MenuItem>
              )}
              <LensCertificationMenuItems
                parentMenuOpen={Boolean(settingsMenuAnchor)}
                actions={lensCertificationActions(
                  data.access,
                  data.certification,
                  data.sharing?.audience === 'company',
                )}
                lensId={lens.id}
                meaning={data.certification?.meaning}
                request={data.access.canRequestCertification ? data.certification?.request : undefined}
                onDone={() => setSettingsMenuAnchor(null)}
              />
              <MenuItem
                onClick={() => {
                  setSettingsMenuAnchor(null);
                  setSaveAsOpen(true);
                }}
              >
                <ListItemIcon>
                  <ContentCopyOutlinedIcon fontSize="small" />
                </ListItemIcon>
                Duplicate Lens
              </MenuItem>
              {lens.spec.customView ? (
                <MenuItem
                  disabled={isPrintingCustomView || isCustomViewBusy || !lens.spec.customView.html.trim()}
                  onClick={() => void printCustomView()}
                >
                  <ListItemIcon>
                    <PictureAsPdfOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  {isPrintingCustomView ? 'Preparing print…' : 'Print / save as PDF'}
                </MenuItem>
              ) : (
                <MenuItem disabled={isExportingPdf || isBoardBusy || isEditMode} onClick={() => void exportBoardPdf()}>
                  <ListItemIcon>
                    <PictureAsPdfOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  {isExportingPdf ? 'Exporting PDF…' : 'Export as PDF'}
                </MenuItem>
              )}
              <MenuItem
                onClick={() => {
                  setSettingsMenuAnchor(null);
                  setSchedulesOpen(true);
                }}
              >
                <ListItemIcon>
                  <ScheduleSendOutlinedIcon fontSize="small" />
                </ListItemIcon>
                Schedule email…
              </MenuItem>
              <Divider sx={{ my: 0.5 }} />
              <MenuItem
                onClick={() => {
                  setSettingsMenuAnchor(null);
                  setDetailsOpen(true);
                }}
              >
                <ListItemIcon>
                  <InfoOutlinedIcon fontSize="small" />
                </ListItemIcon>
                Lens details
              </MenuItem>
              <MenuItem
                disabled={panelHasUnsavedChanges}
                onClick={() => {
                  setSettingsMenuAnchor(null);
                  setHistoryOpen(true);
                }}
              >
                <ListItemIcon>
                  <HistoryOutlinedIcon fontSize="small" />
                </ListItemIcon>
                Version history
              </MenuItem>
              <MenuItem
                disabled={isLoadingData}
                onClick={() => {
                  setSettingsMenuAnchor(null);
                  void refreshData();
                }}
              >
                <ListItemIcon>
                  <RefreshIcon fontSize="small" />
                </ListItemIcon>
                Refresh data
              </MenuItem>
            </Menu>
            <LensCollaborationDialog
              lens={lens}
              access={data.access}
              open={historyOpen}
              onClose={() => setHistoryOpen(false)}
            />
            {schedulesOpen && (
              <LensSchedulesDialog
                filterOptions={lensData?.filterOptions ?? {}}
                lens={lens}
                open={schedulesOpen}
                screenValues={filterValues}
                userEmail={user?.email}
                onClose={() => setSchedulesOpen(false)}
              />
            )}
          </Stack>
        </Box>
        {hasRemoteChanges && (
          <Alert severity="info" sx={{ mt: 1 }}>
            This Lens changed elsewhere. Finish or discard your edits to load the new version.
          </Alert>
        )}
      </Box>

      <Drawer
        anchor="right"
        onClose={() => setDetailsOpen(false)}
        open={detailsOpen}
        PaperProps={{ sx: { maxWidth: '100vw', overflowX: 'hidden', width: { sm: 440, xs: '100%' } } }}
      >
        <Box sx={{ px: { sm: 3, xs: 2 }, py: 3 }}>
          <Stack alignItems="flex-start" direction="row" justifyContent="space-between" spacing={2}>
            <Box sx={{ minWidth: 0 }}>
              <Typography color="text.secondary" sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em' }}>
                LENS DETAILS
              </Typography>
              <Typography
                component="h2"
                sx={{
                  fontFamily: LENS_HERO_FONT_FAMILY,
                  fontSize: 26,
                  lineHeight: 1.2,
                  mt: 1,
                  overflowWrap: 'anywhere',
                }}
              >
                {lens.title}
              </Typography>
            </Box>
            <IconButton aria-label="Close Lens details" onClick={() => setDetailsOpen(false)} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          {lens.description && (
            <Typography color="text.secondary" sx={{ mt: 2 }} variant="body2">
              {lens.description}
            </Typography>
          )}

          {data.access.canEdit && (
            <Button
              disabled={panelHasUnsavedChanges || updateLens.isPending}
              onClick={openLensDetailsEditor}
              size="small"
              startIcon={<DriveFileRenameOutlineIcon fontSize="small" />}
              sx={{ mt: 2 }}
              variant="text"
            >
              Edit Lens details
            </Button>
          )}

          <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 3, pt: 2.5 }}>
            <Typography sx={{ fontWeight: 700, mb: 1 }} variant="subtitle2">
              Scope
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {formatScopeLine(lens.spec.scope, scopeGeographyFilterValue, scopeDatasetNameFallback, {
                includeDataset: false,
              }) ?? 'No scope recorded for this Lens.'}
            </Typography>
            {lensAnchorValue && (
              <Box sx={{ mt: 2 }}>
                <Typography color="text.secondary" variant="caption">
                  Data source
                </Typography>
                {onOpenCatalogueDataset ? (
                  <Button
                    onClick={() => {
                      setDetailsOpen(false);
                      onOpenCatalogueDataset(lensAnchorValue.catalogueId);
                    }}
                    size="small"
                    sx={{ display: 'block', mt: 0.5, px: 0, textAlign: 'left' }}
                    variant="text"
                  >
                    {lensAnchorName}
                  </Button>
                ) : (
                  <Typography variant="body2">{lensAnchorName}</Typography>
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 3, pt: 2.5 }}>
            <Typography sx={{ fontWeight: 700, mb: 1.5 }} variant="subtitle2">
              Presentation check
            </Typography>
            <Stack alignItems="center" direction="row" spacing={1} role="status">
              {isLoadingData || !visualAuditComplete || refreshCard.isPending ? (
                <CircularProgress size={18} />
              ) : visualAuditIssues.length > 0 ? (
                <WarningAmberOutlinedIcon color="warning" sx={{ fontSize: 20 }} />
              ) : (
                <CheckCircleOutlineIcon color="success" sx={{ fontSize: 20 }} />
              )}
              <Typography variant="body2">
                {isLoadingData || !visualAuditComplete || refreshCard.isPending
                  ? 'Checking the cards…'
                  : visualAuditIssues.length > 0
                    ? `${visualAuditIssues.length} ${visualAuditIssues.length === 1 ? 'issue needs' : 'issues need'} attention`
                    : 'No presentation issues found'}
              </Typography>
            </Stack>
            {visualAuditComplete && visualAuditIssues.length > 0 && (
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                {visualAuditIssues.map((issue) => (
                  <Box
                    key={`${issue.cardId}:${issue.kind}`}
                    sx={{ borderLeft: 2, borderColor: 'warning.main', pl: 1.5 }}
                  >
                    <Typography sx={{ fontWeight: 600 }} variant="body2">
                      {issue.cardTitle}
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                      {issue.message}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 3, pt: 2.5 }}>
            <Typography sx={{ fontWeight: 700, mb: 1 }} variant="subtitle2">
              Lens information
            </Typography>
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Typography color="text.secondary" variant="body2">
                  Cards
                </Typography>
                <Typography variant="body2">{lens.spec.cards.length}</Typography>
              </Stack>
              {canvasFreshness && (
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography color="text.secondary" variant="body2">
                    Data freshness
                  </Typography>
                  <Typography sx={{ textAlign: 'right' }} variant="body2">
                    {canvasFreshness}
                  </Typography>
                </Stack>
              )}
              {lens.spec.customView && (
                <Stack direction="row" justifyContent="space-between" spacing={2}>
                  <Typography color="text.secondary" variant="body2">
                    View
                  </Typography>
                  <Typography variant="body2">Custom HTML</Typography>
                </Stack>
              )}
            </Stack>
          </Box>
        </Box>
      </Drawer>

      {isCanvasSelectionMode ? selectionToolbar : editToolbar}

      {lens.spec.cards.length > 0 && !minimalChrome && (
        <Stack
          alignItems={{ sm: 'center', xs: 'flex-start' }}
          aria-label={
            datedDatasets.length > 0
              ? `Reporting period: ${boardPeriodLabel}. ${resolvedPeriodConvention(calendar)}.`
              : undefined
          }
          direction={{ sm: 'row', xs: 'column' }}
          spacing={{ sm: 1.5, xs: 0.5 }}
          sx={{ mb: 1.5, minHeight: 32, flexWrap: 'wrap' }}
          useFlexGap
        >
          {datedDatasets.length > 0 && (
            <Tooltip title={resolvedPeriodConvention(calendar)}>
              <Chip
                aria-label="Period"
                label={boardPeriodLabel}
                onClick={(event) => setFiltersAnchor(event.currentTarget)}
                size="small"
                sx={{
                  fontVariantNumeric: 'tabular-nums',
                  fontWeight: 600,
                  flexShrink: 0,
                  maxWidth: '100%',
                }}
                variant="outlined"
              />
            </Tooltip>
          )}
          {/* What is applied, where it can be undone. A filter at rest reads
              "All", which is the state most boards are in and worth no space. */}
          {filterChips.map((chip) => (
            <Chip
              key={`${chip.kind}:${chip.id}`}
              label={`${chip.label}: ${chip.value}`}
              onClick={(event) => setFiltersAnchor(event.currentTarget)}
              onDelete={() => clearFilterChip(chip)}
              size="small"
              sx={{ maxWidth: 260 }}
              variant="outlined"
            />
          ))}
          {restoreFiltersAction}
          {previewFiltersBelow && (
            <Tooltip title={hasConfiguredFilters ? 'Filters (active)' : 'Filters'}>
              <IconButton
                aria-label="Filters"
                aria-haspopup="dialog"
                aria-expanded={Boolean(filtersAnchor)}
                onClick={(event) => setFiltersAnchor(event.currentTarget)}
                size="small"
                sx={{ alignSelf: 'center', flexShrink: 0, ml: 'auto' }}
              >
                <Badge color="primary" variant="dot" invisible={!hasConfiguredFilters}>
                  <FilterAltOutlinedIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}

      {lens.spec.cards.length === 0 && (
        <Card variant="outlined">
          <CardContent>
            <Stack alignItems="center" spacing={1.5} sx={{ py: 5 }}>
              <Typography variant="h6">Create your first card</Typography>
              <Typography color="text.secondary" variant="body2">
                Choose fields from your dataset, or describe what you want to see.
              </Typography>
              {data.access.canEdit && (
                <Button onClick={() => beginCardPlan('edit')} variant="contained">
                  Build manually
                </Button>
              )}
              <Typography color="text.secondary" variant="body2">
                askRichard what you want to know, refine the answer together, then add the cards to this Lens.
              </Typography>
              {data.access.canEdit && (
                <Button
                  onClick={onOpenAskRichardPanel ?? (() => beginCardPlan('ask'))}
                  sx={accentButtonSx(theme)}
                  variant="contained"
                >
                  askRichard
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* A board fetch that failed says so here, once, in the reader's own terms. Two different
          situations reach this: nothing arrived at all, and something arrived earlier and is
          still on screen while the newest request failed. The second is the dangerous one,
          because the header already reads back the filter the reader just chose while the
          cards below still answer the previous one, so it names that explicitly. */}
      {dataError && (
        <Alert
          action={
            <Button color="inherit" disabled={isLoadingData} onClick={() => void refetchData()} size="small">
              {isLoadingData ? 'Retrying…' : 'Retry'}
            </Button>
          }
          severity="warning"
          sx={{ mb: 1.5 }}
        >
          {hasPreviousData && cardData.length > 0
            ? 'These numbers are from before your last change. The update did not finish, so the cards below do not match the period and filters shown above.'
            : 'This Lens’s numbers could not be loaded. The cards and their layout are unchanged.'}
        </Alert>
      )}

      {/* The shelf opens only when someone wants to change the view. Collapse preserves the
          mounted controls, so an in-progress date draft survives a close and reopen. */}
      <Drawer
        anchor="right"
        ModalProps={{ keepMounted: true }}
        onClose={() => setFiltersAnchor(null)}
        open={Boolean(filtersAnchor)}
        slotProps={{ transition: { onExited: handleFiltersClosed } }}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 440 }, maxWidth: '100vw', overflowX: 'hidden' },
        }}
      >
        <Stack sx={{ height: '100%', minHeight: 0 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Box>
              <Typography variant="h6">Filters</Typography>
              <Typography color="text.secondary" variant="body2">
                Change this view.
              </Typography>
            </Box>
            <IconButton aria-label="Close filters" onClick={() => setFiltersAnchor(null)} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 3, py: 2.5 }}>
            <DatasetFilterBar
              calendar={calendar}
              onAskRichard={openAskPaneFromFilters}
              savedFilters={
                (lens.spec.filters?.length ?? 0) > 0 && (
                  <LensFilters
                    canEdit={canAuthor}
                    filters={lens.spec.filters}
                    onChange={setFilterValues}
                    onEdit={setEditingFilter}
                    optionValues={lensData?.filterOptions}
                    values={effectiveFilterValues}
                  />
                )
              }
              lensId={lens.id}
              queryFilters={baseFilterValues}
              declaredFilters={lens.spec.filters ?? []}
              declaredFilterValues={filterValues}
              declaredFilterOptions={lensData?.filterOptions ?? {}}
              onChangeDeclaredFilters={setFilterValues}
              affectedCards={affectedCards}
              coverageCardCount={filterCoverageSources.length}
              canEdit={canAuthor}
              boardPeriod={boardPeriodOf(lens.spec)}
              period={viewerPeriod}
              resolvedPeriod={lensData?.period}
              datasets={lens.spec.datasets ?? []}
              datedDatasets={datedDatasets}
              filterColumns={lens.spec.filterColumns}
              columnPickerAnchor={filterColumnsAnchor}
              onCloseColumnPicker={() => setFilterColumnsAnchor(null)}
              isReloading={isLoadingData}
              onChange={setSelections}
              onChangePeriod={(patch) =>
                setFilterValues((current) => {
                  const next = { ...current };
                  if (patch === null) {
                    delete next[LENS_PERIOD_KEY];
                  } else {
                    next[LENS_PERIOD_KEY] = {
                      ...(current[LENS_PERIOD_KEY] as Partial<LensPeriod> | undefined),
                      ...patch,
                    } as LensPeriod;
                  }
                  return next;
                })
              }
              onSaveBoardPeriod={async (period) => {
                await updateLens.mutateAsync({
                  id: lens.id,
                  title: lens.title,
                  description: lens.description,
                  spec: { ...lens.spec, period, periodDefault: undefined },
                  version: lens.version,
                  successMessage: 'Board date range saved',
                  refetchData: false,
                });
                setFilterValues((current) => {
                  const next = { ...current };
                  delete next[LENS_PERIOD_KEY];
                  return next;
                });
              }}
              onChangeRanges={setRanges}
              onClearAll={() => {
                setSelections({});
                setCrossFilterSourceCardId(null);
                setRanges({});
              }}
              onSetFilterColumns={async (filterColumns) => {
                await updateLens.mutateAsync({
                  id: lens.id,
                  title: lens.title,
                  description: lens.description,
                  spec: { ...lens.spec, filterColumns },
                  version: lens.version,
                  successMessage: 'Filters updated',
                  // Only which controls are shown changed; the rows still stand.
                  refetchData: false,
                });
              }}
              ranges={ranges}
              selections={selections}
              sources={filterSources}
            />
          </Box>
          <Stack
            direction="row"
            justifyContent="flex-end"
            sx={{ px: 3, py: 1.5, borderTop: 1, borderColor: 'divider' }}
          >
            <Button variant="contained" onClick={() => setFiltersAnchor(null)}>
              Done
            </Button>
          </Stack>
        </Stack>
      </Drawer>

      {/* F10: a freshly normalised version 1 lens still has undeclared datasets (the backend's
          normaliser cannot type them, no BigQuery access in that path); this dry-runs and saves
          them in one pass so a Builder never clicks "Declare now" once per card by hand. Renders
          nothing once every dataset is declared, which is already true for all 8 local lenses. */}
      <UpgradeBanner canEdit={data.access.canEdit} lens={lens} />
      {data.view && <LensViewStrip lensId={lens.id} onMakeCopy={() => setSaveAsOpen(true)} view={data.view} />}

      {/* Holds the board still while it is being captured for a PDF (see exportBoardPdf). */}
      <Dialog aria-label="Making PDF" disableEscapeKeyDown open={isExportingPdf}>
        <Stack alignItems="center" role="status" spacing={1.5} sx={{ p: 3 }}>
          <CircularProgress color="inherit" size={28} />
          <Typography fontWeight={600} variant="body2">
            {pdfProgress ? `Making PDF… ${pdfProgress.done} of ${pdfProgress.total} cards` : 'Preparing PDF…'}
          </Typography>
        </Stack>
      </Dialog>

      {/* Escape clears every cross-filter while the canvas holds focus. Dialogs, menus and
          popovers (the settings menu, every settings dialog, the card editor, a per-card
          overflow menu) all render outside this Stack in the tree, or are MUI Modal-based and
          already stop Escape propagating once they own it, so this can never steal the key
          from one of them. */}
      {visibleCustomView ? (
        <CustomLensView
          ref={customViewRef}
          busy={isCustomViewBusy}
          scheduled={scheduledView}
          showBridgeStatus={false}
          cards={rowsLens && !rowsLens.spec.customView ? [] : cardData}
          filters={effectiveFilterValues}
          lens={rowsLens?.spec.customView ? rowsLens : lens}
          period={{ ...boardRange, summary: lensData?.period?.label.current ?? formatResolvedPeriod(boardRange) }}
          view={rowsLens?.spec.customView ?? visibleCustomView}
        />
      ) : (
        <Box
          aria-busy={isShowingPreviousData || selectionPending || isFetchingSourceContext}
          sx={{ position: 'relative' }}
        >
          {(isShowingPreviousData || selectionPending || isFetchingSourceContext) && (
            <Stack
              alignItems="center"
              aria-live="polite"
              direction="row"
              justifyContent="center"
              role="status"
              spacing={1.25}
              sx={{
                backgroundColor: theme.palette.background.default,
                inset: 0,
                minHeight: 180,
                p: 3,
                position: 'absolute',
                zIndex: 5,
              }}
            >
              <CircularProgress size={20} />
              <Typography fontWeight={600} variant="body2">
                {rowsLensCards
                  ? 'Updating cards with the latest changes…'
                  : 'Updating cards to match the selected period and filters…'}
              </Typography>
            </Stack>
          )}
          <Stack
            ref={visualAuditRootRef}
            onKeyDown={(event) => {
              if (event.key !== 'Escape' || !hasSelection(selections)) {
                return;
              }
              setSelections({});
              setCrossFilterSourceCardId(null);
              announceLensFilter(describeCrossFilterClearedAnnouncement());
            }}
            spacing={`${LENS_LAYOUT.gridGap}px`}
          >
            {cardGroups.map((group) => (
              <Box key={group.id ?? '__ungrouped'} data-lens-pdf-section="">
                {group.title && (
                  <Box sx={{ mb: 1 }}>
                    <Typography component="h2" variant="h6">
                      {group.title}
                    </Typography>
                    {/* The question the section was planned to answer. Carried on
                      the spec rather than re-derived, so the board states the job
                      each group of cards does instead of leaving it to be read
                      off the chart types that happened to be chosen. */}
                    {group.question && (
                      <Typography sx={{ color: 'text.secondary', mt: 0.25 }} variant="body2">
                        {group.question}
                      </Typography>
                    )}
                  </Box>
                )}
                {group.section?.reading?.mode === 'narrative' && (
                  <SectionReading
                    cardDataFor={cardDataFor}
                    cards={group.cards}
                    onPersist={
                      data.access.canEdit ? (cache) => persistSectionReading(group.section!.id, cache) : undefined
                    }
                    period={periodLabel}
                    section={group.section}
                    signature={signatureForSection(group.section.id)}
                  />
                )}
                <LensCanvas
                  canEdit={canAuthor}
                  cards={withRowsDrawerFrames(group.cards, expandedRowsCardIds)}
                  isLocked={updateLens.isPending || isCanvasSelectionMode}
                  onLayoutChange={(frames, message, heightResizedCardId, chartWidthGesture) =>
                    saveCardLayout(
                      applyCanvasFrames(
                        canvasCards,
                        preserveTableHeightModes(
                          canvasCards,
                          preserveChartWidthModes(
                            canvasCards,
                            framesPlacedByHand(
                              withoutCollapsedRowsDrawerFrames(frames, collapsedTableCardIds),
                              canvasCards,
                            ),
                            chartWidthGesture,
                          ),
                          heightResizedCardId,
                        ),
                      ),
                      message,
                    )
                  }
                  renderCard={(card, { isDragging, canDrag, frame }) =>
                    // Only a table card gets the drawer treatment, and only while collapsed: a
                    // reader who has expanded it sees the real card, with a small floating
                    // control layered over it to send the rows back behind the strip.
                    card.type === 'table' && !expandedRowsCardIds.has(card.id) ? (
                      <RowsDrawer
                        columns={(cardDataFor(card.id)?.columns ?? []).map(
                          (column) =>
                            card.config?.fieldLabels?.[column]?.trim() ||
                            governedDisplayDataset(
                              lens.spec.datasets?.find((dataset) => dataset.id === card.datasetId),
                              { savedDatasets: lens.spec.datasets, metadata: data.governedMetadata },
                            )?.columns?.find((field) => field.name === column)?.label ||
                            column,
                        )}
                        expanded={false}
                        onToggle={() => toggleRowsDrawer(card.id)}
                        rowCount={cardDataFor(card.id)?.totalRowCount ?? cardDataFor(card.id)?.rows?.length}
                        title={card.title}
                      />
                    ) : (
                      <Box
                        id={lensCardDomId(card.id)}
                        data-lens-pdf-card={card.id}
                        data-lens-pdf-card-status={
                          card.type === 'insight' ? undefined : shownCardDataFor(card.id)?.status
                        }
                        data-lens-pdf-card-title={card.title}
                        data-lens-pdf-card-type={card.type}
                        data-lens-pdf-total-rows={
                          shownCardDataFor(card.id)?.totalRowCount ?? shownCardDataFor(card.id)?.rows?.length
                        }
                        sx={{ height: '100%', minHeight: 0, position: card.type === 'table' ? 'relative' : 'static' }}
                      >
                        <LensCardView
                          lensId={lens.id}
                          drafting={drafting}
                          boardEvidence={boardEvidence}
                          insightScope={insightScope}
                          onCollapseRows={card.type === 'table' ? () => toggleRowsDrawer(card.id) : undefined}
                          canEdit={canAuthor}
                          canConfigure={canAuthor}
                          groupingChanged={
                            card.id === panelCardId &&
                            (card.datasetId !== panelCard?.datasetId ||
                              card.view?.x !== panelCard?.view?.x ||
                              card.view?.series !== panelCard?.view?.series)
                          }
                          editorMode={isEditMode}
                          canvasLayout={frame}
                          card={card}
                          duplicateOf={duplicateOfByCardId.get(card.id)}
                          onOpenCatalogueDataset={onOpenCatalogueDataset}
                          dragHandle={
                            canDrag
                              ? {
                                  isDragging,
                                }
                              : undefined
                          }
                          data={shownCardDataFor(card.id)}
                          comparison={cardComparison(shownCardDataFor(card.id))}
                          datasets={(rowsLens ?? lens).spec.datasets ?? []}
                          dateRange={shownCardDataFor(card.id)?.period?.current ?? boardRange}
                          isCrossFilterSource={card.id === crossFilterSourceCardId}
                          hasActiveFilters={hasActiveFilters}
                          savedInsightScopeChanged={hasChangedSavedInsightScope}
                          isFetchingData={isLoadingData}
                          isRefreshing={refreshCard.isPending && refreshCard.variables?.cardId === card.id}
                          insightRefresh={insightRefreshes[card.id] ?? 0}
                          key={card.id}
                          onClearFilters={clearAllFilters}
                          onAskRichard={
                            renderAskPane
                              ? () => {
                                  // Read vs build: asking about a card never switches the page into edit mode.
                                  openCardPane(card, 'ask');
                                }
                              : onAskRichard
                                ? () => onAskRichard(`Help me investigate “${card.title}”.`, true)
                                : undefined
                          }
                          onRepair={
                            onAskRichard
                              ? (mismatch) => {
                                  onSelectCard?.({ id: card.id, title: card.title });
                                  onAskRichard(
                                    `Repair the selected card “${card.title}”. Keep its current business question and governed source, but update the card mapping or query so it renders correctly. It expects ${mismatch.missing.join(', ')}; the current result returns ${mismatch.returned.join(', ') || 'no fields'}. Explain the proposed change before applying it.`,
                                    true,
                                    true,
                                  );
                                }
                              : undefined
                          }
                          isBulkSelected={selectedCanvasCardIds.includes(card.id)}
                          isSelected={selectedCardId === card.id}
                          highlightedField={hoveredField?.cardIds.includes(card.id) ? hoveredField.column : undefined}
                          onCardSelect={
                            !isCanvasSelectionMode && (renderAskPane || data.access.canEdit)
                              ? () => {
                                  if (!renderAskPane) {
                                    openCardPane(card);
                                  } else {
                                    // Reading a card must not open its authoring conversation.
                                    // Selection still scopes the Lens's question composer.
                                    onSelectCard?.({ id: card.id, title: card.title });
                                  }
                                }
                              : undefined
                          }
                          onToggleBulkSelection={
                            isCanvasSelectionMode ? () => toggleCanvasCardSelection(card.id) : undefined
                          }
                          selectionMode={isCanvasSelectionMode}
                          onDelete={async () => deleteLensCard(card)}
                          onDuplicate={() => duplicateLensCard(card)}
                          onMoveToSection={canAuthor ? lensCardMoveOptions(card) : undefined}
                          onReorderCategories={
                            canAuthor
                              ? async (order) => {
                                  await updateLens.mutateAsync({
                                    id: lens.id,
                                    title: lens.title,
                                    description: lens.description,
                                    spec: {
                                      ...lens.spec,
                                      cards: lens.spec.cards.map((item) =>
                                        item.id === card.id
                                          ? { ...item, config: { ...item.config, categoryOrder: order } }
                                          : item,
                                      ),
                                    },
                                    version: lens.version,
                                    successMessage: 'Card order saved',
                                  });
                                }
                              : undefined
                          }
                          onEdit={(tab) => {
                            if (!data.access.canEdit || isFinishingEditing) return;
                            setIsEditMode(true);
                            // "Edit card" opens Visual (tab 0); an empty-state card's
                            // own "Add data" call-to-action still opens with no tab
                            // named, which keeps its Data default.
                            // Keep the canvas and editor referring to the same card:
                            // an editor without an obvious live preview is unsafe on
                            // a dense dashboard.
                            onSelectCard?.({ id: card.id, title: card.title });
                            setPanelCardId(card.id);
                            setPanelMode('edit');
                            setPanelFormatFocus(undefined);
                            setPanelSection(
                              tab === undefined ? undefined : PANEL_SECTION_FOR_TAB[PANEL_TAB_FOR_LEGACY_TAB[tab]],
                            );
                          }}
                          onEditCommentary={() => {
                            if (!data.access.canEdit || isFinishingEditing) return;
                            setIsEditMode(true);
                            onSelectCard?.({ id: card.id, title: card.title });
                            setPanelCardId(card.id);
                            setPanelMode('edit');
                            setPanelFormatFocus('commentary');
                            setPanelSection('format');
                          }}
                          onEditSort={() => {
                            if (!data.access.canEdit || isFinishingEditing) return;
                            setIsEditMode(true);
                            onSelectCard?.({ id: card.id, title: card.title });
                            setPanelCardId(card.id);
                            setPanelMode('edit');
                            setPanelFormatFocus('sort');
                            setPanelSection('format');
                          }}
                          onRefresh={() => {
                            if (card.type === 'insight') {
                              setInsightRefreshes((current) => ({
                                ...current,
                                [card.id]: (current[card.id] ?? 0) + 1,
                              }));
                              return;
                            }
                            refreshCard.mutate({ lensId: lens.id, cardId: card.id, filters: effectiveFilterValues });
                          }}
                          onPersistInsight={
                            data.access.canEdit ? (insight) => persistInsight(card.id, insight) : undefined
                          }
                          onSelect={({ column, value, replaceValues }) => {
                            // Clicking a value already chosen removes it, so the same
                            // gesture turns that value on and off.
                            const next = replaceValues
                              ? { ...selections, [column]: replaceValues }
                              : toggleMarkSelection(selections, column, value);
                            setSelections(next);
                            setCrossFilterSourceCardId(next[column] ? card.id : null);
                            // A bar or table row is a filter control like any other, so
                            // it announces through the same polite live region, and it
                            // announces the outcome rather than the gesture.
                            announceLensFilter(
                              next[column]
                                ? describeCrossFilterAnnouncement(value, affectedCards[column]?.length ?? 0)
                                : describeCrossFilterClearedAnnouncement(),
                            );
                          }}
                          ranges={ranges}
                          selection={selections}
                          boardFilters={boardFilters}
                          span={8}
                        />
                      </Box>
                    )
                  }
                />
              </Box>
            ))}
          </Stack>
          {lens.spec.scope && (
            // Only on a Lens the redesigned create conversation actually built (`spec.scope` is
            // absent from anything older, or from one started the pre-scope way): this is the
            // create sheet's own promise, carried onto the board it produced, not a claim to make
            // about a hand-built Lens this page had no part in.
            <Typography sx={{ color: alpha(theme.palette.text.primary, 0.5), fontSize: 12, mt: 2 }}>
              Built from governed sources. Check any figure before you act on it.
            </Typography>
          )}
          {wandAsk.overlay}
        </Box>
      )}

      <CustomLensViewDialog
        cards={cardData}
        filters={effectiveFilterValues}
        lens={lens}
        onClose={() => setIsCustomViewOpen(false)}
        onRemove={async () => {
          const { customView: _customView, ...spec } = lens.spec;
          await updateLens.mutateAsync({
            id: lens.id,
            title: lens.title,
            description: lens.description,
            spec,
            version: lens.version,
            refetchData: false,
            successMessage: 'Custom view removed',
          });
          setIsCustomViewOpen(false);
        }}
        onSave={async (customView) => {
          await updateLens.mutateAsync({
            id: lens.id,
            title: lens.title,
            description: lens.description,
            spec: { ...lens.spec, customView },
            version: lens.version,
            refetchData: false,
            successMessage: lens.spec.customView ? 'Custom view updated' : 'Custom view created',
          });
          setIsCustomViewOpen(false);
        }}
        open={isCustomViewOpen}
        period={{ ...boardRange, summary: lensData?.period?.label.current ?? formatResolvedPeriod(boardRange) }}
        saving={updateLens.isPending}
      />

      <AddLensFilterDialog
        datasets={lens.spec.datasets ?? []}
        existingIds={(lens.spec.filters ?? []).map((filter) => filter.id)}
        filter={editingFilter}
        isSaving={updateLens.isPending}
        onClose={() => {
          setIsAddFilterOpen(false);
          setEditingFilter(null);
        }}
        onDelete={async (filter) => {
          requestConfirmation({
            title: `Remove “${filter.label}” filter?`,
            description: 'Cards will no longer be filtered by this control. Their data remains unchanged.',
            confirmLabel: 'Remove filter',
            onConfirm: async () => {
              await updateLens.mutateAsync({
                id: lens.id,
                title: lens.title,
                description: lens.description,
                spec: {
                  ...lens.spec,
                  cards: unbindFilterFromCards(lens.spec.cards, filter.id),
                  filters: (lens.spec.filters ?? []).filter((item) => item.id !== filter.id),
                },
                version: lens.version,
                successMessage: 'Filter removed',
              });
              setFilterValues((current) => {
                const next = { ...current };
                delete next[filter.id];
                return next;
              });
              setEditingFilter(null);
            },
          });
        }}
        onSave={async (filter) => {
          await updateLens.mutateAsync({
            id: lens.id,
            title: lens.title,
            description: lens.description,
            spec: {
              ...lens.spec,
              filters: editingFilter
                ? (lens.spec.filters ?? []).map((item) => (item.id === editingFilter.id ? filter : item))
                : [...(lens.spec.filters ?? []), filter],
              ...(!editingFilter ? { cards: bindFilterToCards(lens.spec.cards, filter, lens.spec.datasets) } : {}),
            },
            version: lens.version,
            successMessage: editingFilter ? 'Filter updated' : 'Filter added',
          });
          setIsAddFilterOpen(false);
          setEditingFilter(null);
        }}
        open={isAddFilterOpen || Boolean(editingFilter)}
      />

      {editingLensDetails && data.access.canEdit && (
        <EditLensDetailsDialog lens={editingLensDetails} onClose={() => setEditingLensDetails(null)} />
      )}
      {sharingOpen && (
        <ShareLensDialog
          open={sharingOpen}
          onClose={() => setSharingOpen(false)}
          lens={{
            ...lens,
            access: {
              role: null,
              canRead: true,
              canPropose: false,
              canReview: false,
              canRestore: false,
              canShare: false,
              canRequestCertification: false,
              canCertify: false,
              canRemoveCertification: false,
              ...data.access,
            },
            home: data.sharing?.home ?? lens.home,
            audience: data.sharing?.audience ?? lens.audience,
          }}
          spaces={spacesQuery.data?.spaces ?? []}
          spacesLoading={spacesQuery.isLoading}
          spacesError={spacesQuery.isError}
          viewerEmail={user?.email}
          currentView={{ filters: filterValues, selections, ranges }}
        />
      )}
      <SaveLensAsDialog
        onClose={() => setSaveAsOpen(false)}
        onCreated={(createdLens) => {
          setSaveAsOpen(false);
          if (onOpenLens) {
            onOpenLens(createdLens.id);
          } else {
            navigate(`/le-lens/${createdLens.id}`);
          }
        }}
        open={saveAsOpen}
        sourceLens={lens}
      />
      <FiltersDialog
        datasets={lens.spec.datasets ?? []}
        filters={lens.spec.filters ?? []}
        isSaving={updateLens.isPending}
        onClose={() => setIsFiltersOpen(false)}
        onDelete={async (filter) => {
          requestConfirmation({
            title: `Remove “${filter.label}” filter?`,
            description: 'Cards will no longer be filtered by this control. Their data remains unchanged.',
            confirmLabel: 'Remove filter',
            onConfirm: async () => {
              await updateLens.mutateAsync({
                id: lens.id,
                title: lens.title,
                description: lens.description,
                spec: {
                  ...lens.spec,
                  cards: unbindFilterFromCards(lens.spec.cards, filter.id),
                  filters: (lens.spec.filters ?? []).filter((item) => item.id !== filter.id),
                },
                version: lens.version,
                successMessage: 'Filter removed',
              });
              setFilterValues((current) => {
                const next = { ...current };
                delete next[filter.id];
                return next;
              });
            },
          });
        }}
        onEdit={(filter) => {
          setIsFiltersOpen(false);
          setEditingFilter(filter);
        }}
        open={isFiltersOpen}
      />

      <CrossFiltersDialog
        filterColumns={lens.spec.filterColumns}
        isSaving={updateLens.isPending}
        onClose={() => setIsCrossFiltersOpen(false)}
        onSave={async (filterColumns) => {
          await updateLens.mutateAsync({
            id: lens.id,
            title: lens.title,
            description: lens.description,
            spec: { ...lens.spec, filterColumns },
            version: lens.version,
            successMessage: 'Cross-filters updated',
            // This only changes which browser-side controls are visible.
            refetchData: false,
          });
          setIsCrossFiltersOpen(false);
        }}
        open={isCrossFiltersOpen}
        sources={filterSources}
      />

      <DatasetsDialog
        lensId={lens.id}
        lensVersion={lens.version}
        canEdit={canAuthor}
        cards={lens.spec.cards}
        datasets={lens.spec.datasets ?? []}
        filters={lens.spec.filters ?? []}
        isSaving={updateLens.isPending || datasetDeletion.isPending}
        onCheckQuery={(sql) => dryRunDataset.mutateAsync(sql)}
        onClose={() => setIsDatasetsOpen(false)}
        onCombine={
          canAuthor
            ? async (onStatus) => {
                const outcome = await combineQueries({
                  pool: createLensAskPool({ lensId: lens.id }),
                  cards: lens.spec.cards,
                  datasets: lens.spec.datasets ?? [],
                  existingIds: [
                    ...lens.spec.cards.map((card) => card.id),
                    ...(lens.spec.datasets ?? []).map((dataset) => dataset.id),
                    ...(lens.spec.sections ?? []).map((section) => section.id),
                  ],
                  filters: lens.spec.filters ?? [],
                  onStatus,
                  verifySource: (sql) => dryRunDataset.mutateAsync(sql),
                });
                return outcome;
              }
            : undefined
        }
        onApplyCombination={
          canAuthor
            ? async (outcome) => {
                // One reviewed save keeps the cards and their shared sources
                // consistent: there is never an intermediate state where a
                // card points at a dataset that was not persisted.
                await updateLens.mutateAsync({
                  id: lens.id,
                  title: lens.title,
                  description: lens.description,
                  spec: {
                    ...lens.spec,
                    cards: outcome.cards,
                    datasets: [
                      ...(lens.spec.datasets ?? []).filter(
                        (dataset) => !outcome.replacedDatasetIds?.includes(dataset.id),
                      ),
                      ...outcome.datasets,
                    ],
                  },
                  version: lens.version,
                  successMessage: 'Shared queries applied',
                  // The dialog states a failed apply beside its own retry button.
                  quietFailure: true,
                });
              }
            : undefined
        }
        onDelete={async (datasetId) => {
          if (!canAuthor) return;
          const dataset = lens.spec.datasets?.find((entry) => entry.id === datasetId);
          if (!dataset) return;
          const apply = async (deleted: boolean) => {
            await datasetDeletion.mutateAsync({ lensId: lens.id, datasetId, lensVersion: lens.version, deleted });
            setIsDatasetsOpen(false);
          };
          if (dataset.deletedAt) {
            await apply(false);
            return;
          }
          requestConfirmation({
            title: `Delete “${dataset.name}” dataset?`,
            description:
              'Linked cards stay on the Lens and show Dataset deleted. Its definition and history are kept, so you can restore it.',
            confirmLabel: 'Delete dataset',
            onConfirm: () => apply(true),
          });
        }}
        onSave={async (datasets) => {
          if (!canAuthor) return;
          const sqlChanged = datasets.some(
            (entry) => (lens.spec.datasets ?? []).find((existing) => existing.id === entry.id)?.sql !== entry.sql,
          );

          await updateLens.mutateAsync({
            id: lens.id,
            title: lens.title,
            description: lens.description,
            spec: { ...lens.spec, datasets },
            version: lens.version,
            successMessage: 'Queries saved',
            // Only re-run when a query actually changed; a rename should not
            // bill BigQuery for every card reading it.
            refetchData: sqlChanged,
          });
          setIsDatasetsOpen(false);
        }}
        open={isDatasetsOpen}
      />

      <LayoutWorkspace
        onAutoArrange={
          previewGroupedToolbar
            ? (cards) => smartArrangeCards(cards, cardDataById, measureInsightHeights(cards))
            : undefined
        }
        cards={canvasCards}
        isSaving={updateLens.isPending}
        onClose={() => setIsLayoutWorkspaceOpen(false)}
        onSave={async ({ sections, cards }) => {
          if (!canAuthor) throw new Error('Enter Edit Lens before saving the layout.');
          await updateLens.mutateAsync({
            id: lens.id,
            title: lens.title,
            description: lens.description,
            spec: { ...lens.spec, sections, cards },
            version: lens.version,
            successMessage: 'Layout saved',
            refetchData: false,
          });
          setIsLayoutWorkspaceOpen(false);
        }}
        open={isLayoutWorkspaceOpen}
        sections={lens.spec.sections ?? []}
      />

      {(sidebarVisible || renderAskPane) && (
        // On a narrow canvas this is a true modal sheet: it traps focus,
        // blocks clicks into the dashboard and keeps the draft on screen until
        // the reader explicitly keeps or discards it. Desktop retains the
        // docked editor, while the canvas reserves its width above.
        <Drawer
          anchor="right"
          ModalProps={{ keepMounted: true }}
          onClose={closeSidebar}
          open={sidebarVisible}
          PaperProps={{
            sx: {
              bgcolor: 'background.paper',
              // The resize handle draws the pane's edge.
              borderLeft: 0,
              height: `calc(100dvh - ${appChromeBottom}px)`,
              top: appChromeBottom,
              width: panelIsSheet ? '100%' : editorPanelWidth,
            },
          }}
          sx={{ display: sidebarVisible ? undefined : 'none' }}
          variant={panelIsSheet ? 'temporary' : 'permanent'}
        >
          {!panelIsSheet && (
            <Box
              aria-label="Resize card editor pane"
              aria-orientation="vertical"
              aria-valuemax={lensEditorPanelWidthBounds(viewportWidth, reservedCanvasWidth).max}
              aria-valuemin={lensEditorPanelWidthBounds(viewportWidth, reservedCanvasWidth).min}
              aria-valuenow={editorPanelWidth}
              onKeyDown={resizeEditorPanelWithKey}
              onPointerCancel={(event) => finishEditorPanelResize(event.pointerId)}
              onPointerDown={startEditorPanelResize}
              onPointerMove={resizeEditorPanel}
              onPointerUp={(event) => finishEditorPanelResize(event.pointerId)}
              role="separator"
              tabIndex={0}
              sx={{
                bottom: 0,
                cursor: 'col-resize',
                left: 0,
                position: 'absolute',
                top: 0,
                touchAction: 'none',
                width: 16,
                zIndex: 2,
                '&::after': {
                  backgroundColor: isResizingEditorPanel ? 'primary.main' : 'divider',
                  content: '""',
                  height: '100%',
                  left: 7,
                  position: 'absolute',
                  top: 0,
                  transition: 'background-color 120ms ease',
                  width: 2,
                },
                '&:focus-visible::after, &:hover::after': { backgroundColor: 'primary.main' },
              }}
            />
          )}
          <LensSidebar
            allowCardEditing={showCardEditingTools && canAuthor}
            hideEditTabs={Boolean(renderAskPane) && !canAuthor}
            cardEditorOpen={cardEditorOpen}
            cardSelected={Boolean(panelCard)}
            chat={
              askPaneContent ? (
                <Box ref={askPaneContainerRef} sx={{ display: 'contents' }}>
                  {askPaneContent}
                </Box>
              ) : undefined
            }
            chatBanner={
              tablePresentationChanged && (
                <Alert severity="info">Changing fields or chart type uses standard Lens columns.</Alert>
              )
            }
            lensContent={canAuthor ? lensSettingsContent : undefined}
            mode={panelMode}
            onClose={closeSidebar}
            onModeChange={(mode) => {
              // A viewer's pane only ever opens in Ask; Edit mode's own Save bar has no equivalent
              // lock on it, so the toggle itself is refused here rather than trusted to stay
              // disabled further down.
              if (mode !== 'ask' && !data.access.canEdit) {
                return;
              }
              if (mode === 'edit' && !showCardEditingTools) {
                return;
              }
              if (mode !== 'ask') setIsEditMode(true);
              setPanelMode(mode);
            }}
          />
          <LensEditPanel
            key={`${panelCard?.id}:${datasetBindingEpoch}`}
            allowCardEditing={showCardEditingTools && canAuthor}
            card={{
              ...panelCard,
              ...(panelDraft
                ? { datasetId: panelDraft.datasetId, view: panelDraft.view, period: panelDraft.period }
                : {}),
            }}
            filterValues={effectiveFilterValues}
            lensId={lens.id}
            boardPeriod={boardPeriodOf(lens.spec)}
            resolvedPeriod={panelCard ? cardDataFor(panelCard.id)?.period : undefined}
            cards={newCardDraft ? [...lens.spec.cards, newCardDraft] : lens.spec.cards}
            onOpenCatalogueDataset={onOpenCatalogueDataset}
            datasetActionsSlot={
              panelDataset && (
                <DatasetActions
                  canEdit={canAuthor}
                  cards={panelDatasetCards}
                  dataset={panelDataset}
                  isOwner={data.access.canEdit}
                  lensId={lens.id}
                  loadedRows={panelDatasetRows}
                  onNewDatasetInstead={(candidateSql) => setNewDatasetRequest({ prefillSql: candidateSql })}
                  onSaveDataset={saveDatasetChange}
                  ownerEmail={lens.ownerEmail}
                />
              )
            }
            datasets={lens.spec.datasets ?? []}
            declaring={declareNow.declaring}
            dirty={panelHasUnsavedChanges}
            conflict={
              panelCard && editorConflicts[`${lens.id}:${panelCard.id}`] ? (
                <LensConflictReceipt
                  conflict={editorConflicts[`${lens.id}:${panelCard.id}`]}
                  onApply={() => {
                    const { mine, base } = editorConflicts[`${lens.id}:${panelCard.id}`];
                    const dataOf = (card: LensCard) => ({
                      cardId: panelCard.id,
                      datasetId: card.datasetId,
                      view: card.view ?? {},
                      period: card.period,
                    });
                    if (mine.data) setPanelDraft(editedOnto(mine.data, dataOf(base), dataOf(panelCard)));
                    if (mine.type && mine.type !== base.type) setPanelType(mine.type);
                    if (mine.details)
                      setPanelDetailsDraft(
                        editedOnto(mine.details, defaultDetailsDraft(base), defaultDetailsDraft(panelCard)),
                      );
                    if (mine.config)
                      setPanelStyleConfig(editedOnto(mine.config, base.config ?? {}, panelCard.config ?? {}));
                    if (!isEqual(mine.sort, base.config?.sort)) setPanelSort(mine.sort);
                    setEditorConflicts(({ [`${lens.id}:${panelCard.id}`]: _applied, ...others }) => others);
                  }}
                />
              ) : undefined
            }
            chartDetailsContent={chartDetailsContent}
            appearanceDetailsContent={appearanceDetailsContent}
            formatFocus={panelFormatFocus}
            isNewCard={newCardDraft?.id === panelCard?.id}
            isSaving={updateLens.isPending}
            layoutContent={detailsTabContent}
            loading={isLoadingData && !cardDataById.get(panelCard?.id)}
            mergeBanner={
              activeMergeCandidate && (
                <MergeBanner
                  cards={lens.spec.cards}
                  from={activeMergeCandidate.from}
                  grainKeys={activeMergeCandidate.grainKeys}
                  into={activeMergeCandidate.into}
                  lensId={lens.id}
                  onDismiss={() =>
                    setDismissedMergePairs((current) =>
                      new Set(current).add(mergePairKey(activeMergeCandidate.from.id, activeMergeCandidate.into.id)),
                    )
                  }
                  onMerge={mergeDatasets}
                  table={activeMergeCandidate.table}
                />
              )
            }
            onCancel={() => {
              discardPanelDraft();
            }}
            onClose={closePanel}
            onDraftChange={(draft) => {
              if (panelCardId) {
                const previous = panelDraft ?? panelCard;
                const measureChanged =
                  previous &&
                  (previous.datasetId !== draft.datasetId ||
                    previous.view?.value !== draft.view?.value ||
                    !isEqual(previous.view?.ratio, draft.view?.ratio));
                if (measureChanged) {
                  const dataset = lens.spec.datasets?.find((entry) => entry.id === draft.datasetId);
                  const column = dataset?.columns?.find((entry) =>
                    draft.view?.ratio
                      ? entry.ratio?.of === draft.view.ratio.of && entry.ratio?.per === draft.view.ratio.per
                      : entry.name === draft.view?.value,
                  );
                  const previousDataset = lens.spec.datasets?.find((entry) => entry.id === previous.datasetId);
                  const previousColumn = previousDataset?.columns?.find((entry) =>
                    previous.view?.ratio
                      ? entry.ratio?.of === previous.view.ratio.of && entry.ratio?.per === previous.view.ratio.per
                      : entry.name === previous.view?.value,
                  );
                  if (panelDetailsDraftValue) {
                    const title = titleAfterMeasureChange(
                      panelDetailsDraftValue.title,
                      previousColumn?.label ?? previousColumn?.name,
                      column?.label ?? column?.name,
                    );
                    if (title !== panelDetailsDraftValue.title) {
                      setPanelDetailsDraft({ ...panelDetailsDraftValue, title });
                    }
                  }
                  const format = column?.format ?? { kind: draft.view?.ratio ? 'percent' : 'number' };
                  styleTabState.setKind(format.kind);
                  styleTabState.setCurrency(format.currency ?? 'AUD');
                  styleTabState.setDecimals(format.decimals === undefined ? 'auto' : String(format.decimals));
                  styleTabState.setPercentScale(format.percentScale ?? 'unit');
                  styleTabState.setSuffix(format.suffix ?? '');
                }
                setPanelDraft({
                  cardId: panelCardId,
                  datasetId: draft.datasetId,
                  view: draft.view,
                  period: draft.period,
                });
              }
            }}
            onChangeFieldKind={(field, kind) => {
              if (!panelDataset) {
                return;
              }
              void saveDatasetMetadata(
                overrideColumnKind(panelDataset, field.name, kind),
                `Changed ${field.name}’s kind`,
              );
            }}
            onDeclareNow={declareNow.onDeclareNow}
            onDeleteCard={canAuthor ? () => deleteLensCard(panelCard) : undefined}
            onDuplicateCard={canAuthor ? () => duplicateLensCard(panelCard) : undefined}
            onMoveCardToSection={canAuthor && panelCard ? lensCardMoveOptions(panelCard) : undefined}
            onFieldHover={setHoveredField}
            onNewDataset={() =>
              setNewDatasetRequest(newCardDraft?.id === panelCard.id ? { bindCardId: panelCard.id } : {})
            }
            onRelabelField={(field, label) => {
              if (!panelDataset) {
                return;
              }
              void saveDatasetMetadata(
                relabelDatasetColumn(panelDataset, field.name, label),
                `Relabelled ${field.name}`,
              );
            }}
            onRemoveDataset={(datasetId) => {
              requestConfirmation({
                title: 'Delete dataset?',
                description: 'Its definition and history are kept. You can restore it in Data & queries.',
                confirmLabel: 'Delete dataset',
                onConfirm: async () => {
                  await datasetDeletion.mutateAsync({
                    lensId: lens.id,
                    datasetId,
                    lensVersion: lens.version,
                    deleted: true,
                  });
                },
              });
            }}
            onRemoveField={(field) => {
              if (!panelDataset) {
                return;
              }
              void saveDatasetMetadata(removeCalculatedField(panelDataset, field.name), `Removed ${field.name}`);
            }}
            onRequestDiscard={requestPanelDiscard}
            onSortChange={setPanelSort}
            onSave={async (updatedCard) => {
              // Persist from the controls themselves, not the effect-driven
              // preview hand-off. A switch can redraw the chart immediately
              // while setPanelStyleConfig is still one render behind; saving
              // during that window used to write the previous config back.
              const latestStyleConfig = styleTabState.buildConfig();
              if (panelSort) {
                latestStyleConfig.sort = panelSort;
              } else {
                delete latestStyleConfig.sort;
              }
              // usePanelState.buildNextCard only knows datasetId/view: lane
              // C4's own drafts (type, details, style) are folded in here,
              // the one place both meet, rather than by reaching into C1's
              // hook to teach it about fields it has no reason to know.
              const mergedBase: LensCard = {
                ...updatedCard,
                ...(panelType ? { type: panelType } : {}),
                ...(panelDetailsDraft
                  ? {
                      title: panelDetailsDraft.title.trim() || updatedCard.title,
                      description: panelDetailsDraft.description.trim() || undefined,
                      section: panelDetailsDraft.section || undefined,
                    }
                  : {}),
                config: {
                  ...latestStyleConfig,
                  ...(panelDetailsDraft
                    ? {
                        titleAlignment:
                          panelDetailsDraft.alignment === 'default' ? undefined : panelDetailsDraft.alignment,
                        titleSize: panelDetailsDraft.titleSize === 'default' ? undefined : panelDetailsDraft.titleSize,
                      }
                    : {}),
                },
              };
              const presented = editedTablePresentation(panelCard, mergedBase);
              const merged = panelDetailsDraft
                ? applyPanelPresentationFrame(presented, panelDetailsDraft, cardDataFor(mergedBase.id))
                : presented;
              await updateLens.mutateAsync({
                id: lens.id,
                title: lens.title,
                description: lens.description,
                spec: {
                  ...lens.spec,
                  cards:
                    newCardDraft?.id === merged.id
                      ? [...lens.spec.cards, merged]
                      : lens.spec.cards.map((item) => (item.id === merged.id ? merged : item)),
                },
                version: lens.version,
                successMessage: 'Card updated',
                refetchData: cardDataNeedsRefresh(panelCard, merged, lens.spec.datasets ?? []),
              });
              cardEditorDrafts.clear();
              closePanel();
              setNewCardDraft(null);
            }}
            open={cardEditorOpen}
            openSection={panelSection}
            restricted={panelCard?.contract === 'blocked'}
            ringedSections={ringedSections}
            rowCount={cardDataFor(panelCard?.id)?.totalRowCount ?? cardDataFor(panelCard?.id)?.rows?.length}
            runtimeColumns={panelCardData?.columns}
            seriesNames={editingSeriesNames}
            sort={panelSort}
            title={panelDetailsDraftValue?.title ?? panelCard?.title}
            onTitleChange={(title) =>
              setPanelDetailsDraft({ ...(panelDetailsDraftValue ?? defaultDetailsDraft(panelCard)), title })
            }
            type={panelStyleType}
            visualContent={chartTabContent}
            visualBasicsContent={visualBasicsContent}
            widthOverride={panelIsSheet ? 'full' : editorPanelWidth}
          />
        </Drawer>
      )}

      {lens && (
        <Dialog
          PaperProps={{ sx: { maxWidth: 760 } }}
          fullWidth
          maxWidth="md"
          onClose={() => setNewDatasetRequest(null)}
          open={Boolean(newDatasetRequest)}
        >
          <DialogContent>
            {newDatasetRequest && (
              <NewDataset
                initialSql={newDatasetRequest.prefillSql}
                onBrowseCatalogue={
                  onBrowseCatalogue
                    ? () => {
                        // Leaving for the catalogue's own list screen is a bigger move than
                        // Cancel; the picker closes first so it is never left open behind it.
                        setNewDatasetRequest(null);
                        onBrowseCatalogue();
                      }
                    : undefined
                }
                onClose={() => setNewDatasetRequest(null)}
                onSave={saveNewDataset}
                onSaved={(datasetId) => {
                  const bindCardId = newDatasetRequest?.bindCardId;
                  if (!bindCardId) {
                    return;
                  }
                  setNewCardDraft((current) =>
                    current?.id === bindCardId ? { ...current, datasetId, view: {} } : current,
                  );
                  setPanelDraft({ cardId: bindCardId, datasetId, view: {} });
                  setDatasetBindingEpoch((current) => current + 1);
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      )}

      <Dialog
        aria-describedby="lens-confirmation-description"
        disableEscapeKeyDown={updateLens.isPending}
        maxWidth="xs"
        onClose={dismissConfirmation}
        open={Boolean(pendingConfirmation)}
        fullWidth
      >
        <DialogTitle>{pendingConfirmation?.title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography id="lens-confirmation-description" variant="body2">
              {pendingConfirmation?.description}
            </Typography>
            {confirmationError && <Alert severity="error">{confirmationError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button disabled={updateLens.isPending} onClick={dismissConfirmation}>
            Cancel
          </Button>
          <Button
            color={!pendingConfirmation?.destructive ? 'primary' : 'error'}
            disabled={updateLens.isPending}
            onClick={confirmPendingAction}
            variant="contained"
          >
            {updateLens.isPending ? 'Saving…' : pendingConfirmation?.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
  return (
    <GovernedDisplayMetadataProvider
      savedDatasets={(rowsResponse ?? data)?.lens.spec.datasets}
      metadata={(rowsResponse ?? data)?.governedMetadata}
    >
      <GovernedMeasureMetadataProvider
        savedDatasets={(rowsResponse ?? data)?.lens.spec.datasets}
        metadata={(rowsResponse ?? data)?.governedMeasureMetadata}
      >
        <LensCalendarContext.Provider value={calendar}>
          {editBar}
          {board}
        </LensCalendarContext.Provider>
      </GovernedMeasureMetadataProvider>
    </GovernedDisplayMetadataProvider>
  );
}

/** Card types that render a chart. Kept local since LensCardView.tsx, which this file does not
 *  own, does not export an equivalent list of its own. */
const CHART_CARD_TYPES = new Set<LensCardType>([
  'line',
  'bar',
  'drivers',
  'stacked',
  'area',
  'scatter',
  'pie',
  'combo',
  'heatmap',
  'boxplot',
]);

/**
 * The size distributeSpans balances from, when a card's own size is unset. A metric left at the
 * layout's own large default grows to fill an entire otherwise-empty row, per cardLayout.ts's
 * balance step, which is how a lone metric ended up spanning the full page width. Metrics get a
 * compact effective size instead; every other card type keeps deferring to distributeSpans' own
 * default.
 */
export function effectiveCardSize(card: LensCard): LensCardSize | undefined {
  if (card.size || card.type !== 'metric') {
    return card.size;
  }
  return 'medium';
}

/**
 * Charts are capped one column short of a balanced large card's own ceiling, so a lone chart
 * grows to at most eight of twelve columns rather than claiming the whole row the way a metric
 * used to. Metrics are already bounded by their own effective size above, and a table filling
 * the row is the desired behaviour, so neither is clamped here.
 */
export function clampSpanForType(type: LensCardType, span: number): number {
  return CHART_CARD_TYPES.has(type) ? Math.min(span, 5) : span;
}

/** Quiet metadata beside the page title: how many cards and shared datasets this dashboard
 *  declares. Moved here from the filter bar so it reads as dashboard-level context rather than
 *  as something one particular filter column happens to reach. */
export function formatCardDatasetSummary(cardCount: number, datasetCount: number): string {
  const cards = `${cardCount} ${cardCount === 1 ? 'card' : 'cards'}`;
  const datasets = `${datasetCount} ${datasetCount === 1 ? 'dataset' : 'datasets'}`;
  return `${cards} · ${datasets}`;
}

/** The one contained, brand-accent button on the page, so "Build with askRichard" reads as
 *  this Lens's single primary action instead of an MUI-default coloured pill competing
 *  with everything beside it. */
function accentButtonSx(theme: Theme) {
  const tokens = lensTokens(theme);
  return {
    backgroundColor: tokens.accent,
    color: tokens.accentContrast,
    '&:hover': { backgroundColor: alpha(tokens.accent, 0.88) },
  };
}

/** Whether a reviewed query carries both halves of the range itself. */
function bindsDateRange(sql: string): boolean {
  return /@start_date\b/.test(sql) && /@end_date\b/.test(sql);
}

function LensFilters({
  canEdit,
  filters,
  values,
  onChange,
  onEdit,
  optionValues,
}: {
  canEdit: boolean;
  filters: Array<LensFilter>;
  values: LensFilterValues;
  onChange: React.Dispatch<React.SetStateAction<LensFilterValues>>;
  onEdit: (filter: LensFilter) => void;
  optionValues: Record<string, Array<string>> | undefined;
}) {
  const hasActiveFilters = filters.map((filter) => values[filter.id]).some(isActiveDeclaredValue);

  return (
    <Stack alignItems="stretch" spacing={2}>
      {filters.map((filter) =>
        filter.type === 'boolean' || filter.type === 'numberRange' ? (
          <NativeLensFilter
            key={filter.id}
            filter={filter}
            value={values[filter.id]}
            onChange={(value) => onChange((current) => ({ ...current, [filter.id]: value }))}
          />
        ) : filter.type === 'dateRange' ? (
          <Stack key={filter.id} spacing={0.5}>
            <Stack direction="row" flexWrap="wrap" spacing={1}>
              <NativeLensFilter
                filter={filter}
                value={values[filter.id]}
                onChange={(value) => onChange((current) => ({ ...current, [filter.id]: value }))}
              />
              {canEdit && (
                <Button onClick={() => onEdit(filter)} size="small">
                  Edit
                </Button>
              )}
            </Stack>
            <Typography color="text.secondary" sx={{ fontSize: 11.5 }}>
              Changes dates for every card linked to {filter.label}.
            </Typography>
          </Stack>
        ) : (
          <Box key={filter.id} sx={{ minWidth: 0, width: '100%' }}>
            <Stack alignItems="center" direction="row" spacing={1} sx={{ minWidth: 0, width: '100%' }}>
              <Autocomplete<string, boolean, false, false>
                multiple={Boolean(filter.multiple)}
                noOptionsText={filterValuesFailed(filter, optionValues) ? "Couldn't load values" : undefined}
                disableCloseOnSelect={Boolean(filter.multiple)}
                options={
                  filterValuesFailed(filter, optionValues)
                    ? []
                    : Array.from(
                        new Set([
                          ...(optionValues?.[filter.id]?.length ? optionValues[filter.id] : (filter.options ?? [])),
                          ...(Array.isArray(values[filter.id])
                            ? (values[filter.id] as string[])
                            : typeof values[filter.id] === 'string'
                              ? [values[filter.id] as string]
                              : []),
                        ]),
                      ).filter(Boolean)
                }
                value={
                  filter.multiple
                    ? Array.isArray(values[filter.id])
                      ? (values[filter.id] as string[])
                      : []
                    : typeof values[filter.id] === 'string'
                      ? (values[filter.id] as string)
                      : null
                }
                onChange={(_event, value) => onChange((current) => ({ ...current, [filter.id]: value ?? '' }))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={filter.label}
                    size="small"
                    placeholder={`Search ${filter.label.toLowerCase()}`}
                  />
                )}
                sx={{ minWidth: 0, flex: 1 }}
              />
              {canEdit && (
                <Button onClick={() => onEdit(filter)} size="small">
                  Edit
                </Button>
              )}
            </Stack>
          </Box>
        ),
      )}
      {hasActiveFilters && (
        <Button
          onClick={() => onChange((current) => clearDeclaredFilters(current, filters))}
          size="small"
          sx={{ alignSelf: 'flex-start' }}
        >
          Clear saved filter values
        </Button>
      )}
    </Stack>
  );
}

function filterValuesFailed(filter: LensFilter, optionValues: Record<string, Array<string>> | undefined): boolean {
  return optionValues !== undefined && !(filter.id in optionValues) && !filter.options?.length;
}

function LensLoading() {
  return (
    <Container sx={{ py: 4 }}>
      <Skeleton height={56} width="35%" />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2, mt: 3 }}>
        {[1, 2].map((key) => (
          <Skeleton height={240} key={key} variant="rounded" />
        ))}
      </Box>
    </Container>
  );
}

/** Format, Layout's draft, seeded from a card's own saved fields. The card
 *  strip's title field reads and writes through the same shape, so a fresh
 *  title edit before any other Layout field has been touched still carries
 *  every other saved value forward untouched. */
function editedOnto<T>(mine: T, base: T, now: T): T {
  const plain = (value: unknown): value is Record<string, unknown> =>
    Boolean(value) && typeof value === 'object' && !Array.isArray(value);
  if (!plain(mine) || !plain(base) || !plain(now)) {
    return isEqual(mine, base) ? now : mine;
  }
  const result: Record<string, unknown> = { ...now };
  for (const key of new Set([...Object.keys(mine), ...Object.keys(base)])) {
    if (isEqual(mine[key], base[key])) continue;
    result[key] = editedOnto(mine[key], base[key], now[key]);
    if (result[key] === undefined) delete result[key];
  }
  return result as T;
}

function defaultDetailsDraft(card: LensCard): DetailsTabDraft {
  return {
    title: card.title,
    description: card.description ?? '',
    alignment: card.config?.titleAlignment ?? 'default',
    titleSize: card.config?.titleSize ?? 'default',
    size: card.size ?? 'large',
    height: tableUsesAutoHeight(card) ? '' : (card.height ?? (card.type === 'table' ? 'tall' : '')),
    section: card.section ?? '',
  };
}

function cardDataNeedsRefresh(
  previous: LensCard | null,
  next: LensCard,
  datasets: ReadonlyArray<LensDataset>,
): boolean {
  if (!previous || previous.datasetId !== next.datasetId) {
    return true;
  }
  if (!isEqual(previous.period ?? null, next.period ?? null)) {
    return true;
  }
  return (
    datasets.find((dataset) => dataset.id === next.datasetId)?.source?.kind === 'governed' &&
    (previewQueryMode(previous) !== previewQueryMode(next) || !isEqual(previous.view ?? {}, next.view ?? {}))
  );
}

function previewQueryMode(card: LensCard | null | undefined): string {
  if (card?.view?.mode === 'points' || (!card?.view?.mode && (card?.type === 'scatter' || card?.type === 'boxplot')))
    return 'points';
  return card?.type === 'metric' ? 'metric' : 'grouped';
}
